import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Tooltip, Stack,
  Avatar, Chip, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  CircularProgress, Alert, TextField, MenuItem, InputAdornment, Divider, Paper,
  Autocomplete, Grid, Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, LinearProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Assessment as ReportsIcon, ArrowBack as BackIcon, Print as PrintIcon,
  GetApp as DownloadIcon, Search as SearchIcon, ChevronRight as ChevronIcon,
  Category as CategoryIcon, ErrorOutline as EmptyIcon,
  Sms as SmsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import ModuleHeader from '../../components/ModuleHeader';
import { REPORTS_BY_KEY, REPORT_CATEGORIES } from '../../constants/reports';
import API from '../../BaseURL';
import { openPayslipPrint } from '../hr/HRPayrollPage';

// ── CSV export helper ────────────────────────────────────────────
function downloadCsv(filename, headers, rows) {
  const escape = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.map(escape).join(',')];
  for (const r of rows) lines.push(r.map(escape).join(','));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 200);
}

// ── Print-area styling — only the .print-area is visible in print ─
// The browser strips background colors and clips overflow by default,
// which makes our chip-heavy / table-heavy reports look blank or
// truncated. We force colour printing and unblock overflow so
// everything in the print-area actually reaches the page.
const PRINT_CSS = `
  @media print {
    @page { size: A4; margin: 12mm; }

    /* Force backgrounds / chips / coloured cells to actually print */
    *, *::before, *::after {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    body * { visibility: hidden !important; }
    .print-area, .print-area * { visibility: visible !important; }
    .print-area {
      position: absolute !important;
      left: 0 !important; top: 0 !important;
      width: 100% !important;
      box-shadow: none !important;
      border: none !important;
    }
    .no-print, .no-print * { display: none !important; }

    /* TableContainer wraps tables in overflow:auto — that clips content
       in print. Let it grow naturally and remove any maxHeight. */
    .MuiTableContainer-root {
      overflow: visible !important;
      max-height: none !important;
    }
    .MuiTable-root { width: 100% !important; }

    /* Avoid breaking a row across pages */
    tr, .MuiTableRow-root { break-inside: avoid !important; page-break-inside: avoid !important; }
    /* Group cards (per-account sections, reconciliation statements) try
       to stay on one page when they fit. */
    .MuiCard-root { break-inside: avoid !important; page-break-inside: avoid !important;
                    box-shadow: none !important; }

    /* Material's sticky table headers can hide content in print */
    .MuiTableCell-stickyHeader { position: static !important; }

    /* Tooltips, transitions, ripples — strip them out */
    .MuiTooltip-popper { display: none !important; }
  }
`;

// ── Money helpers used by the Finance reports ───────────────────
const fmt2 = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const todayStr = () => new Date().toISOString().slice(0, 10);
// Default to TODAY only — cashier opens a finance report and sees today's
// figures first. Wider ranges are one preset chip away.
const monthAgoStr = () => todayStr();
const FINANCE_KEYS = new Set([
  'fin_general_report',
  'fin_daily_income', 'fin_account_summary', 'fin_class_other',
  'fin_adjustments', 'fin_cashier_summary',
]);
// Snapshot reports — current state, not period-based. They get their
// own filter card (no From/To pickers) because date range isn't relevant.
const SNAPSHOT_BALANCE_KEYS = new Set(['fin_daily_collection_balances']);
const ACCOUNTING_KEYS = new Set([
  'acc_trial_balance', 'acc_bank_reconciliation', 'acc_bank_analysis',
  'acc_cashbook_statement', 'acc_payments_analysis', 'acc_income_vs_expense',
  'acc_petty_statement', 'acc_petty_analysis', 'acc_receipts_payments',
  'acc_cashbook_classic', 'acc_cash_analysis',
  'acc_receipts_payments_classic',
]);
// Ledger reports have their own filter card (category / type / payee /
// source / income type / mode / cashier / class / scope) layered on top
// of the standard accounting date range.
const LEDGER_KEYS = new Set(['acc_expenditure_ledger', 'acc_income_ledger']);
const BANK_REPORT_KEYS = new Set([
  'acc_bank_reconciliation', 'acc_bank_analysis',
  'acc_cashbook_statement', 'acc_payments_analysis',
]);
// Default window for accounting reports: this month, since "today" would
// hide most accounts that haven't moved yet today.
const thisMonthRange = () => {
  const d = new Date();
  const from = new Date(d.getFullYear(), d.getMonth(), 1);
  return { from: from.toISOString().slice(0, 10), to: todayStr() };
};

// Quick date presets the user can click instead of fiddling with the
// calendar pickers. Each returns { from, to } in ISO YYYY-MM-DD.
function dayOffset(days) {
  return new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
}
const DATE_PRESETS = [
  { v: 'today',     label: 'Today',         range: () => ({ from: todayStr(), to: todayStr() }) },
  { v: 'yesterday', label: 'Yesterday',     range: () => ({ from: dayOffset(1), to: dayOffset(1) }) },
  { v: 'this_week', label: 'This week',     range: () => {
      const d = new Date(); const day = (d.getDay() + 6) % 7; // Mon=0
      const from = new Date(d); from.setDate(d.getDate() - day);
      return { from: from.toISOString().slice(0, 10), to: todayStr() };
    } },
  { v: 'this_month', label: 'This month',   range: () => {
      const d = new Date();
      const from = new Date(d.getFullYear(), d.getMonth(), 1);
      return { from: from.toISOString().slice(0, 10), to: todayStr() };
    } },
  { v: 'last_30',    label: 'Last 30 days', range: () => ({ from: dayOffset(30), to: todayStr() }) },
  { v: 'this_year',  label: 'This year',    range: () => {
      const y = new Date().getFullYear();
      return { from: `${y}-01-01`, to: todayStr() };
    } },
];

function activePreset(value) {
  for (const p of DATE_PRESETS) {
    const r = p.range();
    if (r.from === value.from && r.to === value.to) return p.v;
  }
  return 'custom';
}

// ── Student Balances filters ─────────────────────────────────────
// Class + Term + Year + Account multi-select. Apply hits the report
// endpoint with whichever filters are populated.
function BalancesFilters({ keyName, value, onChange, onApply, loading, classes = [], accounts = [] }) {
  const accSelected = Array.isArray(value.accounts) ? value.accounts : [];
  const isReminder = keyName === 'fin_fee_reminders';
  return (
    <Card sx={{ mb: 2 }} className="no-print">
      <CardContent sx={{ pb: '16px !important' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.2 }}>
          <CategoryIcon color="primary" fontSize="small" />
          <Typography variant="overline" color="primary" fontWeight={700}>Filters</Typography>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="center" flexWrap="wrap" rowGap={1.5}>
          <TextField select size="small" label="Class" value={value.cla}
            onChange={(e) => onChange({ ...value, cla: e.target.value })}
            sx={{ minWidth: 180 }}>
            <MenuItem value="">All classes</MenuItem>
            {classes.map(c => (
              <MenuItem key={c.trans_code || c.class} value={c.class}>{c.class}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Term" value={value.term}
            onChange={(e) => onChange({ ...value, term: e.target.value })}
            sx={{ minWidth: 130 }}>
            <MenuItem value="">All</MenuItem>
            {['Term_1','Term_2','Term_3'].map(t => (
              <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>
            ))}
          </TextField>
          <TextField size="small" label="Academic Year" placeholder="e.g. 2025/2026"
            value={value.acc_yer}
            onChange={(e) => onChange({ ...value, acc_yer: e.target.value })}
            sx={{ minWidth: 150 }} />
          <TextField select size="small" label="Accounts (income types)"
            SelectProps={{
              multiple: true,
              renderValue: (selected) => selected.length === 0
                ? 'All accounts'
                : selected.map(s => s.replace(/_/g, ' ')).join(', '),
            }}
            value={accSelected}
            onChange={(e) => {
              const v = e.target.value;
              onChange({ ...value, accounts: typeof v === 'string' ? v.split(',') : v });
            }}
            sx={{ flex: 1, minWidth: 240 }}>
            {accounts.map(a => (
              <MenuItem key={a} value={a}>{a.replace(/_/g, ' ')}</MenuItem>
            ))}
          </TextField>
          {isReminder && (
            <TextField size="small" type="number" label="Min owing"
              placeholder="0.00" value={value.min_owing || ''}
              onChange={(e) => onChange({ ...value, min_owing: e.target.value })}
              sx={{ width: 120 }} />
          )}
          <Button variant="contained" onClick={() => onApply()}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            sx={{ textTransform: 'none', fontWeight: 800, minWidth: 110 }}>
            Apply
          </Button>
        </Stack>
        {isReminder && (
          <>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
              <Chip
                size="small"
                label={value.combine ? 'Combined: ON · single total per slip' : 'Combined: OFF · per-account breakdown'}
                color={value.combine ? 'primary' : 'default'}
                variant={value.combine ? 'filled' : 'outlined'}
                onClick={() => onChange({ ...value, combine: !value.combine })}
                sx={{ fontWeight: 700, cursor: 'pointer' }}
              />
              <Typography variant="caption" color="text.secondary">
                When ON, each slip shows one combined amount across the selected accounts instead of an itemised breakdown.
              </Typography>
            </Stack>
            <TextField size="small" fullWidth label="Custom message (printed on each slip)"
              placeholder="e.g. Kindly settle the outstanding fees before the end of next week to avoid disruption."
              value={value.message || ''}
              onChange={(e) => onChange({ ...value, message: e.target.value })}
              multiline maxRows={3}
              sx={{ mt: 1.5 }} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Student Balances report renderer ─────────────────────────────
// Two-row header (account name spans 3 sub-columns: To Pay / Paid /
// Balance), one row per student. The Analysis panel at the bottom
// (toggleable) shows grand totals — restricted view, intentionally
// hidden by default. When the toggle is OFF, the analysis is removed
// from the DOM, so a Print captures only the roster.
function StudentBalancesReport({ data, schoolName }) {
  const [analysisOn, setAnalysisOn] = useState(false);
  // Merge mode: collapse all account columns into a single summary
  // (Arrears / To Pay / Paid / Balance per student). Useful for class
  // teachers who only need the overall picture, not the per-account
  // breakdown.
  const [mergedOn, setMergedOn] = useState(false);
  const accounts = data?.accounts || [];
  const rows     = data?.rows     || [];
  const f        = data?.filters  || {};
  const totals   = data?.totals   || {};
  const grand    = data?.grand    || { billed: 0, paid: 0, balance: 0 };
  const subtitle = [
    f.cla     ? `Class: ${f.cla}` : 'All classes',
    f.term    ? `Term: ${f.term.replace('_', ' ')}` : null,
    f.acc_yer ? `Year: ${f.acc_yer}` : null,
  ].filter(Boolean).join(' · ');

  const fmt = (n) => Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

  return (
    <Box>
      <PrintHeader schoolName={schoolName} title="Student Balances"
        subtitle={subtitle} />

      {/* View toggles — visible on screen, removed in print */}
      <Box className="no-print" sx={{ mb: 1.5,
        display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 800, fontSize: 10 }}>
          Analysis Panel
        </Typography>
        <Button size="small" variant={analysisOn ? 'contained' : 'outlined'}
          color={analysisOn ? 'success' : 'inherit'}
          onClick={() => setAnalysisOn(v => !v)}
          sx={{ textTransform: 'none', fontWeight: 800, minWidth: 110 }}>
          {analysisOn ? 'Analysis: ON' : 'Analysis: OFF'}
        </Button>

        <Box sx={{ width: 1, height: 22, bgcolor: 'divider', mx: 0.5 }} />

        <Typography variant="caption" color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 800, fontSize: 10 }}>
          View
        </Typography>
        <Button size="small" variant={mergedOn ? 'contained' : 'outlined'}
          color={mergedOn ? 'primary' : 'inherit'}
          onClick={() => setMergedOn(v => !v)}
          sx={{ textTransform: 'none', fontWeight: 800, minWidth: 150 }}>
          {mergedOn ? 'Merged Accounts: ON' : 'Merged Accounts: OFF'}
        </Button>
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>
          {mergedOn
            ? 'Showing one summary row per student (Arrears / To Pay / Paid / Balance).'
            : 'Showing each account separately.'}
        </Typography>
      </Box>

      {data?.warnings?.length > 0 && (
        <Alert severity="warning" className="no-print" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={700} sx={{ mb: 0.4 }}>
            Some fee tables are missing on this database — showing fall-back data:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 12.5 }}>
            {data.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </Box>
        </Alert>
      )}

      {data?.notices?.length > 0 && (
        <Alert severity="info" className="no-print" sx={{ mb: 2 }}>
          <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 12.5 }}>
            {data.notices.map((n, i) => <li key={i}>{n}</li>)}
          </Box>
        </Alert>
      )}

      <TableContainer>
        <Table size="small" sx={{
          '& th, & td': { border: '1px solid #d0d7de' },
        }}>
          <TableHead>
            {mergedOn ? (
              // Merged-view header — single row, no account split.
              <TableRow sx={{ '& th': { bgcolor: '#1565C0', color: '#fff',
                fontWeight: 800, fontSize: 11, textTransform: 'uppercase',
                letterSpacing: 0.5, py: 0.8 } }}>
                <TableCell>Index</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Class</TableCell>
                <TableCell align="right">Arrears</TableCell>
                <TableCell align="right">To Pay</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">Balance</TableCell>
              </TableRow>
            ) : (
              <>
                <TableRow sx={{ '& th': { bgcolor: '#1565C0', color: '#fff',
                  fontWeight: 800, fontSize: 11, textTransform: 'uppercase',
                  letterSpacing: 0.5, py: 0.7 } }}>
                  <TableCell rowSpan={2}>Index</TableCell>
                  <TableCell rowSpan={2}>Name</TableCell>
                  <TableCell rowSpan={2}>Class</TableCell>
                  {accounts.map(acc => (
                    <TableCell key={acc} colSpan={4} align="center">
                      {acc.replace(/_/g, ' ')}
                    </TableCell>
                  ))}
                  {/* Consolidated TOTAL group — sum across every account so
                      the user gets a single per-student figure at the end
                      of the row, regardless of the Analysis toggle. */}
                  <TableCell colSpan={4} align="center"
                    sx={{ bgcolor: '#0d47a1 !important', borderLeft: '2px solid #fff' }}>
                    Total
                  </TableCell>
                </TableRow>
                <TableRow sx={{ '& th': { bgcolor: '#1976d2', color: '#fff',
                  fontWeight: 700, fontSize: 9.5, textTransform: 'uppercase',
                  letterSpacing: 0.5, py: 0.5 } }}>
                  {accounts.flatMap(acc => [
                    <TableCell key={`${acc}-arr`}  align="right">Arrears</TableCell>,
                    <TableCell key={`${acc}-bill`} align="right">To Pay</TableCell>,
                    <TableCell key={`${acc}-paid`} align="right">Paid</TableCell>,
                    <TableCell key={`${acc}-bal`}  align="right">Balance</TableCell>,
                  ])}
                  <TableCell align="right"
                    sx={{ bgcolor: '#0d47a1 !important', borderLeft: '2px solid #fff' }}>
                    Arrears
                  </TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#0d47a1 !important' }}>To Pay</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#0d47a1 !important' }}>Paid</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#0d47a1 !important' }}>Balance</TableCell>
                </TableRow>
              </>
            )}
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={mergedOn ? 7 : (3 + accounts.length * 4 + 4)} align="center"
                  sx={{ py: 4, fontStyle: 'italic', color: 'text.disabled' }}>
                  No students match the current filter.
                </TableCell>
              </TableRow>
            )}
            {rows.map((r, i) => {
              // Consolidated total across every account on this row.
              // Sums arrears / billed / paid / balance so the rightmost
              // TOTAL group answers "what was carried in, what's owed
              // this period, what was paid, and what's left?"
              const totalArrears = accounts.reduce((s, a) => s + Number(r.accounts?.[a]?.arrears || 0), 0);
              const totalBilled  = accounts.reduce((s, a) => s + Number(r.accounts?.[a]?.billed  || 0), 0);
              const totalPaid    = accounts.reduce((s, a) => s + Number(r.accounts?.[a]?.paid    || 0), 0);
              const totalBalance = accounts.reduce((s, a) => s + Number(r.accounts?.[a]?.balance || 0), 0);

              // Merged view — one row per student, 4 totals only.
              if (mergedOn) {
                return (
                  <TableRow key={r.index_no || i} sx={{
                    '& td': { fontSize: 12, py: 0.65,
                      fontVariantNumeric: 'tabular-nums' },
                    bgcolor: i % 2 ? '#fafbfc' : '#fff',
                  }}>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                      {r.index_no}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                    <TableCell>{r.cla === 'Alumna' ? 'Alumni' : r.cla || '—'}</TableCell>
                    <TableCell align="right"
                      sx={{ fontWeight: 700, color: totalArrears > 0 ? '#b71c1c' : 'inherit' }}>
                      {totalArrears > 0 ? fmt(totalArrears) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {totalBilled > 0 ? fmt(totalBilled) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'success.dark' }}>
                      {totalPaid > 0 ? fmt(totalPaid) : '—'}
                    </TableCell>
                    <TableCell align="right"
                      sx={{ fontWeight: 900,
                            color: totalBalance > 0 ? '#b71c1c'
                              : totalBalance < 0 ? '#1b5e20' : 'inherit' }}>
                      {totalBalance !== 0 ? fmt(totalBalance) : '0.00'}
                    </TableCell>
                  </TableRow>
                );
              }

              // Full per-account view (default).
              return (
                <TableRow key={r.index_no || i} sx={{
                  '& td': { fontSize: 11, py: 0.55,
                    fontVariantNumeric: 'tabular-nums' },
                  bgcolor: i % 2 ? '#fafbfc' : '#fff',
                }}>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                    {r.index_no}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                  <TableCell>{r.cla === 'Alumna' ? 'Alumni' : r.cla || '—'}</TableCell>
                  {accounts.flatMap(acc => {
                    const v = r.accounts?.[acc] || { arrears: 0, billed: 0, paid: 0, balance: 0 };
                    return [
                      <TableCell key={`${acc}-arr`} align="right"
                        sx={{ color: Number(v.arrears) > 0 ? '#b71c1c' : 'inherit' }}>
                        {Number(v.arrears) > 0 ? fmt(v.arrears) : '—'}
                      </TableCell>,
                      <TableCell key={`${acc}-bill`} align="right">
                        {v.billed > 0 ? fmt(v.billed) : '—'}
                      </TableCell>,
                      <TableCell key={`${acc}-paid`} align="right">
                        {v.paid > 0 ? fmt(v.paid) : '—'}
                      </TableCell>,
                      <TableCell key={`${acc}-bal`} align="right"
                        sx={{ fontWeight: 700,
                          color: v.balance > 0 ? '#b71c1c'
                            : v.balance < 0 ? '#1b5e20' : 'inherit' }}>
                        {v.balance !== 0 ? fmt(v.balance) : '0.00'}
                      </TableCell>,
                    ];
                  })}
                  <TableCell align="right"
                    sx={{ fontWeight: 800, borderLeft: '2px solid #0d47a1',
                          color: totalArrears > 0 ? '#b71c1c' : 'inherit',
                          bgcolor: i % 2 ? '#eef3f9' : '#f4f8fc' }}>
                    {totalArrears > 0 ? fmt(totalArrears) : '—'}
                  </TableCell>
                  <TableCell align="right"
                    sx={{ fontWeight: 800,
                          bgcolor: i % 2 ? '#eef3f9' : '#f4f8fc' }}>
                    {totalBilled > 0 ? fmt(totalBilled) : '—'}
                  </TableCell>
                  <TableCell align="right"
                    sx={{ fontWeight: 800, color: 'success.dark',
                          bgcolor: i % 2 ? '#eef3f9' : '#f4f8fc' }}>
                    {totalPaid > 0 ? fmt(totalPaid) : '—'}
                  </TableCell>
                  <TableCell align="right"
                    sx={{ fontWeight: 900,
                          color: totalBalance > 0 ? '#b71c1c'
                            : totalBalance < 0 ? '#1b5e20' : 'inherit',
                          bgcolor: i % 2 ? '#eef3f9' : '#f4f8fc' }}>
                    {totalBalance !== 0 ? fmt(totalBalance) : '0.00'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Analysis Panel — only included in the DOM (and therefore in
          print output) when Analysis: ON */}
      {analysisOn && (
        <Box sx={{ mt: 2.5, breakInside: 'avoid', pageBreakInside: 'avoid' }}>
          <Box sx={{
            bgcolor: '#1565C0', color: '#fff', px: 2, py: 1,
            borderTopLeftRadius: 6, borderTopRightRadius: 6,
            fontFamily: 'Serif', letterSpacing: 1.2,
            fontWeight: 800, fontSize: 12, textTransform: 'uppercase',
          }}>
            Analysis · Restricted View
          </Box>
          <TableContainer>
            <Table size="small" sx={{
              '& th, & td': { border: '1px solid #d0d7de' },
            }}>
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: '#fafbfc',
                  fontWeight: 800, fontSize: 10.5, textTransform: 'uppercase',
                  letterSpacing: 0.5, color: 'text.secondary', py: 0.7 } }}>
                  <TableCell>Account</TableCell>
                  <TableCell align="right">Total Arrears</TableCell>
                  <TableCell align="right">Total Billed</TableCell>
                  <TableCell align="right">Total Paid</TableCell>
                  <TableCell align="right">Total Balance (Owing)</TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>% Collected</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map(a => {
                  const t = totals[a] || { arrears: 0, billed: 0, paid: 0, balance: 0 };
                  // Collection % is calculated against everything due
                  // (arrears + billed), so it still answers "how much
                  // of the total chargeable amount has been collected?"
                  const due = Number(t.arrears || 0) + Number(t.billed || 0);
                  const pct = due > 0 ? (t.paid / due) * 100 : 0;
                  return (
                    <TableRow key={a} hover sx={{ '& td': {
                      fontSize: 12, py: 0.6, fontVariantNumeric: 'tabular-nums' } }}>
                      <TableCell sx={{ fontWeight: 700 }}>{a.replace(/_/g, ' ')}</TableCell>
                      <TableCell align="right" sx={{ color: Number(t.arrears) > 0 ? 'error.main' : 'inherit' }}>
                        {fmt(t.arrears)}
                      </TableCell>
                      <TableCell align="right">{fmt(t.billed)}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.dark', fontWeight: 700 }}>
                        {fmt(t.paid)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'error.main', fontWeight: 800 }}>
                        {fmt(t.balance)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: pct >= 75 ? 'success.dark' : pct >= 40 ? 'warning.main' : 'error.main', fontWeight: 800 }}>
                        {pct.toFixed(1)}%
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow sx={{ '& td': { fontWeight: 900, py: 0.9, fontSize: 12.5,
                  borderTop: '2px solid', borderColor: 'primary.main',
                  bgcolor: '#fafbfc', fontVariantNumeric: 'tabular-nums' } }}>
                  <TableCell sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Grand Total · {data?.student_count || rows.length} student(s)
                  </TableCell>
                  <TableCell align="right" sx={{ color: Number(grand.arrears) > 0 ? 'error.main' : 'inherit' }}>
                    {fmt(grand.arrears)}
                  </TableCell>
                  <TableCell align="right">{fmt(grand.billed)}</TableCell>
                  <TableCell align="right" sx={{ color: 'success.dark' }}>{fmt(grand.paid)}</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}>{fmt(grand.balance)}</TableCell>
                  <TableCell align="right">
                    {(Number(grand.arrears) + Number(grand.billed)) > 0
                      ? ((grand.paid / (Number(grand.arrears) + Number(grand.billed))) * 100).toFixed(1)
                      : '0.0'}%
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary"
            sx={{ display: 'block', mt: 0.8, fontSize: 10, fontStyle: 'italic' }}>
            This analysis panel is intended for management review only.
          </Typography>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary"
        sx={{ display: 'block', mt: 2, fontSize: 10, fontStyle: 'italic' }}>
        Generated {new Date(data?.generatedAt || Date.now()).toLocaleString('en-GH')}
      </Typography>
    </Box>
  );
}

// ── Fee Reminders → Send SMS dialog ──────────────────────────────
// Opened from the toolbar when Fee Reminders is loaded. Uses the
// report rows as its recipient list, resolves phones via
// /api/sms/recipients?audience=list, then hands off to /api/sms/send.
// Tokens {name}, {class}, {balance}, {school} are substituted per
// student so one message body reaches everyone with their own values.
function FeeReminderSmsDialog({ open, onClose, rows, schoolName, defaultMessage, combine }) {
  const fmt = (n) => Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  // Consolidate each report row into a single "balance" number the
  // parent will see. When combine is on the report already carries a
  // pre-summed block; otherwise sum the per-account balances.
  const rowBalance = (r) => {
    if (combine && r.consolidated) return Number(r.consolidated.balance || 0);
    return Object.values(r.accounts || {})
      .reduce((s, a) => s + Number(a.balance || 0), 0);
  };

  const seedMessage = defaultMessage && defaultMessage.trim()
    ? defaultMessage
    : `Dear parent of {name} ({class}), our records show an outstanding fee balance of GHS {balance}. Please clear at your earliest convenience. Thank you — ${schoolName || 'School'}.`;

  const [message, setMessage]   = useState(seedMessage);
  const [channel, setChannel]   = useState('parent'); // parent | student
  const [checked, setChecked]   = useState(() => new Set(rows.map(r => r.index_no)));
  const [phones, setPhones]     = useState({});       // index_no → phone (233…)
  const [phoneLoad, setPhoneLoad] = useState(false);
  const [sending, setSending]   = useState(false);
  const [result, setResult]     = useState(null);     // { sent, failed, total }
  const [error, setError]       = useState('');

  // Reset every time the dialog is opened so a second visit picks up
  // the current row set + a fresh message.
  useEffect(() => {
    if (!open) return;
    setMessage(seedMessage);
    setChecked(new Set(rows.map(r => r.index_no)));
    setResult(null); setError('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fetch phones for the current row set. Debounced when channel
  // flips so the parent/student toggle re-pulls the preferred number.
  useEffect(() => {
    if (!open || rows.length === 0) return;
    setPhoneLoad(true);
    const inds = rows.map(r => r.index_no).filter(Boolean).join(',');
    API.get('/sms/recipients', {
      params: { audience: 'list', student_inds: inds, channel },
    })
      .then(r => {
        const map = {};
        for (const rec of (r.data?.recipients || [])) {
          map[rec.student_ind] = rec.phone || null;
        }
        setPhones(map);
      })
      .catch(() => setPhones({}))
      .finally(() => setPhoneLoad(false));
  }, [open, channel, rows]);

  const toggle = (ind) => {
    setChecked(prev => {
      const next = new Set(prev);
      next.has(ind) ? next.delete(ind) : next.add(ind);
      return next;
    });
  };
  const toggleAll = () => {
    if (checked.size === rows.length) setChecked(new Set());
    else setChecked(new Set(rows.map(r => r.index_no)));
  };
  const selectPhoneOnly = () => {
    setChecked(new Set(rows.filter(r => phones[r.index_no]).map(r => r.index_no)));
  };

  // Per-recipient message: swap {tokens} for that student's own values.
  const buildBody = (r) => {
    const bal = fmt(rowBalance(r));
    return String(message)
      .replaceAll('{name}',    r.name || '')
      .replaceAll('{class}',   r.cla === 'Alumna' ? 'Alumni' : (r.cla || ''))
      .replaceAll('{balance}', bal)
      .replaceAll('{school}',  schoolName || 'School');
  };

  const doSend = async () => {
    setSending(true); setError(''); setResult(null);
    try {
      // Group rows by identical rendered body so recipients that share
      // the exact same text can go out in a single /sms/send call
      // (fewer requests, one campaign_id → one row per student in the
      // log). With per-student tokens the group is almost always
      // size-1, but that's fine — we still batch when possible.
      const groups = new Map(); // body → [{ phone, student_ind, class_name }]
      for (const r of rows) {
        if (!checked.has(r.index_no)) continue;
        const phone = phones[r.index_no];
        if (!phone) continue;
        const body = buildBody(r).trim();
        if (!body) continue;
        const key = body;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push({
          phone,
          student_ind: r.index_no,
          class_name:  r.cla || null,
        });
      }
      if (groups.size === 0) {
        setError('Nothing to send — no selected student has a usable phone number.');
        setSending(false);
        return;
      }
      let sent = 0, failed = 0, total = 0;
      for (const [body, recipients] of groups.entries()) {
        total += recipients.length;
        // eslint-disable-next-line no-await-in-loop
        const res = await API.post('/sms/send', {
          recipients,
          message: body,
          kind: 'fee_reminder',
        });
        sent   += res.data?.sent   || 0;
        failed += res.data?.failed || 0;
      }
      setResult({ sent, failed, total });
    } catch (e) {
      setError(e.response?.data?.message || 'Send failed');
    } finally { setSending(false); }
  };

  const parts = (() => {
    const len = message.length;
    if (len === 0) return 0;
    if (len <= 160) return 1;
    return Math.ceil((len - 160) / 153) + 1;
  })();

  const withPhone    = rows.filter(r => phones[r.index_no]).length;
  const withoutPhone = rows.length - withPhone;
  const selectedWithPhone = rows.filter(r =>
    checked.has(r.index_no) && phones[r.index_no]).length;

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
        <SmsIcon color="primary" /> Send fee-reminder SMS
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center"
              justifyContent="space-between" sx={{ width: '100%' }}>
              <Box>
                <Typography variant="body2" fontWeight={700}>
                  {rows.length} student(s) in report · {withPhone} have a usable phone
                  {withoutPhone > 0 && ` · ${withoutPhone} without`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Tokens available: {'{name}'}, {'{class}'}, {'{balance}'}, {'{school}'}
                </Typography>
              </Box>
              <TextField select size="small" label="Prefer" value={channel}
                onChange={(e) => setChannel(e.target.value)} sx={{ minWidth: 160 }}>
                <MenuItem value="parent">Parent phone</MenuItem>
                <MenuItem value="student">Student phone</MenuItem>
              </TextField>
            </Stack>
          </Alert>

          <TextField multiline minRows={3} label="Message" value={message}
            onChange={(e) => setMessage(e.target.value)}
            helperText={`${message.length} chars · ${parts} SMS part${parts === 1 ? '' : 's'}`}
            disabled={sending} />

          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 320, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox size="small"
                      checked={rows.length > 0 && checked.size === rows.length}
                      indeterminate={checked.size > 0 && checked.size < rows.length}
                      onChange={toggleAll} disabled={sending} />
                  </TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell align="right">Balance (GHS)</TableCell>
                  <TableCell>Phone</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => {
                  const phone = phones[r.index_no];
                  const bal   = rowBalance(r);
                  return (
                    <TableRow key={r.index_no} hover
                      sx={{ opacity: phone ? 1 : 0.55 }}>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={checked.has(r.index_no)}
                          onChange={() => toggle(r.index_no)}
                          disabled={sending || !phone} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>{r.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{r.index_no}</Typography>
                      </TableCell>
                      <TableCell>{r.cla === 'Alumna' ? 'Alumni' : (r.cla || '—')}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {fmt(bal)}
                      </TableCell>
                      <TableCell>
                        {phone
                          ? <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{phone}</Typography>
                          : <Chip size="small" label="No phone" color="warning" variant="outlined" />}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          {phoneLoad && <LinearProgress />}
          {sending  && <LinearProgress color="secondary" />}
          {error    && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
          {result   && (
            <Alert severity={result.failed ? 'warning' : 'success'}>
              Sent {result.sent}/{result.total}{result.failed ? ` · ${result.failed} failed — check SMS Messaging → History` : ''}.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={selectPhoneOnly} disabled={sending || phoneLoad}>
          Select all with phone
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={sending}>Close</Button>
        <Button variant="contained" startIcon={sending
          ? <CircularProgress size={14} color="inherit" />
          : <SmsIcon />}
          onClick={doSend}
          disabled={sending || selectedWithPhone === 0 || message.trim().length === 0}>
          Send to {selectedWithPhone}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Fee Reminders report renderer ────────────────────────────────
// Each student with outstanding balance gets one compact slip. We
// fit two slips per row so an A4 sheet holds many — they're meant
// to be printed and cut up. Students with zero balance are already
// excluded by the backend, and within each slip only owing accounts
// are listed (so a student owing only PTA gets a one-row slip).
function FeeRemindersReport({ data, schoolName, customMessage, combine = false }) {
  const rows = data?.rows || [];
  const f    = data?.filters || {};
  const fmt  = (n) => Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  const today = new Date().toLocaleDateString('en-GH', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
  const cls = f.cla || 'All Classes';

  return (
    <Box>
      {/* Print summary header — only appears once at the top */}
      <Box sx={{ mb: 2, textAlign: 'center' }} className="no-print">
        <Typography variant="h6" fontWeight={800}>{schoolName || 'School'} · Fee Reminders</Typography>
        <Typography variant="body2" color="text.secondary">
          {cls}{f.term ? ` · ${f.term.replace('_', ' ')}` : ''}{f.acc_yer ? ` · ${f.acc_yer}` : ''}
          {' · '}{rows.length} student(s) with outstanding balance
        </Typography>
      </Box>

      {rows.length === 0 ? (
        <Card sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" color="success.dark" fontWeight={800}>✓ All students are up to date</Typography>
          <Typography variant="body2" color="text.secondary">
            No outstanding balances under the current filter — nothing to print.
          </Typography>
        </Card>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1.5,
        }}>
          {rows.map((r, i) => {
            const accounts = Object.entries(r.accounts || {});
            return (
              <Box key={r.index_no || i} sx={{
                border: '1.5px solid #0d47a1',
                borderRadius: 1,
                p: 1.4,
                breakInside: 'avoid',
                pageBreakInside: 'avoid',
                bgcolor: '#fff',
                fontFamily: 'Serif',
              }}>
                {/* School band */}
                <Box sx={{
                  borderBottom: '2px solid #0d47a1',
                  pb: 0.6, mb: 0.8, textAlign: 'center',
                }}>
                  <Typography sx={{
                    fontFamily: 'Serif', fontWeight: 800, fontSize: 13,
                    letterSpacing: 0.6, color: '#0d47a1', lineHeight: 1.2,
                  }}>
                    {schoolName || 'School'}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Serif', fontSize: 10,
                    fontStyle: 'italic', color: 'text.secondary' }}>
                    Statement of Outstanding Fees · {today}
                  </Typography>
                </Box>

                {/* Student identity */}
                <Stack direction="row" justifyContent="space-between" alignItems="baseline"
                  sx={{ mb: 0.6 }}>
                  <Box>
                    <Typography sx={{ fontFamily: 'Serif', fontSize: 10,
                      fontWeight: 700, color: 'text.secondary',
                      textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Student
                    </Typography>
                    <Typography sx={{ fontFamily: 'Serif', fontSize: 13, fontWeight: 800 }}>
                      {r.name}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: 10.5, fontWeight: 700 }}>
                      {r.index_no}
                    </Typography>
                    <Typography sx={{ fontFamily: 'Serif', fontSize: 10, color: 'text.secondary' }}>
                      {r.cla === 'Alumna' ? 'Alumni' : r.cla || '—'}
                    </Typography>
                  </Box>
                </Stack>

                {/* Owing accounts table — full Day-Payment-style columns:
                    Arrears | Term Bill | Scholarship | Adjustments |
                    Paid | Amount Due. With combine ON the per-account
                    rows collapse into a single consolidated line that
                    sums every column across the student's accounts
                    (matching the Day Payment screen totals). With OFF
                    each account gets its own row, same columns. */}
                {(() => {
                  // Prefer the server-side consolidated block when
                  // combine is on (it sums arrears, term bill,
                  // scholarship, adjustments, paid, total_due and
                  // balance across EVERY account the student has an
                  // exposure to — not just the ones with a remaining
                  // balance, which is what the old client-side sum
                  // was doing).
                  const con = combine && r.consolidated
                    ? r.consolidated
                    : null;
                  const sum = (k) => con
                    ? Number(con[k] || 0)
                    : accounts.reduce((s, [, v]) => s + Number(v[k] || 0), 0);
                  const cArrears   = sum('arrears');
                  const cTermBill  = sum('term_bill');
                  const cScholar   = sum('scholarship');
                  const cAdjust    = sum('adjustments');
                  const cPaid      = sum('paid');
                  const cTotalDue  = sum('total_due');
                  const cBalance   = sum('balance');
                  return (
                    <Table size="small" sx={{
                      '& th, & td': { border: '1px solid #b0bec5',
                        fontFamily: 'Serif', fontSize: 9.5, py: 0.4, px: 0.6 },
                    }}>
                      <TableHead>
                        <TableRow sx={{ '& th': { bgcolor: '#0d47a1', color: '#fff',
                          fontWeight: 800, fontSize: 8.5, textTransform: 'uppercase',
                          letterSpacing: 0.4 } }}>
                          <TableCell>{combine ? 'Description' : 'Account'}</TableCell>
                          <TableCell align="right">Arrears</TableCell>
                          <TableCell align="right">Term Bill</TableCell>
                          <TableCell align="right">Scholarship</TableCell>
                          <TableCell align="right">Adjustments</TableCell>
                          <TableCell align="right">Paid</TableCell>
                          <TableCell align="right">Amount Due</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {combine ? (
                          <TableRow sx={{ '& td': { fontVariantNumeric: 'tabular-nums' } }}>
                            <TableCell sx={{ fontWeight: 700 }}>
                              {accounts.length <= 1
                                ? 'Consolidated fees'
                                : `Consolidated fees (${accounts.length} accounts)`}
                            </TableCell>
                            <TableCell align="right" sx={{ color: cArrears > 0 ? '#b71c1c' : 'inherit' }}>
                              {cArrears > 0 ? fmt(cArrears) : '—'}
                            </TableCell>
                            <TableCell align="right">{fmt(cTermBill)}</TableCell>
                            <TableCell align="right">{cScholar > 0 ? fmt(cScholar) : '—'}</TableCell>
                            <TableCell align="right">{cAdjust !== 0 ? fmt(cAdjust) : '—'}</TableCell>
                            <TableCell align="right">{cPaid > 0 ? fmt(cPaid) : '—'}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, color: '#b71c1c' }}>
                              {fmt(cBalance)}
                            </TableCell>
                          </TableRow>
                        ) : (
                          accounts.map(([acc, v], j) => (
                            <TableRow key={acc} sx={{
                              bgcolor: j % 2 ? '#fafbfc' : '#fff',
                              '& td': { fontVariantNumeric: 'tabular-nums' },
                            }}>
                              <TableCell sx={{ fontWeight: 700 }}>{acc.replace(/_/g, ' ')}</TableCell>
                              <TableCell align="right" sx={{ color: Number(v.arrears) > 0 ? '#b71c1c' : 'inherit' }}>
                                {Number(v.arrears) > 0 ? fmt(v.arrears) : '—'}
                              </TableCell>
                              <TableCell align="right">{fmt(v.term_bill ?? v.billed)}</TableCell>
                              <TableCell align="right">{Number(v.scholarship || 0) > 0 ? fmt(v.scholarship) : '—'}</TableCell>
                              <TableCell align="right">{Number(v.adjustments || 0) !== 0 ? fmt(v.adjustments) : '—'}</TableCell>
                              <TableCell align="right">{v.paid > 0 ? fmt(v.paid) : '—'}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 800, color: '#b71c1c' }}>
                                {fmt(v.balance)}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                        <TableRow sx={{ '& td': { fontWeight: 900, fontSize: 10,
                          borderTop: '2px solid #0d47a1',
                          bgcolor: '#e3f2fd' } }}>
                          <TableCell sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Total
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {cArrears > 0 ? fmt(cArrears) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {fmt(cTermBill)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {cScholar > 0 ? fmt(cScholar) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {cAdjust !== 0 ? fmt(cAdjust) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {cPaid > 0 ? fmt(cPaid) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#b71c1c',
                            fontVariantNumeric: 'tabular-nums' }}>
                            GHS {fmt(r.total_owing)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  );
                })()}

                {/* Custom message + closing */}
                {customMessage && customMessage.trim() && (
                  <Typography sx={{
                    fontFamily: 'Serif', fontSize: 10.5,
                    fontStyle: 'italic', mt: 0.8, color: 'text.primary',
                    lineHeight: 1.45,
                    p: 0.7, bgcolor: '#fff8e1', borderRadius: 0.5,
                  }}>
                    {customMessage.replace(/\{\{name\}\}/g, r.name)
                                  .replace(/\{\{amount\}\}/g, fmt(r.total_owing))}
                  </Typography>
                )}

                <Typography sx={{
                  fontFamily: 'Serif', fontSize: 9.5, color: 'text.secondary',
                  mt: 0.6, textAlign: 'right', fontStyle: 'italic',
                }}>
                  Kindly remit the outstanding balance at your earliest convenience.
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

// ── Finance date / status filter card ───────────────────────────
function FinanceFilters({ keyName, value, onChange, onApply, loading, cashiers = [] }) {
  const active = activePreset(value);
  const apply = (preset) => {
    const next = { ...value, ...preset.range() };
    onChange(next);
    onApply(next);
  };
  return (
    <Card sx={{ mb: 2 }} className="no-print">
      <CardContent sx={{ pb: '16px !important' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.2 }}>
          <CategoryIcon color="primary" fontSize="small" />
          <Typography variant="overline" color="primary" fontWeight={700}>
            Filters
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Chip size="small" variant="outlined"
            label={`${value.from} → ${value.to}`}
            sx={{ fontWeight: 700, fontSize: 11 }} />
          {keyName === 'fin_general_report' && value.cashier && (
            <Chip size="small" color="primary"
              label={`Cashier: ${value.cashier}`}
              onDelete={() => { const next = { ...value, cashier: '' }; onChange(next); onApply(next); }}
              sx={{ fontWeight: 700 }} />
          )}
        </Stack>

        {/* Quick presets */}
        <Stack direction="row" spacing={0.6} flexWrap="wrap" rowGap={0.6} sx={{ mb: 1.5 }}>
          {DATE_PRESETS.map(p => (
            <Chip key={p.v} size="small" label={p.label}
              color={active === p.v ? 'primary' : 'default'}
              variant={active === p.v ? 'filled' : 'outlined'}
              onClick={() => apply(p)}
              sx={{ fontWeight: 700, cursor: 'pointer' }} />
          ))}
          {active === 'custom' && (
            <Chip size="small" label="Custom" color="primary" variant="filled"
              sx={{ fontWeight: 700 }} />
          )}
        </Stack>

        <Divider sx={{ mb: 1.5 }} />

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="center">
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
            value={value.from} onChange={(e) => onChange({ ...value, from: e.target.value })}
            sx={{ flex: 1 }} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
            value={value.to} onChange={(e) => onChange({ ...value, to: e.target.value })}
            sx={{ flex: 1 }} />
          {keyName === 'fin_adjustments' && (
            <TextField select size="small" label="Status" value={value.status || ''}
              onChange={(e) => onChange({ ...value, status: e.target.value })}
              sx={{ flex: 1, minWidth: 140 }}>
              <MenuItem value="">All</MenuItem>
              {['POSTED', 'PENDING', 'REJECTED'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          )}
          {keyName === 'fin_general_report' && (
            <TextField select size="small" label="Cashier" value={value.cashier || ''}
              onChange={(e) => onChange({ ...value, cashier: e.target.value })}
              sx={{ flex: 1, minWidth: 200 }}>
              <MenuItem value="">All cashiers</MenuItem>
              {cashiers.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          )}
          <Button variant="contained" size="medium" onClick={() => onApply()}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            sx={{ textTransform: 'none', fontWeight: 800, minWidth: 110 }}>
            Apply
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Accounting filters card ─────────────────────────────────────
// Trial balance, bank reconciliation and bank analysis share the same
// date-range pattern. The two bank reports also expose an account
// dropdown that's populated from the loaded data.
function AccountingFilters({ keyName, value, onChange, onApply, loading, banks = [] }) {
  const active = activePreset(value);
  const apply = (preset) => {
    const next = { ...value, ...preset.range() };
    onChange(next);
    onApply(next);
  };
  const showBankPicker = BANK_REPORT_KEYS.has(keyName);
  return (
    <Card sx={{ mb: 2 }} className="no-print">
      <CardContent sx={{ pb: '16px !important' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.2 }}>
          <CategoryIcon color="primary" fontSize="small" />
          <Typography variant="overline" color="primary" fontWeight={700}>
            Filters
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Chip size="small" variant="outlined"
            label={`${value.from} → ${value.to}`}
            sx={{ fontWeight: 700, fontSize: 11 }} />
          {value.account && (
            <Chip size="small" color="primary"
              label={`Account: ${value.account}`}
              onDelete={() => { const next = { ...value, account: '' }; onChange(next); onApply(next); }}
              sx={{ fontWeight: 700 }} />
          )}
        </Stack>

        <Stack direction="row" spacing={0.6} flexWrap="wrap" rowGap={0.6} sx={{ mb: 1.5 }}>
          {DATE_PRESETS.map(p => (
            <Chip key={p.v} size="small" label={p.label}
              color={active === p.v ? 'primary' : 'default'}
              variant={active === p.v ? 'filled' : 'outlined'}
              onClick={() => apply(p)}
              sx={{ fontWeight: 700, cursor: 'pointer' }} />
          ))}
          {active === 'custom' && (
            <Chip size="small" label="Custom" color="primary" variant="filled"
              sx={{ fontWeight: 700 }} />
          )}
        </Stack>

        <Divider sx={{ mb: 1.5 }} />

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="center">
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
            value={value.from} onChange={(e) => onChange({ ...value, from: e.target.value })}
            sx={{ flex: 1 }} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
            value={value.to} onChange={(e) => onChange({ ...value, to: e.target.value })}
            sx={{ flex: 1 }} />
          {showBankPicker && (
            <TextField select size="small" label="Bank Account" value={value.account || ''}
              onChange={(e) => onChange({ ...value, account: e.target.value })}
              sx={{ flex: 1, minWidth: 200 }}>
              <MenuItem value="">All banks</MenuItem>
              {banks.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
            </TextField>
          )}
          <Button variant="contained" size="medium" onClick={() => onApply()}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            sx={{ textTransform: 'none', fontWeight: 800, minWidth: 110 }}>
            Apply
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Ledger Filters ─────────────────────────────────────────────
// Date presets + From/To plus drill-down dropdowns specific to the
// chosen ledger (Expenditure vs Income). All distinct values come
// from the report response so the dropdowns reflect what's actually
// in the period. Apply hits the report endpoint with the populated
// filters.
function LedgerFilters({ keyName, value, onChange, onApply, loading, distinct = {} }) {
  const isExp = keyName === 'acc_expenditure_ledger';
  const active = activePreset(value);
  const apply = (preset) => {
    const next = { ...value, ...preset.range() };
    onChange(next);
    onApply(next);
  };
  const clearChip = (k) => {
    const next = { ...value, [k]: '' };
    onChange(next); onApply(next);
  };

  return (
    <Card sx={{ mb: 2 }} className="no-print">
      <CardContent sx={{ pb: '16px !important' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.2 }}>
          <CategoryIcon color="primary" fontSize="small" />
          <Typography variant="overline" color="primary" fontWeight={700}>Filters</Typography>
          <Box sx={{ flex: 1 }} />
          <Chip size="small" variant="outlined"
            label={`${value.from} → ${value.to}`}
            sx={{ fontWeight: 700, fontSize: 11 }} />
          {isExp && value.source && (
            <Chip size="small" color="primary" label={`Source: ${value.source}`}
              onDelete={() => clearChip('source')} sx={{ fontWeight: 700 }} />
          )}
          {isExp && value.category && (
            <Chip size="small" color="error" label={`Cat: ${value.category}`}
              onDelete={() => clearChip('category')} sx={{ fontWeight: 700 }} />
          )}
          {isExp && value.type && (
            <Chip size="small" label={`Type: ${value.type}`}
              onDelete={() => clearChip('type')} sx={{ fontWeight: 700 }} />
          )}
          {isExp && value.payee && (
            <Chip size="small" label={`Payee: ${value.payee}`}
              onDelete={() => clearChip('payee')} sx={{ fontWeight: 700 }} />
          )}
          {isExp && value.account && (
            <Chip size="small" label={`Bank: ${value.account}`}
              onDelete={() => clearChip('account')} sx={{ fontWeight: 700 }} />
          )}
          {!isExp && value.scope && (
            <Chip size="small" color="primary" label={`Scope: ${value.scope}`}
              onDelete={() => clearChip('scope')} sx={{ fontWeight: 700 }} />
          )}
          {!isExp && value.income_type && (
            <Chip size="small" color="success" label={`Income: ${value.income_type}`}
              onDelete={() => clearChip('income_type')} sx={{ fontWeight: 700 }} />
          )}
          {!isExp && value.mode && (
            <Chip size="small" label={`Mode: ${value.mode}`}
              onDelete={() => clearChip('mode')} sx={{ fontWeight: 700 }} />
          )}
          {!isExp && value.cashier && (
            <Chip size="small" label={`Cashier: ${value.cashier}`}
              onDelete={() => clearChip('cashier')} sx={{ fontWeight: 700 }} />
          )}
          {!isExp && value.class_name && (
            <Chip size="small" label={`Class: ${value.class_name}`}
              onDelete={() => clearChip('class_name')} sx={{ fontWeight: 700 }} />
          )}
        </Stack>

        <Stack direction="row" spacing={0.6} flexWrap="wrap" rowGap={0.6} sx={{ mb: 1.5 }}>
          {DATE_PRESETS.map(p => (
            <Chip key={p.v} size="small" label={p.label}
              color={active === p.v ? 'primary' : 'default'}
              variant={active === p.v ? 'filled' : 'outlined'}
              onClick={() => apply(p)}
              sx={{ fontWeight: 700, cursor: 'pointer' }} />
          ))}
          {active === 'custom' && (
            <Chip size="small" label="Custom" color="primary" variant="filled"
              sx={{ fontWeight: 700 }} />
          )}
        </Stack>

        <Divider sx={{ mb: 1.5 }} />

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="center"
          flexWrap="wrap" rowGap={1.5}>
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
            value={value.from} onChange={(e) => onChange({ ...value, from: e.target.value })}
            sx={{ flex: 1, minWidth: 140 }} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
            value={value.to} onChange={(e) => onChange({ ...value, to: e.target.value })}
            sx={{ flex: 1, minWidth: 140 }} />

          {isExp ? (
            <>
              <TextField select size="small" label="Source" value={value.source || ''}
                onChange={(e) => onChange({ ...value, source: e.target.value })}
                sx={{ flex: 1, minWidth: 130 }}>
                <MenuItem value="">All sources</MenuItem>
                {(distinct.sources || []).map(s => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </TextField>
              <TextField select size="small" label="Category" value={value.category || ''}
                onChange={(e) => onChange({ ...value, category: e.target.value })}
                sx={{ flex: 1, minWidth: 160 }}>
                <MenuItem value="">All categories</MenuItem>
                {(distinct.categories || []).map(c => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </TextField>
              <TextField select size="small" label="Type" value={value.type || ''}
                onChange={(e) => onChange({ ...value, type: e.target.value })}
                sx={{ flex: 1, minWidth: 160 }}>
                <MenuItem value="">All types</MenuItem>
                {(distinct.types || []).map(t => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </TextField>
              <TextField select size="small" label="Payee" value={value.payee || ''}
                onChange={(e) => onChange({ ...value, payee: e.target.value })}
                sx={{ flex: 1, minWidth: 160 }}>
                <MenuItem value="">All payees</MenuItem>
                {(distinct.payees || []).map(p => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </TextField>
              <TextField select size="small" label="Bank Account" value={value.account || ''}
                onChange={(e) => onChange({ ...value, account: e.target.value })}
                sx={{ flex: 1, minWidth: 160 }}>
                <MenuItem value="">All accounts</MenuItem>
                {(distinct.accounts || []).map(a => (
                  <MenuItem key={a} value={a}>{a}</MenuItem>
                ))}
              </TextField>
            </>
          ) : (
            <>
              <TextField select size="small" label="Scope" value={value.scope || ''}
                onChange={(e) => onChange({ ...value, scope: e.target.value })}
                sx={{ flex: 1, minWidth: 150 }}>
                <MenuItem value="">All scopes</MenuItem>
                {(distinct.scopes || []).map(s => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </TextField>
              <TextField select size="small" label="Income Type" value={value.income_type || ''}
                onChange={(e) => onChange({ ...value, income_type: e.target.value })}
                sx={{ flex: 1, minWidth: 160 }}>
                <MenuItem value="">All income types</MenuItem>
                {(distinct.income_types || []).map(t => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </TextField>
              <TextField select size="small" label="Mode" value={value.mode || ''}
                onChange={(e) => onChange({ ...value, mode: e.target.value })}
                sx={{ flex: 1, minWidth: 130 }}>
                <MenuItem value="">All modes</MenuItem>
                {(distinct.modes || []).map(m => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </TextField>
              <TextField select size="small" label="Cashier" value={value.cashier || ''}
                onChange={(e) => onChange({ ...value, cashier: e.target.value })}
                sx={{ flex: 1, minWidth: 160 }}>
                <MenuItem value="">All cashiers</MenuItem>
                {(distinct.cashiers || []).map(c => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </TextField>
              <TextField select size="small" label="Class" value={value.class_name || ''}
                onChange={(e) => onChange({ ...value, class_name: e.target.value })}
                sx={{ flex: 1, minWidth: 150 }}>
                <MenuItem value="">All classes</MenuItem>
                {(distinct.classes || []).map(c => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </TextField>
            </>
          )}

          <Button variant="contained" size="medium" onClick={() => onApply()}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            sx={{ textTransform: 'none', fontWeight: 800, minWidth: 110 }}>
            Apply
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Accounting report renderers ─────────────────────────────────
const ACCOUNT_TYPE_LABELS = {
  asset:     'Assets',
  liability: 'Liabilities',
  equity:    'Equity',
  income:    'Revenue',
  expense:   'Expenses',
};
const ACCOUNT_TYPE_ORDER = ['asset', 'liability', 'equity', 'income', 'expense'];
const ACCOUNT_TYPE_COLOR = {
  asset:     '#1565C0',
  liability: '#c62828',
  equity:    '#6a1b9a',
  income:    '#2e7d32',
  expense:   '#ef6c00',
};

function TrialBalanceReport({ data, schoolName }) {
  const subtitle = `${data.from} → ${data.to}`;
  const balanced = Math.abs(Number(data.grand?.difference || 0)) < 0.01;
  const groups = ACCOUNT_TYPE_ORDER.map(t => {
    const rows = (data.rows || []).filter(r => r.account_type === t);
    const dr = rows.reduce((s, r) => s + Number(r.dr_total || 0), 0);
    const cr = rows.reduce((s, r) => s + Number(r.cr_total || 0), 0);
    return { type: t, label: ACCOUNT_TYPE_LABELS[t], rows, dr, cr };
  }).filter(g => g.rows.length > 0);

  return (
    <Box>
      <PrintHeader schoolName={schoolName} title="Trial Balance" subtitle={subtitle} />

      {/* Status strip */}
      <Stack direction="row" spacing={1.2} flexWrap="wrap" rowGap={1} sx={{ mb: 2 }}>
        <Chip color="primary" label={`Total Debits · GHS ${fmt2(data.grand?.dr)}`}
          sx={{ fontWeight: 800, fontSize: 13, height: 30 }} />
        <Chip color="primary" variant="outlined"
          label={`Total Credits · GHS ${fmt2(data.grand?.cr)}`}
          sx={{ fontWeight: 800, fontSize: 13, height: 30 }} />
        <Chip color={balanced ? 'success' : 'error'}
          label={balanced
            ? 'Balanced ✓'
            : `Out of balance: GHS ${fmt2(Math.abs(data.grand?.difference))}`}
          sx={{ fontWeight: 800, fontSize: 13, height: 30 }} />
        <Chip variant="outlined" label={`${data.rows?.length || 0} accounts with movement`}
          sx={{ fontWeight: 700 }} />
      </Stack>

      <TableContainer>
        <Table size="small" sx={{
          '& .MuiTableCell-head': { bgcolor: '#fafbfc', fontWeight: 800, fontSize: 11,
            textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary',
            borderBottom: '2px solid', borderColor: 'primary.light' },
        }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 110 }}>Code</TableCell>
              <TableCell>Account</TableCell>
              <TableCell align="right" sx={{ width: 140 }}>Debit (GHS)</TableCell>
              <TableCell align="right" sx={{ width: 140 }}>Credit (GHS)</TableCell>
              <TableCell align="right" sx={{ width: 140 }}>Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.flatMap(g => [
              <TableRow key={`hd-${g.type}`} sx={{
                bgcolor: alpha(ACCOUNT_TYPE_COLOR[g.type], 0.06),
                '& td': { fontWeight: 800, py: 0.9, borderBottom: '2px solid',
                  borderColor: alpha(ACCOUNT_TYPE_COLOR[g.type], 0.3),
                  color: ACCOUNT_TYPE_COLOR[g.type] },
              }}>
                <TableCell colSpan={5} sx={{ fontSize: 11, textTransform: 'uppercase',
                  letterSpacing: 1 }}>
                  {g.label}
                </TableCell>
              </TableRow>,
              ...g.rows.map(r => (
                <TableRow key={r.account_id} hover sx={{
                  '& td': { fontSize: 12, py: 0.85, fontVariantNumeric: 'tabular-nums' },
                }}>
                  <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                    {r.code}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{r.account_name}</TableCell>
                  <TableCell align="right"
                    sx={{ color: r.dr_total > 0 ? 'text.primary' : 'text.disabled',
                          fontWeight: r.dr_total > 0 ? 700 : 400 }}>
                    {r.dr_total > 0 ? fmt2(r.dr_total) : '—'}
                  </TableCell>
                  <TableCell align="right"
                    sx={{ color: r.cr_total > 0 ? 'text.primary' : 'text.disabled',
                          fontWeight: r.cr_total > 0 ? 700 : 400 }}>
                    {r.cr_total > 0 ? fmt2(r.cr_total) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800,
                    color: r.balance >= 0 ? 'text.primary' : 'error.main' }}>
                    {fmt2(r.balance)} {r.balance_side}
                  </TableCell>
                </TableRow>
              )),
              <TableRow key={`sub-${g.type}`} sx={{
                '& td': { fontWeight: 800, py: 0.9,
                  borderBottom: '1px solid', borderColor: 'divider',
                  bgcolor: alpha(ACCOUNT_TYPE_COLOR[g.type], 0.03) },
              }}>
                <TableCell />
                <TableCell sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                  Subtotal · {g.label}
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {fmt2(g.dr)}
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {fmt2(g.cr)}
                </TableCell>
                <TableCell />
              </TableRow>,
            ])}
            <TableRow sx={{
              '& td': { fontWeight: 900, py: 1.2, fontSize: 13,
                borderTop: '3px double', borderColor: 'text.primary',
                bgcolor: '#fafbfc' },
            }}>
              <TableCell />
              <TableCell sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Grand Total
              </TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmt2(data.grand?.dr)}
              </TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmt2(data.grand?.cr)}
              </TableCell>
              <TableCell align="right"
                sx={{ color: balanced ? 'success.dark' : 'error.main' }}>
                {balanced ? '✓ Balanced' : `Δ ${fmt2(Math.abs(data.grand?.difference))}`}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function BankReconciliationReport({ data, schoolName }) {
  const subtitle = `${data.from} → ${data.to}${data.account ? ` · ${data.account}` : ' · All banks'}`;
  return (
    <Box>
      <PrintHeader schoolName={schoolName} title="Bank Reconciliation" subtitle={subtitle} />

      {/* Per-account summary */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(260px, 1fr))' },
        gap: 1.5, mb: 2 }}>
        {(data.accounts || []).map(a => (
          <Card key={a.account} sx={{ p: 1.5, borderLeft: '4px solid', borderColor: 'primary.main' }}>
            <Typography variant="subtitle2" fontWeight={800}>{a.account}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.4, mt: 1, fontSize: 12 }}>
              <Typography variant="caption">Opening</Typography>
              <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                GHS {fmt2(a.opening)}
              </Typography>
              <Typography variant="caption">Deposits (+)</Typography>
              <Typography sx={{ fontVariantNumeric: 'tabular-nums', color: 'success.dark', fontWeight: 700 }}>
                GHS {fmt2(a.deposits)}
              </Typography>
              <Typography variant="caption">Payments (−)</Typography>
              <Typography sx={{ fontVariantNumeric: 'tabular-nums', color: 'error.main', fontWeight: 700 }}>
                GHS {fmt2(a.payments)}
              </Typography>
              <Typography variant="caption" fontWeight={800}
                sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 0.4 }}>
                Closing (Book)
              </Typography>
              <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 800, fontSize: 14,
                borderTop: '1px solid', borderColor: 'divider', pt: 0.4 }}>
                GHS {fmt2(a.closing)}
              </Typography>
            </Box>
          </Card>
        ))}
      </Box>

      {/* Per-account activity */}
      {(data.accounts || []).map(a => {
        const accRows = (data.rows || []).filter(r => r.account === a.account);
        return (
          <Box key={a.account} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.7,
              borderBottom: '2px solid', borderColor: 'primary.main', pb: 0.5 }}>
              {a.account} · activity
            </Typography>
            <TableContainer>
              <Table size="small" sx={{
                '& .MuiTableCell-head': { bgcolor: '#fafbfc', fontWeight: 800, fontSize: 11,
                  textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' },
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 110 }}>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell sx={{ width: 110 }}>Cheque</TableCell>
                    <TableCell align="right" sx={{ width: 130 }}>Deposit</TableCell>
                    <TableCell align="right" sx={{ width: 130 }}>Payment</TableCell>
                    <TableCell align="right" sx={{ width: 140 }}>Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow sx={{ bgcolor: alpha('#1565C0', 0.04),
                    '& td': { fontWeight: 800, py: 0.85 } }}>
                    <TableCell />
                    <TableCell sx={{ fontStyle: 'italic' }}>Opening Balance</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {fmt2(a.opening)}
                    </TableCell>
                  </TableRow>
                  {accRows.length === 0 && (
                    <TableRow><TableCell colSpan={6} align="center"
                      sx={{ py: 3, color: 'text.disabled', fontStyle: 'italic' }}>
                      No activity in this period.
                    </TableCell></TableRow>
                  )}
                  {accRows.map(r => (
                    <TableRow key={r.id} hover sx={{ '& td': { fontSize: 12, py: 0.7,
                      fontVariantNumeric: 'tabular-nums' } }}>
                      <TableCell sx={{ color: 'text.secondary' }}>{r.date}</TableCell>
                      <TableCell sx={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Tooltip title={r.description}>
                          <span>{r.description}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{r.cheque_no || '—'}</TableCell>
                      <TableCell align="right" sx={{ color: r.deposit > 0 ? 'success.dark' : 'text.disabled',
                        fontWeight: r.deposit > 0 ? 700 : 400 }}>
                        {r.deposit > 0 ? fmt2(r.deposit) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: r.payment > 0 ? 'error.main' : 'text.disabled',
                        fontWeight: r.payment > 0 ? 700 : 400 }}>
                        {r.payment > 0 ? fmt2(r.payment) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt2(r.balance)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: alpha('#1565C0', 0.06),
                    '& td': { fontWeight: 800, py: 1, borderTop: '2px solid', borderColor: 'primary.main' } }}>
                    <TableCell />
                    <TableCell sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Closing Balance · {a.account}
                    </TableCell>
                    <TableCell />
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'success.dark' }}>
                      {fmt2(a.deposits)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'error.main' }}>
                      {fmt2(a.payments)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>
                      {fmt2(a.closing)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })}

      {/* Per-account reconciliation statements */}
      {(data.accounts || []).map(a => (
        <ReconciliationStatement key={`recon-${a.account}`} account={a} asOf={data.to} />
      ))}
    </Box>
  );
}

// Standard accountant's bank reconciliation statement: two-column —
// Bank side (per statement) and Cash Book side. The user fills in the
// statement balance and any reconciling items; both sides compute live
// and the result must agree to be reconciled. Stored only in component
// state — printable via the global Print button.
function ReconciliationStatement({ account, asOf }) {
  const [statementBal,    setStatementBal]    = useState('');
  const [depositsInTransit, setDepositsInTransit] = useState('');
  const [unpresentedCheq, setUnpresentedCheq] = useState('');
  const [otherBankAdd,    setOtherBankAdd]    = useState('');
  const [otherBankLess,   setOtherBankLess]   = useState('');
  const [directCredits,   setDirectCredits]   = useState('');
  const [bankCharges,     setBankCharges]     = useState('');
  const [otherBookAdd,    setOtherBookAdd]    = useState('');
  const [otherBookLess,   setOtherBookLess]   = useState('');

  const num = (v) => {
    const n = Number(String(v || '').replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
  };
  const bookBalance = Number(account.closing || 0);
  const adjBank =
      num(statementBal)
    + num(depositsInTransit)
    - num(unpresentedCheq)
    + num(otherBankAdd)
    - num(otherBankLess);
  const adjBook =
      bookBalance
    + num(directCredits)
    - num(bankCharges)
    + num(otherBookAdd)
    - num(otherBookLess);
  const difference = Number((adjBank - adjBook).toFixed(2));
  const reconciled = Math.abs(difference) < 0.01 && (statementBal !== '');
  const touched    = statementBal !== '' || depositsInTransit !== '' ||
                     unpresentedCheq !== '' || directCredits !== '' || bankCharges !== '';

  // Reusable inline input — keeps the table looking like a printed
  // statement (no Material text-field chrome), with right-aligned
  // tabular numbers.
  const moneyInput = (value, setValue, placeholder = '0.00') => (
    <input
      type="text"
      inputMode="decimal"
      value={value}
      placeholder={placeholder}
      onChange={(e) => setValue(e.target.value)}
      style={{
        width: '100%',
        textAlign: 'right',
        border: 'none',
        borderBottom: '1px solid #94a3b8',
        background: 'transparent',
        font: 'inherit',
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 600,
        padding: '2px 4px',
        outline: 'none',
      }}
    />
  );

  const lineRow = (label, valueCell, opts = {}) => (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: '1fr 130px',
      alignItems: 'center',
      gap: 1,
      py: 0.5,
      borderBottom: opts.divider ? '1px solid' : 'none',
      borderTop: opts.topRule ? '2px solid #000' : 'none',
      borderBottomColor: 'rgba(0,0,0,0.08)',
      ...opts.sx,
    }}>
      <Typography sx={{
        fontSize: opts.bold ? 13 : 12,
        fontWeight: opts.bold ? 800 : (opts.semibold ? 700 : 500),
        color: opts.muted ? 'text.secondary' : 'text.primary',
      }}>
        {label}
      </Typography>
      <Box sx={{ textAlign: 'right' }}>{valueCell}</Box>
    </Box>
  );

  const moneyLabel = (n, opts = {}) => (
    <Typography sx={{
      fontVariantNumeric: 'tabular-nums',
      fontWeight: opts.bold ? 800 : 700,
      fontSize: opts.bold ? 14 : 12,
      color: opts.color || 'text.primary',
    }}>
      {fmt2(n)}
    </Typography>
  );

  return (
    <Card sx={{
      p: { xs: 1.5, md: 2.5 },
      mt: 2.5,
      border: '1px solid',
      borderColor: 'primary.main',
      breakInside: 'avoid', pageBreakInside: 'avoid',
    }}>
      <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="overline" color="primary" fontWeight={800}
            sx={{ letterSpacing: 1.2 }}>
            Bank Reconciliation Statement
          </Typography>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
            {account.account}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            As of {asOf}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        {touched && (
          <Chip
            color={reconciled ? 'success' : 'error'}
            label={reconciled
              ? '✓ Reconciled'
              : `Out of balance · ${fmt2(Math.abs(difference))}`}
            sx={{ fontWeight: 800, fontSize: 12, height: 28 }}
          />
        )}
      </Stack>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: { xs: 2, md: 3 },
      }}>
        {/* ── BANK SIDE ─────────────────────────────────────────── */}
        <Box>
          <Box sx={{
            bgcolor: alpha('#1565C0', 0.08),
            border: '1px solid', borderColor: 'primary.light',
            borderRadius: 1, px: 1.4, py: 0.7, mb: 1,
          }}>
            <Typography variant="subtitle2" fontWeight={800} color="primary.main"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 11 }}>
              Per Bank Statement
            </Typography>
          </Box>

          {lineRow('Balance per Bank Statement',
            moneyInput(statementBal, setStatementBal),
            { semibold: true })}

          {lineRow('Add: Deposits in transit',
            moneyInput(depositsInTransit, setDepositsInTransit))}

          {lineRow('Add: Other (specify)',
            moneyInput(otherBankAdd, setOtherBankAdd),
            { muted: true })}

          {lineRow('Less: Unpresented cheques',
            moneyInput(unpresentedCheq, setUnpresentedCheq))}

          {lineRow('Less: Other (specify)',
            moneyInput(otherBankLess, setOtherBankLess),
            { muted: true, divider: true })}

          {lineRow('Adjusted Bank Balance',
            moneyLabel(adjBank, { bold: true, color: 'primary.main' }),
            { bold: true, topRule: true, sx: { borderTop: '2px solid #000', mt: 0.4 } })}
        </Box>

        {/* ── CASH BOOK SIDE ────────────────────────────────────── */}
        <Box>
          <Box sx={{
            bgcolor: alpha('#2e7d32', 0.08),
            border: '1px solid', borderColor: 'success.light',
            borderRadius: 1, px: 1.4, py: 0.7, mb: 1,
          }}>
            <Typography variant="subtitle2" fontWeight={800} color="success.dark"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 11 }}>
              Per Cash Book
            </Typography>
          </Box>

          {lineRow('Balance per Cash Book',
            moneyLabel(bookBalance),
            { semibold: true })}

          {lineRow('Add: Direct credits / Bank receipts',
            moneyInput(directCredits, setDirectCredits))}

          {lineRow('Add: Other (specify)',
            moneyInput(otherBookAdd, setOtherBookAdd),
            { muted: true })}

          {lineRow('Less: Bank charges / Returned cheques',
            moneyInput(bankCharges, setBankCharges))}

          {lineRow('Less: Other (specify)',
            moneyInput(otherBookLess, setOtherBookLess),
            { muted: true, divider: true })}

          {lineRow('Adjusted Cash Book Balance',
            moneyLabel(adjBook, { bold: true, color: 'success.dark' }),
            { bold: true, topRule: true, sx: { borderTop: '2px solid #000', mt: 0.4 } })}
        </Box>
      </Box>

      {/* Reconciliation result strip */}
      <Box sx={{
        mt: 2, p: 1.4,
        borderRadius: 1,
        bgcolor: !touched
          ? alpha('#94a3b8', 0.08)
          : reconciled
            ? alpha('#2e7d32', 0.10)
            : alpha('#d32f2f', 0.08),
        border: '1px solid',
        borderColor: !touched ? 'divider'
          : reconciled ? 'success.light' : 'error.light',
      }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={1}>
          <Typography sx={{
            fontWeight: 800, fontSize: 13,
            color: !touched ? 'text.secondary'
                 : reconciled ? 'success.dark' : 'error.dark',
            textTransform: 'uppercase', letterSpacing: 0.6,
          }}>
            {!touched
              ? 'Enter the statement balance to begin'
              : reconciled ? 'Both sides agree — reconciled'
                           : 'Difference must be investigated'}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
            Adjusted Bank: <strong>GHS {fmt2(adjBank)}</strong>
            &nbsp;·&nbsp;
            Adjusted Book: <strong>GHS {fmt2(adjBook)}</strong>
            &nbsp;·&nbsp;
            Difference:{' '}
            <strong style={{
              color: reconciled ? '#1b5e20' : '#b71c1c',
            }}>
              GHS {fmt2(Math.abs(difference))}
            </strong>
          </Typography>
        </Stack>
      </Box>

      {/* Signatures */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ borderBottom: '1px solid #000', minHeight: 36 }} />
          <Typography fontSize={10} color="text.secondary" sx={{ mt: 0.4 }}>
            Prepared by · Date
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ borderBottom: '1px solid #000', minHeight: 36 }} />
          <Typography fontSize={10} color="text.secondary" sx={{ mt: 0.4 }}>
            Reviewed by · Date
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ borderBottom: '1px solid #000', minHeight: 36 }} />
          <Typography fontSize={10} color="text.secondary" sx={{ mt: 0.4 }}>
            Approved by · Date
          </Typography>
        </Box>
      </Stack>
    </Card>
  );
}

function BankAnalysisReport({ data, schoolName }) {
  const subtitle = `${data.from} → ${data.to}${data.account ? ` · ${data.account}` : ' · All banks'}`;
  // Pivot the trend by account → month for the side-by-side mini-bars.
  const months = [...new Set((data.trend || []).map(t => t.mon_yer))];
  return (
    <Box>
      <PrintHeader schoolName={schoolName} title="Bank Analysis" subtitle={subtitle} />

      {/* Per-account summary */}
      <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
        Movement Summary
      </Typography>
      <TableContainer sx={{ mb: 2.5 }}>
        <Table size="small" sx={{
          '& .MuiTableCell-head': { bgcolor: '#fafbfc', fontWeight: 800, fontSize: 11,
            textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary',
            borderBottom: '2px solid', borderColor: 'primary.light' },
        }}>
          <TableHead>
            <TableRow>
              <TableCell>Account</TableCell>
              <TableCell align="right">Opening</TableCell>
              <TableCell align="right">Deposits (+)</TableCell>
              <TableCell align="right">Payments (−)</TableCell>
              <TableCell align="right">Net</TableCell>
              <TableCell align="right">Closing</TableCell>
              <TableCell align="right" sx={{ width: 90 }}>Dep · Pay #</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data.accounts || []).map(a => (
              <TableRow key={a.account} hover sx={{ '& td': { fontSize: 12, py: 0.85,
                fontVariantNumeric: 'tabular-nums' } }}>
                <TableCell sx={{ fontWeight: 700 }}>{a.account}</TableCell>
                <TableCell align="right">{fmt2(a.opening)}</TableCell>
                <TableCell align="right" sx={{ color: 'success.dark', fontWeight: 700 }}>
                  {fmt2(a.deposits)}
                </TableCell>
                <TableCell align="right" sx={{ color: 'error.main', fontWeight: 700 }}>
                  {fmt2(a.payments)}
                </TableCell>
                <TableCell align="right"
                  sx={{ fontWeight: 800, color: a.net >= 0 ? 'success.dark' : 'error.main' }}>
                  {a.net >= 0 ? '+' : ''}{fmt2(a.net)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt2(a.closing)}</TableCell>
                <TableCell align="right" sx={{ color: 'text.secondary', fontSize: 11 }}>
                  {a.dep_count} · {a.pay_count}
                </TableCell>
              </TableRow>
            ))}
            {(data.accounts || []).length === 0 && (
              <TableRow><TableCell colSpan={7} align="center"
                sx={{ py: 3, color: 'text.disabled', fontStyle: 'italic' }}>
                No bank movement in this period.
              </TableCell></TableRow>
            )}
            {(data.accounts || []).length > 1 && (
              <TableRow sx={{ '& td': { fontWeight: 900, py: 1,
                borderTop: '2px solid', borderColor: 'text.primary',
                bgcolor: '#fafbfc', fontSize: 13, fontVariantNumeric: 'tabular-nums' } }}>
                <TableCell sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>Total</TableCell>
                <TableCell align="right">
                  {fmt2(data.accounts.reduce((s, a) => s + Number(a.opening || 0), 0))}
                </TableCell>
                <TableCell align="right" sx={{ color: 'success.dark' }}>
                  {fmt2(data.accounts.reduce((s, a) => s + Number(a.deposits || 0), 0))}
                </TableCell>
                <TableCell align="right" sx={{ color: 'error.main' }}>
                  {fmt2(data.accounts.reduce((s, a) => s + Number(a.payments || 0), 0))}
                </TableCell>
                <TableCell align="right">
                  {fmt2(data.accounts.reduce((s, a) => s + Number(a.net || 0), 0))}
                </TableCell>
                <TableCell align="right">
                  {fmt2(data.accounts.reduce((s, a) => s + Number(a.closing || 0), 0))}
                </TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Monthly trend table */}
      {months.length > 0 && (
        <>
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
            Monthly Trend
          </Typography>
          <TableContainer sx={{ mb: 2.5 }}>
            <Table size="small" sx={{
              '& .MuiTableCell-head': { bgcolor: '#fafbfc', fontWeight: 800, fontSize: 11,
                textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' },
            }}>
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Deposits</TableCell>
                  <TableCell align="right">Payments</TableCell>
                  <TableCell align="right">Net</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.trend || []).map((t, i) => (
                  <TableRow key={i} hover sx={{ '& td': { fontSize: 12, py: 0.7,
                    fontVariantNumeric: 'tabular-nums' } }}>
                    <TableCell sx={{ fontWeight: 700 }}>{t.account}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{t.mon_yer}</TableCell>
                    <TableCell align="right" sx={{ color: 'success.dark', fontWeight: 700 }}>
                      {fmt2(t.deposits)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'error.main', fontWeight: 700 }}>
                      {fmt2(t.payments)}
                    </TableCell>
                    <TableCell align="right"
                      sx={{ fontWeight: 800, color: (t.deposits - t.payments) >= 0 ? 'success.dark' : 'error.main' }}>
                      {fmt2(t.deposits - t.payments)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Top payees */}
      {(data.top_payees || []).length > 0 && (
        <>
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
            Top Payees
          </Typography>
          <TableContainer sx={{ mb: 2.5 }}>
            <Table size="small" sx={{
              '& .MuiTableCell-head': { bgcolor: '#fafbfc', fontWeight: 800, fontSize: 11,
                textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' },
            }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 50 }}>#</TableCell>
                  <TableCell>Payee</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell align="right" sx={{ width: 80 }}>Vouchers</TableCell>
                  <TableCell align="right" sx={{ width: 140 }}>Total Paid</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.top_payees.map((p, i) => (
                  <TableRow key={i} hover sx={{ '& td': { fontSize: 12, py: 0.7,
                    fontVariantNumeric: 'tabular-nums' } }}>
                    <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{p.payee}</TableCell>
                    <TableCell>{p.account}</TableCell>
                    <TableCell align="right">{p.n}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: 'error.main' }}>
                      GHS {fmt2(p.total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Cheque stats */}
      {(data.cheque_stats || []).length > 0 && (
        <>
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
            Cheque Book Usage
          </Typography>
          <TableContainer>
            <Table size="small" sx={{
              '& .MuiTableCell-head': { bgcolor: '#fafbfc', fontWeight: 800, fontSize: 11,
                textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' },
            }}>
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>First Cheque</TableCell>
                  <TableCell>Last Cheque</TableCell>
                  <TableCell align="right">Cheques Issued</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.cheque_stats.map((c, i) => (
                  <TableRow key={i} hover sx={{ '& td': { fontSize: 12, py: 0.7,
                    fontFamily: 'monospace' } }}>
                    <TableCell sx={{ fontFamily: 'inherit', fontWeight: 700 }}>{c.account}</TableCell>
                    <TableCell>{c.first || '—'}</TableCell>
                    <TableCell>{c.last || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'inherit', fontWeight: 800 }}>
                      {c.count}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}

// ── Cashbook Statement (per-account chronological with running balance)
function CashbookStatementReport({ data, schoolName }) {
  const subtitle = `${data.from} → ${data.to}${data.account ? ` · ${data.account}` : ' · All accounts'}`;
  return (
    <Box>
      <PrintHeader schoolName={schoolName} title="Cashbook Statement" subtitle={subtitle} />

      {/* Account summary tiles */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(240px, 1fr))' },
        gap: 1.5, mb: 2 }}>
        {(data.accounts || []).map(a => (
          <Card key={a.account} sx={{ p: 1.4, borderLeft: '4px solid', borderColor:
            a.account === 'Cash-In-Hand' ? 'success.main' : 'primary.main' }}>
            <Typography variant="subtitle2" fontWeight={800}>{a.account}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.3, mt: 0.6, fontSize: 12 }}>
              <Typography variant="caption">Opening</Typography>
              <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                {fmt2(a.opening)}
              </Typography>
              <Typography variant="caption">Money In (+)</Typography>
              <Typography sx={{ fontVariantNumeric: 'tabular-nums', color: 'success.dark', fontWeight: 700 }}>
                {fmt2(a.in_total)}
              </Typography>
              <Typography variant="caption">Money Out (−)</Typography>
              <Typography sx={{ fontVariantNumeric: 'tabular-nums', color: 'error.main', fontWeight: 700 }}>
                {fmt2(a.out_total)}
              </Typography>
              <Typography variant="caption" fontWeight={800}
                sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 0.3 }}>
                Closing
              </Typography>
              <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 800, fontSize: 14,
                borderTop: '1px solid', borderColor: 'divider', pt: 0.3 }}>
                {fmt2(a.closing)}
              </Typography>
            </Box>
          </Card>
        ))}
      </Box>

      {/* Per-account chronological listing */}
      {(data.accounts || []).map(a => {
        const accRows = (data.rows || []).filter(r => r.account === a.account);
        return (
          <Box key={a.account} sx={{ mb: 3, breakInside: 'avoid' }}>
            <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.7,
              borderBottom: '2px solid', borderColor: 'primary.main', pb: 0.5 }}>
              {a.account}
            </Typography>
            <TableContainer>
              <Table size="small" sx={{
                '& .MuiTableCell-head': { bgcolor: '#fafbfc', fontWeight: 800, fontSize: 11,
                  textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' },
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 100 }}>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell sx={{ width: 100 }}>Voucher</TableCell>
                    <TableCell sx={{ width: 100 }}>Cheque</TableCell>
                    <TableCell align="right" sx={{ width: 110 }}>In</TableCell>
                    <TableCell align="right" sx={{ width: 110 }}>Out</TableCell>
                    <TableCell align="right" sx={{ width: 130 }}>Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow sx={{ bgcolor: alpha('#1565C0', 0.05),
                    '& td': { fontWeight: 800, py: 0.7 } }}>
                    <TableCell />
                    <TableCell sx={{ fontStyle: 'italic' }}>Opening Balance</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {fmt2(a.opening)}
                    </TableCell>
                  </TableRow>
                  {accRows.length === 0 && (
                    <TableRow><TableCell colSpan={7} align="center"
                      sx={{ py: 3, color: 'text.disabled', fontStyle: 'italic' }}>
                      No activity in this period.
                    </TableCell></TableRow>
                  )}
                  {accRows.map(r => (
                    <TableRow key={r.id} hover sx={{ '& td': { fontSize: 12, py: 0.65,
                      fontVariantNumeric: 'tabular-nums' } }}>
                      <TableCell sx={{ color: 'text.secondary' }}>{r.date}</TableCell>
                      <TableCell sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Tooltip title={r.description}><span>{r.description}</span></Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{r.voucher_no || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{r.cheque_no || '—'}</TableCell>
                      <TableCell align="right" sx={{ color: r.in_amt > 0 ? 'success.dark' : 'text.disabled',
                        fontWeight: r.in_amt > 0 ? 700 : 400 }}>
                        {r.in_amt > 0 ? fmt2(r.in_amt) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: r.out_amt > 0 ? 'error.main' : 'text.disabled',
                        fontWeight: r.out_amt > 0 ? 700 : 400 }}>
                        {r.out_amt > 0 ? fmt2(r.out_amt) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt2(r.balance)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: alpha('#1565C0', 0.06),
                    '& td': { fontWeight: 800, py: 0.85, borderTop: '2px solid', borderColor: 'primary.main' } }}>
                    <TableCell />
                    <TableCell sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Closing Balance
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'success.dark' }}>
                      {fmt2(a.in_total)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'error.main' }}>
                      {fmt2(a.out_total)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>
                      {fmt2(a.closing)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })}
    </Box>
  );
}

// ── Payments Analysis (POSTED payment vouchers, multi-axis breakdown)
function PaymentsAnalysisReport({ data, schoolName }) {
  const subtitle = `${data.from} → ${data.to}${data.account ? ` · ${data.account}` : ' · All accounts'}`;
  const breakdownTable = (title, rows, labelColor = 'text.primary') => (
    <Box>
      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.7 }}>{title}</Typography>
      <Table size="small">
        <TableHead><TableRow>
          <TableCell><b>{title.replace(/^By /, '')}</b></TableCell>
          <TableCell align="right" sx={{ width: 70 }}><b>#</b></TableCell>
          <TableCell align="right" sx={{ width: 130 }}><b>Total</b></TableCell>
          <TableCell align="right" sx={{ width: 70 }}><b>%</b></TableCell>
        </TableRow></TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={4} align="center"
              sx={{ py: 2, color: 'text.disabled', fontStyle: 'italic' }}>
              No data
            </TableCell></TableRow>
          )}
          {rows.slice(0, 15).map((r, i) => (
            <TableRow key={i}>
              <TableCell sx={{ color: labelColor }}>{r.label}</TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.n}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                GHS {fmt2(r.total)}
              </TableCell>
              <TableCell align="right" sx={{ color: 'text.secondary' }}>
                {data.grand ? ((r.total / data.grand) * 100).toFixed(1) + '%' : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
  return (
    <Box>
      <PrintHeader schoolName={schoolName} title="Payments Analysis" subtitle={subtitle} />

      <Stack direction="row" spacing={1.2} flexWrap="wrap" rowGap={1} sx={{ mb: 2 }}>
        <Chip color="error" label={`Total Paid · GHS ${fmt2(data.grand)}`}
          sx={{ fontWeight: 800, fontSize: 13, height: 30 }} />
        <Chip variant="outlined" label={`${data.voucher_count} vouchers`}
          sx={{ fontWeight: 700 }} />
        <Chip variant="outlined" label={`${data.line_count} line items`}
          sx={{ fontWeight: 700 }} />
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 1.5, mb: 2 }}>
        {breakdownTable('By Category', data.by_category || [], 'error.main')}
        {breakdownTable('By Type', data.by_type || [])}
        {breakdownTable('By Account', data.by_account || [], 'primary.main')}
        {breakdownTable('By Month', data.by_month || [])}
      </Box>

      {(data.by_payee || []).length > 0 && (
        <>
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.7 }}>
            Top Payees
          </Typography>
          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell sx={{ width: 50 }}><b>#</b></TableCell>
                <TableCell><b>Payee</b></TableCell>
                <TableCell align="right" sx={{ width: 80 }}><b>Vouchers</b></TableCell>
                <TableCell align="right" sx={{ width: 140 }}><b>Total</b></TableCell>
                <TableCell align="right" sx={{ width: 80 }}><b>%</b></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {data.by_payee.slice(0, 20).map((p, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{p.label}</TableCell>
                    <TableCell align="right">{p.n}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: 'error.main',
                      fontVariantNumeric: 'tabular-nums' }}>
                      GHS {fmt2(p.total)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>
                      {data.grand ? ((p.total / data.grand) * 100).toFixed(1) + '%' : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.7 }}>
        Voucher Details · {(data.rows || []).length} lines
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell><b>Date</b></TableCell>
            <TableCell><b>Voucher</b></TableCell>
            <TableCell><b>Account</b></TableCell>
            <TableCell><b>Cheque</b></TableCell>
            <TableCell><b>Category</b></TableCell>
            <TableCell><b>Type</b></TableCell>
            <TableCell><b>Payee</b></TableCell>
            <TableCell align="right"><b>Amount</b></TableCell>
          </TableRow></TableHead>
          <TableBody>
            {(data.rows || []).map((r, i) => (
              <TableRow key={i} sx={{ '& td': { fontSize: 11.5, py: 0.55,
                fontVariantNumeric: 'tabular-nums' } }}>
                <TableCell sx={{ color: 'text.secondary' }}>{r.date}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{r.voucher_no}</TableCell>
                <TableCell>{r.account}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{r.cheque_no || '—'}</TableCell>
                <TableCell>{r.category}</TableCell>
                <TableCell>{r.type}</TableCell>
                <TableCell>{r.payee}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main' }}>
                  {fmt2(r.amount)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ── Expenditure Ledger ───────────────────────────────────────────
// Flat per-voucher list of every posted expense across cashbook,
// petty cash and legacy cashbook entries. Shows category/source/payee
// totals up top and prints the line list at the bottom. Filtering
// (category / type / payee / source / bank) is applied server-side
// and the drill-down filter card is rendered separately as
// LedgerFilters.
function ExpenditureLedgerReport({ data, schoolName }) {
  const f = data?.filters || {};
  const bits = [
    `${data.from} → ${data.to}`,
    f.source   && `Source: ${f.source}`,
    f.account  && `Account: ${f.account}`,
    f.category && `Category: ${f.category}`,
    f.type     && `Type: ${f.type}`,
    f.payee    && `Payee: ${f.payee}`,
  ].filter(Boolean);
  const subtitle = bits.join(' · ');

  const breakdownTable = (title, rows, labelColor = 'text.primary') => (
    <Box>
      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.7 }}>{title}</Typography>
      <Table size="small">
        <TableHead><TableRow>
          <TableCell><b>{title.replace(/^By /, '')}</b></TableCell>
          <TableCell align="right" sx={{ width: 70 }}><b>#</b></TableCell>
          <TableCell align="right" sx={{ width: 130 }}><b>Total</b></TableCell>
          <TableCell align="right" sx={{ width: 70 }}><b>%</b></TableCell>
        </TableRow></TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={4} align="center"
              sx={{ py: 2, color: 'text.disabled', fontStyle: 'italic' }}>
              No data
            </TableCell></TableRow>
          )}
          {rows.slice(0, 12).map((r, i) => (
            <TableRow key={i}>
              <TableCell sx={{ color: labelColor }}>{r.label}</TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.n}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                GHS {fmt2(r.total)}
              </TableCell>
              <TableCell align="right" sx={{ color: 'text.secondary' }}>
                {data.grand ? ((r.total / data.grand) * 100).toFixed(1) + '%' : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );

  return (
    <Box>
      <PrintHeader schoolName={schoolName} title="Expenditure Ledger" subtitle={subtitle} />

      <Stack direction="row" spacing={1.2} flexWrap="wrap" rowGap={1} sx={{ mb: 2 }}>
        <Chip color="error" label={`Total Expensed · GHS ${fmt2(data.grand)}`}
          sx={{ fontWeight: 800, fontSize: 13, height: 30 }} />
        <Chip variant="outlined" label={`${data.count || 0} line${data.count === 1 ? '' : 's'}`}
          sx={{ fontWeight: 700 }} />
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 1.5, mb: 2 }}>
        {breakdownTable('By Source',   data.by_source   || [], 'primary.main')}
        {breakdownTable('By Category', data.by_category || [], 'error.main')}
        {breakdownTable('By Type',     data.by_type     || [])}
        {breakdownTable('By Account',  data.by_account  || [])}
      </Box>

      <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.7 }}>
        Ledger Entries · {(data.rows || []).length} line{(data.rows || []).length === 1 ? '' : 's'}
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell><b>Date</b></TableCell>
            <TableCell><b>Source</b></TableCell>
            <TableCell><b>Voucher</b></TableCell>
            <TableCell><b>Account</b></TableCell>
            <TableCell><b>Category</b></TableCell>
            <TableCell><b>Type</b></TableCell>
            <TableCell><b>Payee</b></TableCell>
            <TableCell><b>Narration</b></TableCell>
            <TableCell align="right"><b>Amount</b></TableCell>
          </TableRow></TableHead>
          <TableBody>
            {(data.rows || []).length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center"
                  sx={{ py: 4, color: 'text.disabled', fontStyle: 'italic' }}>
                  No expenditure recorded for the chosen filters.
                </TableCell>
              </TableRow>
            )}
            {(data.rows || []).map((r, i) => (
              <TableRow key={i} sx={{ '& td': { fontSize: 11.5, py: 0.55,
                fontVariantNumeric: 'tabular-nums' } }}>
                <TableCell sx={{ color: 'text.secondary' }}>
                  {r.date ? new Date(r.date).toISOString().slice(0, 10) : ''}
                </TableCell>
                <TableCell>{r.source}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{r.voucher_no || '—'}</TableCell>
                <TableCell>{r.account}</TableCell>
                <TableCell>{r.category}</TableCell>
                <TableCell>{r.type}</TableCell>
                <TableCell>{r.payee}</TableCell>
                <TableCell sx={{ maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden',
                  textOverflow: 'ellipsis' }}>
                  {r.narration || '—'}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main' }}>
                  {fmt2(r.amount)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          {(data.rows || []).length > 0 && (
            <TableHead>
              <TableRow sx={{ bgcolor: (t) => alpha(t.palette.error.main, 0.06) }}>
                <TableCell colSpan={8} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  <b>Total Expenditure</b>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 900, color: 'error.main',
                  fontVariantNumeric: 'tabular-nums' }}>
                  GHS {fmt2(data.grand)}
                </TableCell>
              </TableRow>
            </TableHead>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
}

// ── Income Ledger ────────────────────────────────────────────────
// Flat per-receipt list of every income receipt from the universal
// `day` table, with breakdowns by income type / cashier / mode / scope
// and a printable line list at the bottom. Filtering (income type /
// mode / cashier / class / scope) is server-side; the drill-down
// filter card is rendered separately as LedgerFilters.
function IncomeLedgerReport({ data, schoolName }) {
  const f = data?.filters || {};
  const bits = [
    `${data.from} → ${data.to}`,
    f.scope       && `Scope: ${f.scope}`,
    f.income_type && `Income: ${f.income_type}`,
    f.mode        && `Mode: ${f.mode}`,
    f.cashier     && `Cashier: ${f.cashier}`,
    f.class_name  && `Class: ${f.class_name}`,
  ].filter(Boolean);
  const subtitle = bits.join(' · ');

  const breakdownTable = (title, rows, labelColor = 'text.primary') => (
    <Box>
      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.7 }}>{title}</Typography>
      <Table size="small">
        <TableHead><TableRow>
          <TableCell><b>{title.replace(/^By /, '')}</b></TableCell>
          <TableCell align="right" sx={{ width: 70 }}><b>#</b></TableCell>
          <TableCell align="right" sx={{ width: 130 }}><b>Total</b></TableCell>
          <TableCell align="right" sx={{ width: 70 }}><b>%</b></TableCell>
        </TableRow></TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={4} align="center"
              sx={{ py: 2, color: 'text.disabled', fontStyle: 'italic' }}>
              No data
            </TableCell></TableRow>
          )}
          {rows.slice(0, 12).map((r, i) => (
            <TableRow key={i}>
              <TableCell sx={{ color: labelColor }}>{r.label}</TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.n}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                GHS {fmt2(r.total)}
              </TableCell>
              <TableCell align="right" sx={{ color: 'text.secondary' }}>
                {data.grand ? ((r.total / data.grand) * 100).toFixed(1) + '%' : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );

  return (
    <Box>
      <PrintHeader schoolName={schoolName} title="Income Ledger" subtitle={subtitle} />

      <Stack direction="row" spacing={1.2} flexWrap="wrap" rowGap={1} sx={{ mb: 2 }}>
        <Chip color="success" label={`Total Income · GHS ${fmt2(data.grand)}`}
          sx={{ fontWeight: 800, fontSize: 13, height: 30 }} />
        <Chip variant="outlined" label={`${data.count || 0} receipt${data.count === 1 ? '' : 's'}`}
          sx={{ fontWeight: 700 }} />
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 1.5, mb: 2 }}>
        {breakdownTable('By Income Type', data.by_income_type || [], 'success.dark')}
        {breakdownTable('By Scope',       data.by_scope       || [], 'primary.main')}
        {breakdownTable('By Mode',        data.by_mode        || [])}
        {breakdownTable('By Cashier',     data.by_cashier     || [])}
      </Box>

      <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.7 }}>
        Ledger Entries · {(data.rows || []).length} receipt{(data.rows || []).length === 1 ? '' : 's'}
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell><b>Date</b></TableCell>
            <TableCell><b>Receipt</b></TableCell>
            <TableCell><b>Scope</b></TableCell>
            <TableCell><b>Income Type</b></TableCell>
            <TableCell><b>Payer</b></TableCell>
            <TableCell><b>Student / Class</b></TableCell>
            <TableCell><b>Mode</b></TableCell>
            <TableCell><b>Cashier</b></TableCell>
            <TableCell align="right"><b>Amount</b></TableCell>
          </TableRow></TableHead>
          <TableBody>
            {(data.rows || []).length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center"
                  sx={{ py: 4, color: 'text.disabled', fontStyle: 'italic' }}>
                  No income recorded for the chosen filters.
                </TableCell>
              </TableRow>
            )}
            {(data.rows || []).map((r, i) => (
              <TableRow key={i} sx={{ '& td': { fontSize: 11.5, py: 0.55,
                fontVariantNumeric: 'tabular-nums' } }}>
                <TableCell sx={{ color: 'text.secondary' }}>
                  {r.day ? new Date(r.day).toISOString().slice(0, 10) : ''}
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{r.receipt_no || '—'}</TableCell>
                <TableCell>{r.scope_label}</TableCell>
                <TableCell>{r.income_type || '—'}</TableCell>
                <TableCell>{r.payer_name || '—'}</TableCell>
                <TableCell>
                  {[r.student_id, r.class_name].filter(v => v && v !== '-' && v !== '').join(' · ') || '—'}
                </TableCell>
                <TableCell>{r.mode || '—'}</TableCell>
                <TableCell>{r.received_by_name || '—'}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'success.dark' }}>
                  {fmt2(r.amount)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          {(data.rows || []).length > 0 && (
            <TableHead>
              <TableRow sx={{ bgcolor: (t) => alpha(t.palette.success.main, 0.06) }}>
                <TableCell colSpan={8} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  <b>Total Income</b>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 900, color: 'success.dark',
                  fontVariantNumeric: 'tabular-nums' }}>
                  GHS {fmt2(data.grand)}
                </TableCell>
              </TableRow>
            </TableHead>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
}

// ── Income vs Expenditure (period P&L)
function IncomeVsExpenseReport({ data, schoolName }) {
  const subtitle = `${data.from} → ${data.to}`;
  const t = data.totals || { income: 0, expense: 0, net: 0 };
  const surplus = t.net >= 0;
  return (
    <Box>
      <PrintHeader schoolName={schoolName} title="Income vs Expenditure" subtitle={subtitle} />

      {/* Hero result */}
      <Box sx={{
        p: 2, mb: 2, borderRadius: 2,
        bgcolor: surplus ? alpha('#2e7d32', 0.08) : alpha('#d32f2f', 0.08),
        border: '2px solid', borderColor: surplus ? 'success.main' : 'error.main',
      }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-around">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="overline" color="success.dark" fontWeight={800}>Total Income</Typography>
            <Typography variant="h4" fontWeight={800} sx={{ color: 'success.dark', fontVariantNumeric: 'tabular-nums' }}>
              GHS {fmt2(t.income)}
            </Typography>
          </Box>
          <Typography variant="h3" sx={{ color: 'text.disabled' }}>−</Typography>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="overline" color="error.main" fontWeight={800}>Total Expenditure</Typography>
            <Typography variant="h4" fontWeight={800} sx={{ color: 'error.main', fontVariantNumeric: 'tabular-nums' }}>
              GHS {fmt2(t.expense)}
            </Typography>
          </Box>
          <Typography variant="h3" sx={{ color: 'text.disabled' }}>=</Typography>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="overline" fontWeight={800}
              color={surplus ? 'success.dark' : 'error.main'}>
              Net {surplus ? 'Surplus' : 'Deficit'}
            </Typography>
            <Typography variant="h3" fontWeight={900} sx={{
              color: surplus ? 'success.dark' : 'error.main',
              fontVariantNumeric: 'tabular-nums',
            }}>
              GHS {fmt2(Math.abs(t.net))}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 2, mb: 2 }}>
        {/* Income side */}
        <Box>
          <Typography variant="subtitle1" fontWeight={800} sx={{ color: 'success.dark', mb: 0.7,
            borderBottom: '2px solid', borderColor: 'success.main', pb: 0.5 }}>
            Income Breakdown
          </Typography>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell><b>Category</b></TableCell>
              <TableCell align="right" sx={{ width: 70 }}><b>#</b></TableCell>
              <TableCell align="right" sx={{ width: 130 }}><b>Total</b></TableCell>
              <TableCell align="right" sx={{ width: 70 }}><b>%</b></TableCell>
            </TableRow></TableHead>
            <TableBody>
              {(data.income || []).map((r, i) => (
                <TableRow key={i}>
                  <TableCell>{r.label}</TableCell>
                  <TableCell align="right">{r.n}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: 'success.dark',
                    fontVariantNumeric: 'tabular-nums' }}>
                    {fmt2(r.total)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    {t.income ? ((r.total / t.income) * 100).toFixed(1) + '%' : '—'}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ '& td': { fontWeight: 800, borderTop: '2px solid',
                borderColor: 'success.main', bgcolor: alpha('#2e7d32', 0.05) } }}>
                <TableCell sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Income</TableCell>
                <TableCell align="right">{(data.income || []).reduce((s, r) => s + Number(r.n || 0), 0)}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'success.dark' }}>
                  GHS {fmt2(t.income)}
                </TableCell>
                <TableCell align="right">100%</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Expense side */}
        <Box>
          <Typography variant="subtitle1" fontWeight={800} sx={{ color: 'error.main', mb: 0.7,
            borderBottom: '2px solid', borderColor: 'error.main', pb: 0.5 }}>
            Expenditure Breakdown
          </Typography>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell><b>Category</b></TableCell>
              <TableCell align="right" sx={{ width: 70 }}><b>#</b></TableCell>
              <TableCell align="right" sx={{ width: 130 }}><b>Total</b></TableCell>
              <TableCell align="right" sx={{ width: 70 }}><b>%</b></TableCell>
            </TableRow></TableHead>
            <TableBody>
              {(data.expenses || []).map((r, i) => (
                <TableRow key={i}>
                  <TableCell>{r.label}</TableCell>
                  <TableCell align="right">{r.n}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main',
                    fontVariantNumeric: 'tabular-nums' }}>
                    {fmt2(r.total)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    {t.expense ? ((r.total / t.expense) * 100).toFixed(1) + '%' : '—'}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ '& td': { fontWeight: 800, borderTop: '2px solid',
                borderColor: 'error.main', bgcolor: alpha('#d32f2f', 0.05) } }}>
                <TableCell sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Expenditure</TableCell>
                <TableCell align="right">{(data.expenses || []).reduce((s, r) => s + Number(r.n || 0), 0)}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'error.main' }}>
                  GHS {fmt2(t.expense)}
                </TableCell>
                <TableCell align="right">100%</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Box>

      {/* Monthly trend */}
      {(data.monthly || []).length > 0 && (
        <>
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.7,
            borderBottom: '2px solid', borderColor: 'primary.main', pb: 0.5 }}>
            Monthly Trend
          </Typography>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell><b>Month</b></TableCell>
              <TableCell align="right"><b>Income</b></TableCell>
              <TableCell align="right"><b>Expenditure</b></TableCell>
              <TableCell align="right"><b>Net</b></TableCell>
            </TableRow></TableHead>
            <TableBody>
              {data.monthly.map((m, i) => (
                <TableRow key={i} sx={{ '& td': { fontVariantNumeric: 'tabular-nums', py: 0.6 } }}>
                  <TableCell sx={{ fontWeight: 700 }}>{m.mon_yer}</TableCell>
                  <TableCell align="right" sx={{ color: 'success.dark', fontWeight: 700 }}>
                    {fmt2(m.income)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'error.main', fontWeight: 700 }}>
                    {fmt2(m.expense)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800,
                    color: m.net >= 0 ? 'success.dark' : 'error.main' }}>
                    {m.net >= 0 ? '+' : ''}{fmt2(m.net)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ '& td': { fontWeight: 900, borderTop: '2px solid',
                borderColor: 'text.primary', bgcolor: '#fafbfc',
                fontVariantNumeric: 'tabular-nums', fontSize: 13 } }}>
                <TableCell sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>Period Total</TableCell>
                <TableCell align="right" sx={{ color: 'success.dark' }}>{fmt2(t.income)}</TableCell>
                <TableCell align="right" sx={{ color: 'error.main' }}>{fmt2(t.expense)}</TableCell>
                <TableCell align="right" sx={{ color: surplus ? 'success.dark' : 'error.main' }}>
                  {surplus ? '+' : ''}{fmt2(t.net)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </>
      )}
    </Box>
  );
}

// ── Petty Cashbook Statement (chronological with running balance)
function PettyStatementReport({ data, schoolName }) {
  const subtitle = `${data.from} → ${data.to}`;
  return (
    <Box>
      <PrintHeader schoolName={schoolName} title="Petty Cashbook Statement" subtitle={subtitle} />

      <Stack direction="row" spacing={1.2} flexWrap="wrap" rowGap={1} sx={{ mb: 2 }}>
        <Chip variant="outlined" label={`Opening · GHS ${fmt2(data.opening)}`}
          sx={{ fontWeight: 700, fontSize: 12 }} />
        <Chip color="success" label={`Reimbursed · GHS ${fmt2(data.in_total)}`}
          sx={{ fontWeight: 800, fontSize: 12 }} />
        <Chip color="error" label={`Expensed · GHS ${fmt2(data.out_total)}`}
          sx={{ fontWeight: 800, fontSize: 12 }} />
        <Chip color="primary" label={`Closing · GHS ${fmt2(data.closing)}`}
          sx={{ fontWeight: 800, fontSize: 13 }} />
      </Stack>

      <TableContainer>
        <Table size="small" sx={{
          '& .MuiTableCell-head': { bgcolor: '#fafbfc', fontWeight: 800, fontSize: 11,
            textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' },
        }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 100 }}>Date</TableCell>
              <TableCell>Description</TableCell>
              <TableCell sx={{ width: 130 }}>Voucher / Ref</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Requester</TableCell>
              <TableCell align="right" sx={{ width: 110 }}>In</TableCell>
              <TableCell align="right" sx={{ width: 110 }}>Out</TableCell>
              <TableCell align="right" sx={{ width: 130 }}>Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={{ bgcolor: alpha('#1565C0', 0.05),
              '& td': { fontWeight: 800, py: 0.7 } }}>
              <TableCell />
              <TableCell sx={{ fontStyle: 'italic' }}>Opening Balance</TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmt2(data.opening)}
              </TableCell>
            </TableRow>
            {(data.rows || []).length === 0 && (
              <TableRow><TableCell colSpan={8} align="center"
                sx={{ py: 3, color: 'text.disabled', fontStyle: 'italic' }}>
                No petty cash activity in this period.
              </TableCell></TableRow>
            )}
            {(data.rows || []).map(r => (
              <TableRow key={r.id} hover sx={{ '& td': { fontSize: 12, py: 0.65,
                fontVariantNumeric: 'tabular-nums' } }}>
                <TableCell sx={{ color: 'text.secondary' }}>{r.date}</TableCell>
                <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.is_bf && (
                    <Chip size="small" color="primary" label="B/F"
                      sx={{ height: 16, fontSize: 9, fontWeight: 800, mr: 0.5,
                        '& .MuiChip-label': { px: 0.6 } }} />
                  )}
                  <Tooltip title={r.description}><span>{r.description}</span></Tooltip>
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{r.voucher_no || '—'}</TableCell>
                <TableCell>{r.category || '—'}</TableCell>
                <TableCell>{r.request_holder || '—'}</TableCell>
                <TableCell align="right" sx={{ color: r.in_amt > 0 ? 'success.dark' : 'text.disabled',
                  fontWeight: r.in_amt > 0 ? 700 : 400 }}>
                  {r.in_amt > 0 ? fmt2(r.in_amt) : '—'}
                </TableCell>
                <TableCell align="right" sx={{ color: r.out_amt > 0 ? 'error.main' : 'text.disabled',
                  fontWeight: r.out_amt > 0 ? 700 : 400 }}>
                  {r.out_amt > 0 ? fmt2(r.out_amt) : '—'}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt2(r.balance)}</TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: alpha('#1565C0', 0.06),
              '& td': { fontWeight: 800, py: 0.85, borderTop: '2px solid', borderColor: 'primary.main' } }}>
              <TableCell />
              <TableCell sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Closing Balance
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'success.dark' }}>
                {fmt2(data.in_total)}
              </TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'error.main' }}>
                {fmt2(data.out_total)}
              </TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>
                {fmt2(data.closing)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ── Petty Cash Analysis (multi-axis breakdown of petty spending)
function PettyAnalysisReport({ data, schoolName }) {
  const subtitle = `${data.from} → ${data.to}`;
  const reim = data.reimbursement_summary || {};
  const breakdownTable = (title, rows) => (
    <Box>
      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.7 }}>{title}</Typography>
      <Table size="small">
        <TableHead><TableRow>
          <TableCell><b>{title.replace(/^By /, '')}</b></TableCell>
          <TableCell align="right" sx={{ width: 70 }}><b>#</b></TableCell>
          <TableCell align="right" sx={{ width: 130 }}><b>Total</b></TableCell>
          <TableCell align="right" sx={{ width: 70 }}><b>%</b></TableCell>
        </TableRow></TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={4} align="center"
              sx={{ py: 2, color: 'text.disabled', fontStyle: 'italic' }}>
              No data
            </TableCell></TableRow>
          )}
          {rows.slice(0, 15).map((r, i) => (
            <TableRow key={i}>
              <TableCell>{r.label}</TableCell>
              <TableCell align="right">{r.n}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main',
                fontVariantNumeric: 'tabular-nums' }}>
                GHS {fmt2(r.total)}
              </TableCell>
              <TableCell align="right" sx={{ color: 'text.secondary' }}>
                {data.grand ? ((r.total / data.grand) * 100).toFixed(1) + '%' : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
  return (
    <Box>
      <PrintHeader schoolName={schoolName} title="Petty Cash Analysis" subtitle={subtitle} />

      <Stack direction="row" spacing={1.2} flexWrap="wrap" rowGap={1} sx={{ mb: 2 }}>
        <Chip color="error" label={`Total Expensed · GHS ${fmt2(data.grand)}`}
          sx={{ fontWeight: 800, fontSize: 13, height: 30 }} />
        <Chip variant="outlined" label={`${data.voucher_count} vouchers`}
          sx={{ fontWeight: 700 }} />
        <Chip variant="outlined" label={`${data.line_count} line items`}
          sx={{ fontWeight: 700 }} />
      </Stack>

      {/* Reimbursement requests summary */}
      <Card sx={{ p: 1.5, mb: 2, borderLeft: '4px solid', borderColor: 'primary.main' }}>
        <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.7 }}>
          Reimbursement Requests (period)
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
          <Chip color="primary" label={`Total · GHS ${fmt2(reim.total)}`}
            sx={{ fontWeight: 800 }} />
          <Chip variant="outlined" label={`${reim.n || 0} requests`} sx={{ fontWeight: 700 }} />
          <Chip color="success" label={`${reim.approved || 0} approved`} sx={{ fontWeight: 700 }} />
          <Chip color="warning" label={`${reim.pending  || 0} pending`}  sx={{ fontWeight: 700 }} />
          <Chip color="error"   label={`${reim.rejected || 0} rejected`} sx={{ fontWeight: 700 }} />
        </Stack>
      </Card>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 1.5, mb: 2 }}>
        {breakdownTable('By Category',  data.by_category  || [])}
        {breakdownTable('By Type',      data.by_type      || [])}
        {breakdownTable('By Requester', data.by_requester || [])}
        {breakdownTable('By Month',     data.by_month     || [])}
      </Box>
    </Box>
  );
}

// ── Receipts & Payments Account (3-column cashbook: Disc · Cash · Bank)
function ReceiptsAndPaymentsReport({ data, schoolName }) {
  const subtitle = `${data.from} → ${data.to}`;
  const t = data.totals || {};

  // Reusable row inside a column. `pad` indents sub-lines under a
  // category heading. The three amount slots are Discount / Cash / Bank.
  const SideRow = ({ label, disc, cash, bank, pad = 0, bold, italic, divider, topRule, color }) => (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: '1fr 60px 90px 90px',
      alignItems: 'baseline',
      gap: 0.6,
      py: 0.35, px: 0.4,
      borderTop: topRule ? '2px solid #000' : 'none',
      borderBottom: divider ? '1px solid' : 'none',
      borderBottomColor: 'rgba(0,0,0,0.08)',
    }}>
      <Typography sx={{
        pl: pad ? pad * 1.4 : 0,
        fontSize: bold ? 12.5 : 11.5,
        fontWeight: bold ? 800 : (italic ? 600 : 500),
        fontStyle: italic ? 'italic' : 'normal',
        color: color || (italic ? 'text.secondary' : 'text.primary'),
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {label}
      </Typography>
      {[disc, cash, bank].map((v, i) => (
        <Typography key={i} sx={{
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          fontSize: bold ? 12.5 : 11.5,
          fontWeight: bold ? 800 : (v ? 600 : 400),
          color: bold ? 'text.primary' : (v ? 'text.primary' : 'text.disabled'),
        }}>
          {v ? fmt2(v) : '—'}
        </Typography>
      ))}
    </Box>
  );

  const SideHeader = ({ label }) => (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: '1fr 60px 90px 90px',
      gap: 0.6,
      px: 0.4, py: 0.6, mt: 1,
      bgcolor: alpha('#1565C0', 0.06),
      borderTop: '1px solid', borderColor: 'primary.light',
      borderBottom: '1px solid', borderBottomColor: 'primary.light',
    }}>
      <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.6,
        textTransform: 'uppercase', color: 'primary.main' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5,
        color: 'text.secondary', textAlign: 'right' }}>DISC</Typography>
      <Typography sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5,
        color: 'text.secondary', textAlign: 'right' }}>CASH</Typography>
      <Typography sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5,
        color: 'text.secondary', textAlign: 'right' }}>BANK</Typography>
    </Box>
  );

  return (
    <Box>
      <PrintHeader schoolName={schoolName}
        title="Receipts and Payments Account"
        subtitle={`Three-Column Cashbook · ${subtitle}`} />

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: { xs: 2, md: 2 },
        border: '1px solid', borderColor: 'divider',
        borderRadius: 1, p: { xs: 1, md: 1.5 },
      }}>
        {/* ── RECEIPTS SIDE ───────────────────────────────────────── */}
        <Box sx={{ pr: { md: 1 }, borderRight: { md: '2px solid' }, borderRightColor: { md: 'divider' } }}>
          <Typography variant="h6" fontWeight={800} sx={{ textAlign: 'center',
            color: 'success.dark', borderBottom: '2px solid', borderColor: 'success.main',
            pb: 0.6, mb: 0.6, textTransform: 'uppercase', letterSpacing: 1, fontSize: 14 }}>
            Receipts (Dr.)
          </Typography>

          <SideHeader label="Particulars" />

          {/* Opening balances */}
          <SideRow label="To Balance b/d" italic />
          {(data.opening?.accounts || []).map((a, i) => (
            <SideRow key={i} label={a.account} pad={1}
              cash={a.is_cash ? a.balance : 0}
              bank={!a.is_cash ? a.balance : 0} />
          ))}
          <SideRow label="Subtotal — Opening" pad={1} italic divider
            cash={data.opening?.cash}
            bank={data.opening?.bank} />

          {/* Receipts (income) */}
          {(data.receipts || []).length > 0 && (
            <>
              <SideRow label="Income Receipts" italic />
              {data.receipts.map((r, i) => (
                <SideRow key={i} label={`To ${r.type}${r.n > 1 ? ` · ${r.n} txns` : ''}`} pad={1}
                  disc={r.discount_total}
                  cash={r.cash_total}
                  bank={r.bank_total} />
              ))}
            </>
          )}

          {/* Internal cash → bank deposit shows as a Bank-side receipt */}
          {data.cash_to_bank_transfer > 0 && (
            <SideRow label="To Cash Deposited (from cashbox)" pad={1}
              bank={data.cash_to_bank_transfer} />
          )}

          {/* Reconciling residual when breakdown < ledger movement */}
          {(data.other_receipts_cash > 0.01 || data.other_receipts_bank > 0.01) && (
            <SideRow label="To Other receipts (reconciling)" pad={1} italic
              cash={Math.max(0, data.other_receipts_cash)}
              bank={Math.max(0, data.other_receipts_bank)} />
          )}

          <SideRow label="TOTAL"
            bold topRule
            cash={t.receipts_cash}
            bank={t.receipts_bank} />
        </Box>

        {/* ── PAYMENTS SIDE ───────────────────────────────────────── */}
        <Box sx={{ pl: { md: 1 } }}>
          <Typography variant="h6" fontWeight={800} sx={{ textAlign: 'center',
            color: 'error.main', borderBottom: '2px solid', borderColor: 'error.main',
            pb: 0.6, mb: 0.6, textTransform: 'uppercase', letterSpacing: 1, fontSize: 14 }}>
            Payments (Cr.)
          </Typography>

          <SideHeader label="Particulars" />

          {/* Payments by category → types */}
          {(data.payments_by_category || []).length === 0 && (
            <SideRow label="No payments in this period" italic color="text.disabled" />
          )}
          {(data.payments_by_category || []).map((c, i) => (
            <Box key={i}>
              <SideRow label={`By ${c.category}`} italic />
              {(c.types || []).map((tt, j) => (
                <SideRow key={j} label={tt.type} pad={1}
                  disc={tt.discount_total}
                  cash={tt.cash_total}
                  bank={tt.bank_total} />
              ))}
              <SideRow label={`Subtotal — ${c.category}`} pad={1} italic divider
                cash={c.cash_total}
                bank={c.bank_total} />
            </Box>
          ))}

          {/* Cash deposited to bank shown as a Cash-side payment */}
          {data.cash_to_bank_transfer > 0 && (
            <SideRow label="By Cash Deposited (to bank)" pad={1}
              cash={data.cash_to_bank_transfer} />
          )}

          {/* Closing balances */}
          <SideRow label="By Balance c/d" italic />
          {(data.closing?.accounts || []).map((a, i) => (
            <SideRow key={i} label={a.account} pad={1}
              cash={a.is_cash ? a.balance : 0}
              bank={!a.is_cash ? a.balance : 0} />
          ))}
          <SideRow label="Subtotal — Closing" pad={1} italic divider
            cash={data.closing?.cash}
            bank={data.closing?.bank} />

          <SideRow label="TOTAL"
            bold topRule
            cash={t.payments_cash}
            bank={t.payments_bank} />
        </Box>
      </Box>

      {/* Balance proof strip */}
      <Box sx={{
        mt: 2, p: 1.4, borderRadius: 1,
        bgcolor: alpha('#2e7d32', 0.08),
        border: '1px solid', borderColor: 'success.light',
      }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={1}>
          <Typography sx={{ fontWeight: 800, fontSize: 12, color: 'success.dark',
            textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Both columns balance · Cash and Bank totals match
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
            Cash: <strong>GHS {fmt2(t.receipts_cash)}</strong> = <strong>GHS {fmt2(t.payments_cash)}</strong>
            {'  ·  '}
            Bank: <strong>GHS {fmt2(t.receipts_bank)}</strong> = <strong>GHS {fmt2(t.payments_bank)}</strong>
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

// ── Cashbook (Two-Column) ───────────────────────────────────────
// Faithful re-render of the legacy Jasper cashbook report. Landscape,
// single-month: Receipts (Date · Description · Cash · Bank) on the
// left, Payment (Date · Description · Cheque No · Type · Amount) on
// the right; Cash and Bank summary boxes side-by-side at the bottom.
function ClassicCashbookReport({ data, schoolName }) {
  // Inline column-header style — opaque pale blue, thin black border,
  // same as the old JRXML's table_CH style.
  const ch = {
    bgcolor: '#BFE1FF',
    border: '1px solid #000',
    fontFamily: 'Serif',
    fontSize: 11,
    fontWeight: 800,
    px: 0.6, py: 0.3,
    textAlign: 'center',
  };
  const td = {
    border: '1px solid #000',
    fontFamily: 'Serif',
    fontSize: 11,
    px: 0.6, py: 0.25,
  };
  const tdR = { ...td, textAlign: 'right' };
  const tdC = { ...td, textAlign: 'center' };

  const summaryRow = (label, value, opts = {}) => (
    <Stack direction="row" spacing={1} sx={{
      px: 1, py: 0.4,
      borderBottom: opts.divider ? '1px solid #000' : '1px solid rgba(0,0,0,0.15)',
      ...opts.sx,
    }}>
      <Typography sx={{
        flex: 1, fontFamily: 'Serif', fontSize: 12,
        fontWeight: opts.bold ? 800 : 700,
      }}>
        {label}
      </Typography>
      <Typography sx={{
        bgcolor: '#E6E6E6', color: '#006699',
        fontFamily: 'Serif', fontSize: 12, fontWeight: 800,
        textAlign: 'right',
        minWidth: 110, px: 1, py: 0.1,
        fontVariantNumeric: 'tabular-nums',
      }}>
        GH¢ {fmt2(value)}
      </Typography>
    </Stack>
  );

  return (
    <Box>
      <PrintHeader schoolName={schoolName} title="" subtitle="" />

      {/* Title bar — centred, blue text on grey background */}
      <Box sx={{
        bgcolor: '#E6E6E6',
        textAlign: 'center',
        py: 0.8, mb: 0,
        border: '1px solid #000',
      }}>
        <Typography sx={{
          fontFamily: 'Serif', fontSize: 14, fontWeight: 800,
          color: '#0066CC',
        }}>
          {data.title}
        </Typography>
      </Box>

      {/* Section labels: RECEIPTS | PAYMENT (italic, underlined) */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        borderBottom: '1px solid #000',
      }}>
        <Box sx={{ px: 1.5, py: 0.6, borderRight: '1px solid #000' }}>
          <Typography sx={{
            fontFamily: 'Serif', fontSize: 12, fontWeight: 800,
            fontStyle: 'italic', textDecoration: 'underline',
          }}>
            RECEIPTS
          </Typography>
        </Box>
        <Box sx={{ px: 1.5, py: 0.6 }}>
          <Typography sx={{
            fontFamily: 'Serif', fontSize: 12, fontWeight: 800,
            fontStyle: 'italic', textDecoration: 'underline',
          }}>
            PAYMENT
          </Typography>
        </Box>
      </Box>

      {/* Two side-by-side tables */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) minmax(0,1.2fr)' },
      }}>
        {/* RECEIPTS table */}
        <Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '14% 50% 18% 18%' }}>
            <Box sx={ch}>Date</Box>
            <Box sx={ch}>Description</Box>
            <Box sx={ch}>Cash</Box>
            <Box sx={ch}>Bank</Box>
          </Box>
          {data.receipts?.length === 0 && (
            <Box sx={{ ...td, gridColumn: '1 / -1', textAlign: 'center',
              fontStyle: 'italic', color: 'text.disabled', py: 1 }}>
              No receipts in this period.
            </Box>
          )}
          <Box>
            {(data.receipts || []).map((r, i) => (
              <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '14% 50% 18% 18%' }}>
                <Box sx={td}>{r.date}</Box>
                <Box sx={td}>{r.description}</Box>
                <Box sx={tdR}>
                  {r.cash > 0 ? fmt2(r.cash) : '—'}
                </Box>
                <Box sx={tdR}>
                  {r.bank > 0 ? fmt2(r.bank) : '—'}
                </Box>
              </Box>
            ))}
          </Box>
          {/* Footer row — totals */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '14% 50% 18% 18%' }}>
            <Box sx={td}>&nbsp;</Box>
            <Box sx={{ ...td, fontWeight: 800 }}>Total</Box>
            <Box sx={{ ...tdR, fontWeight: 800, textDecoration: 'underline' }}>
              {fmt2(data.totals?.cash_total)}
            </Box>
            <Box sx={{ ...tdR, fontWeight: 800, textDecoration: 'underline' }}>
              {fmt2(data.totals?.bank_total)}
            </Box>
          </Box>
        </Box>

        {/* PAYMENT table */}
        <Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '15% 30% 16% 24% 15%' }}>
            <Box sx={ch}>Date</Box>
            <Box sx={ch}>Description</Box>
            <Box sx={ch}>Cheque_No.</Box>
            <Box sx={ch}>Expenditure Type</Box>
            <Box sx={ch}>Amount</Box>
          </Box>
          {data.payments?.length === 0 && (
            <Box sx={{ ...td, gridColumn: '1 / -1', textAlign: 'center',
              fontStyle: 'italic', color: 'text.disabled', py: 1 }}>
              No payments in this period.
            </Box>
          )}
          <Box>
            {(data.payments || []).map((p, i) => (
              <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '15% 30% 16% 24% 15%' }}>
                <Box sx={tdC}>{p.date}</Box>
                <Box sx={td}>{p.description}</Box>
                <Box sx={tdC}>{p.cheque_no}</Box>
                <Box sx={td}>{p.exp_type}</Box>
                <Box sx={tdR}>
                  {p.amount > 0 ? fmt2(p.amount) : ''}
                </Box>
              </Box>
            ))}
          </Box>
          {/* Footer — total */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '15% 30% 16% 24% 15%' }}>
            <Box sx={td}>&nbsp;</Box>
            <Box sx={{ ...td, fontWeight: 800 }}>Total</Box>
            <Box sx={td}>&nbsp;</Box>
            <Box sx={td}>&nbsp;</Box>
            <Box sx={{ ...tdR, fontWeight: 800, textDecoration: 'underline' }}>
              {fmt2(data.totals?.payment_total)}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Summary boxes — Cash (left) and Bank (right) */}
      <Box sx={{
        mt: 3,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 4,
      }}>
        {/* CASH summary */}
        <Box>
          <Typography sx={{ fontFamily: 'Serif', fontSize: 13, fontWeight: 800,
            fontStyle: 'italic', mb: 0.5 }}>
            Cash
          </Typography>
          <Box sx={{ border: '1px solid #000' }}>
            {summaryRow('+  Balance Brought Forward', data.cash_summary?.bf)}
            {summaryRow('+  Total Receipts',          data.cash_summary?.receipts)}
            {summaryRow('−  Total Bank Deposit',      data.cash_summary?.bank_deposit)}
            {summaryRow('−  Total Cash Payment',      data.cash_summary?.cash_payment, { divider: true })}
            {summaryRow('=  Balance C/fwd',           data.cash_summary?.cfwd, { bold: true })}
          </Box>

          {/* Note + cheque listing under cash */}
          <Typography sx={{ fontFamily: 'Serif', fontSize: 13, fontWeight: 800, mt: 1.5, mb: 0.5 }}>
            Note
          </Typography>
          {(data.cheques || []).length === 0 ? (
            <Typography sx={{ fontFamily: 'Serif', fontSize: 11,
              fontStyle: 'italic', color: 'text.disabled' }}>
              No cheque payments in this period.
            </Typography>
          ) : (
            <Box sx={{ border: '1px solid #000' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '57% 43%' }}>
                <Box sx={ch}>Cheque No</Box>
                <Box sx={ch}>Amount</Box>
              </Box>
              {data.cheques.map((c, i) => (
                <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '57% 43%' }}>
                  <Box sx={tdC}>{c.cheque_no}</Box>
                  <Box sx={tdC}>GH¢ {fmt2(c.amount)}</Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* BANK summary */}
        <Box>
          <Typography sx={{ fontFamily: 'Serif', fontSize: 13, fontWeight: 800,
            fontStyle: 'italic', mb: 0.5 }}>
            Bank
          </Typography>
          <Box sx={{ border: '1px solid #000' }}>
            {summaryRow('+  Balance Brought Forward', data.bank_summary?.bf)}
            {summaryRow('+  Total Deposit',            data.bank_summary?.deposit)}
            {summaryRow('−  Cheque Payment',           data.bank_summary?.cheque_payment, { divider: true })}
            {summaryRow('=  Balance C/fwd',            data.bank_summary?.cfwd, { bold: true })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ── Cash & Bank Analysis (single-month, landscape grid) ─────────
// Faithful re-render of the legacy Jasper "Analysis Of Cash & Bank
// Balances" report. Eight columns: Details · Cash · Bank b/f ·
// Deposits · Bank Payment · Cash Payment · Closing Bal. Cash ·
// Closing Bal. Bank. One row for Cash B/F, one for Cash Receipts /
// Cash Payment, one row per bank account, then a Totals row.
function CashAnalysisReport({ data, schoolName }) {
  const t = data.totals || {};
  // Column layout — eight columns matching the JRXML widths.
  const COLS = '22% 11% 9% 7% 10% 11% 14% 14%'; // sums to 98% — the rest is borders / minor padding
  const cellBase = {
    border: '1px solid #000',
    fontFamily: 'Serif',
    fontSize: 11,
    px: 0.6, py: 0.35,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  };
  const ch = {
    ...cellBase,
    bgcolor: '#f5f5f5',
    fontWeight: 700,
    textAlign: 'center',
  };
  const td = cellBase;
  const tdC = { ...cellBase, textAlign: 'center' };
  const tdR = { ...cellBase, textAlign: 'right',
    fontVariantNumeric: 'tabular-nums', fontWeight: 700 };
  const totalsCell = { ...tdR, bgcolor: '#fafbfc', fontSize: 12 };

  return (
    <Box>
      <PrintHeader schoolName={schoolName} title="" subtitle="" />

      {/* Title — centered, large serif (matches JRXML SubTitle style) */}
      <Box sx={{ textAlign: 'center', mb: 1.5 }}>
        <Typography sx={{
          fontFamily: 'Serif', fontSize: 16, fontWeight: 800,
        }}>
          {data.title}
        </Typography>
      </Box>

      {/* Month label — bold underlined */}
      <Typography sx={{
        fontFamily: 'Serif', fontSize: 13, fontWeight: 800,
        textDecoration: 'underline',
        mb: 0.6, mt: 0.5,
      }}>
        {data.mon_yer}
      </Typography>

      {/* Header row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: COLS, border: '1px solid #000',
        bgcolor: '#f0f0f0' }}>
        <Box sx={ch}>Details</Box>
        <Box sx={ch}>Cash</Box>
        <Box sx={ch}>Bank b/f</Box>
        <Box sx={ch}>Deposits</Box>
        <Box sx={ch}>Bank Payment</Box>
        <Box sx={ch}>Cash Payment</Box>
        <Box sx={ch}>Closing Bal. Cash</Box>
        <Box sx={ch}>Closing Bal. Bank</Box>
      </Box>

      {/* Cash Bal. B/F row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: COLS,
        borderLeft: '1px solid #000', borderRight: '1px solid #000' }}>
        <Box sx={{ ...td, fontWeight: 700 }}>Cash Bal. B/F</Box>
        <Box sx={tdR}>{fmt2(data.cash_bf)}</Box>
        <Box sx={td}>&nbsp;</Box>
        <Box sx={td}>&nbsp;</Box>
        <Box sx={td}>&nbsp;</Box>
        <Box sx={td}>&nbsp;</Box>
        <Box sx={td}>&nbsp;</Box>
        <Box sx={td}>&nbsp;</Box>
      </Box>

      {/* Cash Receipt / Payment row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: COLS,
        borderLeft: '1px solid #000', borderRight: '1px solid #000',
        borderBottom: '1px solid #000' }}>
        <Box sx={{ ...td, fontWeight: 700 }}>Cash Receipt / Payment</Box>
        <Box sx={tdR}>{fmt2(data.cash_receipt)}</Box>
        <Box sx={td}>&nbsp;</Box>
        <Box sx={td}>&nbsp;</Box>
        <Box sx={td}>&nbsp;</Box>
        <Box sx={tdR}>{fmt2(data.cash_payment)}</Box>
        <Box sx={td}>&nbsp;</Box>
        <Box sx={td}>&nbsp;</Box>
      </Box>

      {/* Per-bank rows */}
      {(data.banks || []).length === 0 && (
        <Box sx={{ ...td, gridColumn: '1 / -1', textAlign: 'center',
          fontStyle: 'italic', color: 'text.disabled', py: 1.5,
          borderLeft: '1px solid #000', borderRight: '1px solid #000',
          borderBottom: '1px solid #000' }}>
          No bank accounts with movement in this month.
        </Box>
      )}
      {(data.banks || []).map((b, i) => (
        <Box key={i} sx={{ display: 'grid', gridTemplateColumns: COLS,
          borderLeft: '1px solid #000', borderRight: '1px solid #000',
          borderBottom: '1px solid #000' }}>
          <Box sx={{ ...td, fontWeight: 600 }}>Bank Bal. B/f - {b.account}</Box>
          <Box sx={td}>&nbsp;</Box>
          <Box sx={tdR}>{fmt2(b.bank_bf)}</Box>
          <Box sx={tdR}>{fmt2(b.deposits)}</Box>
          <Box sx={tdR}>{fmt2(b.bank_payment)}</Box>
          <Box sx={td}>&nbsp;</Box>
          <Box sx={td}>&nbsp;</Box>
          <Box sx={td}>&nbsp;</Box>
        </Box>
      ))}

      {/* Totals row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: COLS,
        border: '1px solid #000', borderTop: '2px solid #000',
        bgcolor: '#fafbfc' }}>
        <Box sx={{ ...td, fontWeight: 800, fontSize: 12,
          borderBottom: '1px solid #000' }}>
          Totals
        </Box>
        <Box sx={totalsCell}>{fmt2(t.cash_total_in)}</Box>
        <Box sx={totalsCell}>{fmt2(t.bank_bf)}</Box>
        <Box sx={totalsCell}>{fmt2(t.deposits)}</Box>
        <Box sx={totalsCell}>{fmt2(t.bank_payment)}</Box>
        <Box sx={totalsCell}>{fmt2(t.cash_payment)}</Box>
        <Box sx={{ ...totalsCell, fontSize: 13, color: 'success.dark' }}>
          {fmt2(t.closing_cash)}
        </Box>
        <Box sx={{ ...totalsCell, fontSize: 13, color: 'success.dark' }}>
          {fmt2(t.closing_bank)}
        </Box>
      </Box>

      {/* Petty note */}
      {Number(data.petty_cash_bal || 0) !== 0 && (
        <Typography sx={{
          fontFamily: 'Serif', fontSize: 11, fontStyle: 'italic',
          color: 'text.secondary', mt: 1,
        }}>
          Note: Closing Cash includes petty cash float of GH¢ {fmt2(data.petty_cash_bal)}.
        </Typography>
      )}
    </Box>
  );
}

// ── Receipts & Payment Account (Classic, single column portrait) ─
// Faithful re-render of the legacy Jasper "Receipts & Payment
// Account" report. Cash & Bank b/fwd at the top, RECEIPTS by income
// type, LESS PAYMENT grouped by category, and Closing Cash + Bank at
// the bottom. Both sides balance to the same grand total.
function ReceiptsPaymentsClassicReport({ data, schoolName }) {
  // Inner column shows individual line amounts; outer column shows
  // group subtotals / grand totals (matches the JRXML two-column
  // layout on the right edge).
  const Line = ({ label, indent = 0, inner, outer, opts = {} }) => (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', py: opts.tight ? 0.2 : 0.4 }}>
      <Typography sx={{
        flex: 1,
        pl: indent * 2,
        fontFamily: 'Serif',
        fontSize: opts.bold ? 12 : 11.5,
        fontWeight: opts.bold ? 800 : (opts.semibold ? 700 : 500),
        textDecoration: opts.underline ? 'underline' : 'none',
        textTransform: opts.upper ? 'uppercase' : 'none',
        color: opts.muted ? 'text.secondary' : 'text.primary',
      }}>
        {label}
      </Typography>
      <Box sx={{
        width: 110, textAlign: 'right',
        fontFamily: 'Serif', fontSize: 11.5,
        fontWeight: opts.bold ? 800 : 600,
        fontVariantNumeric: 'tabular-nums',
        borderTop: opts.innerRule ? '1px solid #000' : 'none',
        pr: 0.4,
      }}>
        {inner != null && inner !== '' ? fmt2(inner) : ''}
      </Box>
      <Box sx={{
        width: 110, textAlign: 'right',
        fontFamily: 'Serif', fontSize: opts.grand ? 12 : 11.5,
        fontWeight: opts.bold || opts.grand ? 800 : 600,
        fontVariantNumeric: 'tabular-nums',
        borderTop: opts.outerRule ? '1px solid #000' : 'none',
        borderBottom: opts.outerDouble ? '3px double #000' : 'none',
        pr: 0.4,
      }}>
        {outer != null && outer !== '' ? fmt2(outer) : ''}
      </Box>
    </Stack>
  );

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <PrintHeader schoolName={schoolName} title="" subtitle="" />

      {/* Title — centred, underlined */}
      <Typography sx={{
        textAlign: 'center',
        fontFamily: 'Serif', fontSize: 13,
        textDecoration: 'underline',
        mb: 0.5,
      }}>
        {data.title}
      </Typography>

      {/* Two GH¢ headers at the right edge */}
      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', mt: 1.5, mb: 0.5 }}>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ width: 110, textAlign: 'center',
          fontFamily: 'Serif', fontSize: 14, fontWeight: 800, color: '#990000' }}>
          GH¢
        </Box>
        <Box sx={{ width: 110, textAlign: 'center',
          fontFamily: 'Serif', fontSize: 14, fontWeight: 800, color: '#990000' }}>
          GH¢
        </Box>
      </Stack>

      {/* ── Cash & Bank b/fwd ─────────────────────────────── */}
      <Line label="Cash b/fwd" semibold inner={data.bf?.cash + (data.bf?.petty || 0)} />
      <Line label="Bank b/fwd" semibold inner={data.bf?.bank} innerRule />
      <Line label="Total Cash & Bank b/fwd" semibold outer={data.bf?.total} />

      {/* ── RECEIPTS section ─────────────────────────────── */}
      <Typography sx={{
        fontFamily: 'Serif', fontSize: 13, fontWeight: 800,
        textDecoration: 'underline',
        mt: 2, mb: 0.5,
      }}>
        RECEIPTS:
      </Typography>
      {(data.receipts?.by_type || []).length === 0 && (
        <Typography sx={{ pl: 1, fontFamily: 'Serif', fontSize: 11.5,
          fontStyle: 'italic', color: 'text.disabled', py: 0.5 }}>
          No receipts in this period.
        </Typography>
      )}
      {(data.receipts?.by_type || []).map((r, i) => (
        <Line key={i} label={r.type} indent={1} inner={r.total} />
      ))}

      <Line label="TOTAL RECEIPTS" semibold upper inner={data.receipts?.total} innerRule />
      <Line label="" outer={data.receipts_grand} grand outerDouble />

      {/* ── LESS PAYMENT section ─────────────────────────── */}
      <Typography sx={{
        fontFamily: 'Serif', fontSize: 13, fontWeight: 800,
        textDecoration: 'underline',
        mt: 2, mb: 0.5,
      }}>
        LESS PAYMENT
      </Typography>
      {(data.payments?.by_category || []).length === 0 && (
        <Typography sx={{ pl: 1, fontFamily: 'Serif', fontSize: 11.5,
          fontStyle: 'italic', color: 'text.disabled', py: 0.5 }}>
          No payments in this period.
        </Typography>
      )}
      {(data.payments?.by_category || []).map((c, i) => (
        <Box key={i} sx={{ mb: 0.6 }}>
          {/* Category header — pale beige bar (matches JRXML SubTitle frame) */}
          <Box sx={{
            bgcolor: '#F2EBDF',
            border: '1px solid #B89F7D',
            px: 1, py: 0.4, mb: 0.2,
          }}>
            <Typography sx={{
              fontFamily: 'Serif', fontSize: 12, fontWeight: 800,
              color: '#736343',
            }}>
              {c.category}
            </Typography>
          </Box>
          {(c.narrations || []).map((n, j) => (
            <Line key={j} label={n.narration} indent={1} inner={n.total} opts={{ tight: true }} />
          ))}
          <Line label="" inner={c.total} opts={{ innerRule: true, tight: true }} />
        </Box>
      ))}

      <Line label="TOTAL PAYMENT" semibold upper outer={data.payments?.total} outerRule />

      {/* ── Closing Cash Bal ─────────────────────────────── */}
      <Box sx={{ mt: 2 }}>
        <Line label="Closing Cash Bal:" semibold />
        <Line label="Cashbook" indent={1} inner={data.closing?.cashbook} opts={{ tight: true }} />
        <Line label="Petty Cash" indent={1} inner={data.closing?.petty} opts={{ tight: true, innerRule: true }} />
        <Line label="Total Cash-In-Hand" indent={1} semibold inner={data.closing?.cash_in_hand} />
      </Box>

      {/* ── Bank closing balances ────────────────────────── */}
      {(data.closing?.banks || []).length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Line label="Closing Bank Bal:" semibold />
          {data.closing.banks.map((b, i) => (
            <Line key={i} label={b.account} indent={1} inner={b.balance} opts={{ tight: true }} />
          ))}
          <Line label="Total Bank" indent={1} semibold inner={data.closing.bank_total}
            opts={{ innerRule: true }} />
        </Box>
      )}

      {/* ── Grand Total of Payments side ─────────────────── */}
      <Line label="" outer={data.payments_grand} grand outerDouble />

      {/* ── Balance check strip ──────────────────────────── */}
      <Box sx={{
        mt: 2, p: 1.2, borderRadius: 1,
        bgcolor: Math.abs(data.difference || 0) < 0.01
          ? alpha('#2e7d32', 0.08) : alpha('#d32f2f', 0.08),
        border: '1px solid',
        borderColor: Math.abs(data.difference || 0) < 0.01 ? 'success.light' : 'error.light',
      }}>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography sx={{
            fontFamily: 'Serif', fontWeight: 800, fontSize: 12,
            color: Math.abs(data.difference || 0) < 0.01 ? 'success.dark' : 'error.dark',
            textTransform: 'uppercase', letterSpacing: 0.5,
          }}>
            {Math.abs(data.difference || 0) < 0.01
              ? '✓ Balanced'
              : `Out of balance: GH¢ ${fmt2(Math.abs(data.difference || 0))}`}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontFamily: 'Serif', fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
            Receipts side: <strong>GH¢ {fmt2(data.receipts_grand)}</strong>
            &nbsp;·&nbsp;
            Payments side: <strong>GH¢ {fmt2(data.payments_grand)}</strong>
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

// ── Finance report renderers ────────────────────────────────────
function GeneralReport({ data, schoolName }) {
  const subtitle = `${data.from} → ${data.to}${data.cashier ? ` · Cashier: ${data.cashier}` : ' · All cashiers'}`;
  const scopeColor = (label) => {
    switch (label) {
      case 'Fee Payment':        return 'primary';
      case 'Class Payment':      return 'secondary';
      case 'Other Payment':      return 'info';
      case 'Individual Payment': return 'success';
      default:                   return 'default';
    }
  };
  return (
    <Box>
      <PrintHeader schoolName={schoolName} title="General Income Report" subtitle={subtitle} />

      {/* Top summary tiles */}
      <Stack direction="row" spacing={1.2} flexWrap="wrap" rowGap={1} sx={{ mb: 2 }}>
        <Chip label={`Total · GHS ${fmt2(data.grandTotal)}`} color="success"
          sx={{ fontWeight: 800, fontSize: 13, px: 0.6, height: 30 }} />
        <Chip label={`${data.receiptCount} receipts`} variant="outlined" sx={{ fontWeight: 700 }} />
        {data.cashier
          ? <Chip color="primary" label={`Cashier: ${data.cashier}`} sx={{ fontWeight: 700 }} />
          : <Chip variant="outlined" label="All cashiers" sx={{ fontWeight: 700 }} />}
      </Stack>

      {/* Breakdown summaries */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 1.5, mb: 2 }}>
        {data.by_scope?.length > 0 && (
          <Box>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.7 }}>By Payment Scope</Typography>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell><b>Scope</b></TableCell>
                <TableCell align="right"><b>Total</b></TableCell>
                <TableCell align="right"><b>%</b></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {data.by_scope.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Chip size="small" color={scopeColor(r.label)} label={r.label}
                        sx={{ fontWeight: 700, height: 22 }} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>GHS {fmt2(r.total)}</TableCell>
                    <TableCell align="right">
                      {data.grandTotal ? ((Number(r.total) / data.grandTotal) * 100).toFixed(1) + '%' : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {data.by_mode?.length > 0 && (
          <Box>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.7 }}>By Payment Mode</Typography>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell><b>Mode</b></TableCell>
                <TableCell align="right"><b>Total</b></TableCell>
                <TableCell align="right"><b>%</b></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {data.by_mode.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.label}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>GHS {fmt2(r.total)}</TableCell>
                    <TableCell align="right">
                      {data.grandTotal ? ((Number(r.total) / data.grandTotal) * 100).toFixed(1) + '%' : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {!data.cashier && data.by_cashier?.length > 0 && (
          <Box>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.7 }}>By Cashier</Typography>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell><b>Cashier</b></TableCell>
                <TableCell align="right"><b>Total</b></TableCell>
                <TableCell align="right"><b>%</b></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {data.by_cashier.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.label}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>GHS {fmt2(r.total)}</TableCell>
                    <TableCell align="right">
                      {data.grandTotal ? ((Number(r.total) / data.grandTotal) * 100).toFixed(1) + '%' : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {data.by_account?.length > 0 && (
          <Box>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.7 }}>By Income Type</Typography>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell><b>Income Type</b></TableCell>
                <TableCell align="right"><b>Total</b></TableCell>
                <TableCell align="right"><b>%</b></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {data.by_account.slice(0, 12).map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.label}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>GHS {fmt2(r.total)}</TableCell>
                    <TableCell align="right">
                      {data.grandTotal ? ((Number(r.total) / data.grandTotal) * 100).toFixed(1) + '%' : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>

      {/* Detail rows */}
      <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
        Receipts · {data.receiptCount}
      </Typography>
      <Table size="small">
        <TableHead><TableRow>
          <TableCell><b>Date</b></TableCell>
          <TableCell><b>Receipt</b></TableCell>
          <TableCell><b>Scope</b></TableCell>
          <TableCell><b>Income Type</b></TableCell>
          <TableCell><b>Payer / Student</b></TableCell>
          <TableCell><b>Class</b></TableCell>
          <TableCell><b>Mode</b></TableCell>
          <TableCell><b>Cashier</b></TableCell>
          <TableCell align="right"><b>Amount</b></TableCell>
        </TableRow></TableHead>
        <TableBody>
          {data.rows.map((r, i) => (
            <TableRow key={i}>
              <TableCell>{r.day}</TableCell>
              <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{r.receipt_no}</TableCell>
              <TableCell>
                <Chip size="small" color={scopeColor(r.scope_label)} label={r.scope_label}
                  variant="outlined" sx={{ fontWeight: 700, height: 22 }} />
              </TableCell>
              <TableCell>{r.income_type || '—'}</TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>{r.payer_name || '—'}</Typography>
                {r.student_id && r.student_id !== '-' && (
                  <Typography variant="caption" color="text.secondary">{r.student_id}</Typography>
                )}
              </TableCell>
              <TableCell>{r.class_name || '—'}</TableCell>
              <TableCell>{r.mode || '—'}</TableCell>
              <TableCell>{r.received_by_name || '—'}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>GHS {fmt2(r.amount)}</TableCell>
            </TableRow>
          ))}
          {data.rows.length === 0 && (
            <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
              No receipts in this period.
            </TableCell></TableRow>
          )}
          {data.rows.length > 0 && (
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell colSpan={8} sx={{ fontWeight: 800, textAlign: 'right' }}>GRAND TOTAL</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800 }}>GHS {fmt2(data.grandTotal)}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

function DailyIncomeReport({ data, schoolName }) {
  return (
    <Box>
      <PrintHeader schoolName={schoolName} title="Daily Income Summary"
        subtitle={`${data.from} → ${data.to}`} />
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow><TableCell><b>Date</b></TableCell>
              <TableCell align="right"><b>Fee Total</b></TableCell>
              <TableCell align="right"><b>Other Total</b></TableCell>
              <TableCell align="right"><b>Day Total</b></TableCell>
              <TableCell align="right"><b>Receipts</b></TableCell>
              <TableCell align="right"><b>Cashiers</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell>{r.day}</TableCell>
                <TableCell align="right">GHS {fmt2(r.fee_total)}</TableCell>
                <TableCell align="right">GHS {fmt2(r.other_total)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  GHS {fmt2(Number(r.fee_total) + Number(r.other_total))}
                </TableCell>
                <TableCell align="right">{r.receipts}</TableCell>
                <TableCell align="right">{r.cashiers}</TableCell>
              </TableRow>
            ))}
            {data.rows.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                No receipts in this date range.
              </TableCell></TableRow>
            )}
            {data.rows.length > 0 && (
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 800 }}>TOTAL</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>GHS {fmt2(data.totals.fee_total)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>GHS {fmt2(data.totals.other_total)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>
                  GHS {fmt2(data.totals.fee_total + data.totals.other_total)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>{data.totals.receipts}</TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {data.by_mode?.length > 0 && (
        <>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 3, mb: 1 }}>By Payment Mode</Typography>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell><b>Mode</b></TableCell>
              <TableCell align="right"><b>Receipts</b></TableCell>
              <TableCell align="right"><b>Total</b></TableCell>
            </TableRow></TableHead>
            <TableBody>
              {data.by_mode.map(m => (
                <TableRow key={m.mode}>
                  <TableCell>{m.mode}</TableCell>
                  <TableCell align="right">{m.count}</TableCell>
                  <TableCell align="right">GHS {fmt2(m.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Box>
  );
}

function AccountSummaryReport({ data, schoolName }) {
  return (
    <Box>
      <PrintHeader schoolName={schoolName} title="Income by Account"
        subtitle={`${data.from} → ${data.to}`} />
      <Table size="small">
        <TableHead><TableRow>
          <TableCell><b>Account / Income Type</b></TableCell>
          <TableCell align="right"><b>Receipts</b></TableCell>
          <TableCell align="right"><b>Distinct Payers</b></TableCell>
          <TableCell align="right"><b>Total</b></TableCell>
          <TableCell align="right"><b>% of Grand</b></TableCell>
        </TableRow></TableHead>
        <TableBody>
          {data.rows.map((r, i) => (
            <TableRow key={i}>
              <TableCell sx={{ fontWeight: 600 }}>{r.account}</TableCell>
              <TableCell align="right">{r.receipts}</TableCell>
              <TableCell align="right">{r.payers}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>GHS {fmt2(r.total)}</TableCell>
              <TableCell align="right">{data.grand ? ((Number(r.total) / data.grand) * 100).toFixed(1) : '0.0'}%</TableCell>
            </TableRow>
          ))}
          {data.rows.length === 0 && (
            <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
              No income recorded in this period.
            </TableCell></TableRow>
          )}
          {data.rows.length > 0 && (
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 800 }}>GRAND TOTAL</TableCell>
              <TableCell />
              <TableCell />
              <TableCell align="right" sx={{ fontWeight: 800 }}>GHS {fmt2(data.grand)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800 }}>100%</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

function ClassOtherReport({ data, schoolName }) {
  return (
    <Box>
      <PrintHeader schoolName={schoolName} title="Class & Other Payments Report"
        subtitle={`${data.from} → ${data.to}`} />
      <Table size="small">
        <TableHead><TableRow>
          <TableCell><b>Date</b></TableCell>
          <TableCell><b>Receipt</b></TableCell>
          <TableCell><b>Type</b></TableCell>
          <TableCell><b>Payer</b></TableCell>
          <TableCell><b>Class</b></TableCell>
          <TableCell><b>Mode</b></TableCell>
          <TableCell><b>Cashier</b></TableCell>
          <TableCell align="right"><b>Amount</b></TableCell>
        </TableRow></TableHead>
        <TableBody>
          {data.rows.map((r, i) => (
            <TableRow key={i}>
              <TableCell>{r.dat}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{r.rece_no}</TableCell>
              <TableCell>{r.payment_type}</TableCell>
              <TableCell>{r.payer_name || '—'}</TableCell>
              <TableCell>{r.class_name || '—'}</TableCell>
              <TableCell>{r.mode || '—'}</TableCell>
              <TableCell>{r.received_by_name || '—'}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>GHS {fmt2(r.amount)}</TableCell>
            </TableRow>
          ))}
          {data.rows.length === 0 && (
            <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
              No class / other payments in this period.
            </TableCell></TableRow>
          )}
          {data.rows.length > 0 && (
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell colSpan={7} sx={{ fontWeight: 800, textAlign: 'right' }}>TOTAL</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800 }}>GHS {fmt2(data.total)}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

function AdjustmentsReport({ data, schoolName }) {
  return (
    <Box>
      <PrintHeader schoolName={schoolName} title="Balance Adjustments Report"
        subtitle={`${data.from} → ${data.to}`} />
      <Stack direction="row" spacing={2} sx={{ mb: 1.5 }} flexWrap="wrap">
        <Chip label={`Write-offs · ${data.summary.writeoff.count} (GHS ${fmt2(data.summary.writeoff.posted)} posted)`}
          color="warning" sx={{ fontWeight: 700 }} />
        <Chip label={`Add-bills · ${data.summary.addbill.count} (GHS ${fmt2(data.summary.addbill.posted)} posted)`}
          color="info" sx={{ fontWeight: 700 }} />
      </Stack>
      <Table size="small">
        <TableHead><TableRow>
          <TableCell><b>When</b></TableCell>
          <TableCell><b>Student</b></TableCell>
          <TableCell><b>Account</b></TableCell>
          <TableCell><b>Type</b></TableCell>
          <TableCell align="right"><b>Amount</b></TableCell>
          <TableCell align="right"><b>Old → New</b></TableCell>
          <TableCell><b>Reason</b></TableCell>
          <TableCell><b>Status</b></TableCell>
          <TableCell><b>By / Approver</b></TableCell>
        </TableRow></TableHead>
        <TableBody>
          {data.rows.map((r) => (
            <TableRow key={r.id} sx={{ opacity: r.status === 'REVERSED' || r.status === 'REJECTED' ? 0.55 : 1 }}>
              <TableCell>{new Date(r.created_at).toLocaleString('en-GH')}</TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>{r.student_name}</Typography>
                <Typography variant="caption" color="text.secondary">{r.student_id}</Typography>
              </TableCell>
              <TableCell>{r.acc_name}</TableCell>
              <TableCell>{r.type === 'writeoff' ? 'Write-off' : 'Add Bill'}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>GHS {fmt2(r.amount)}</TableCell>
              <TableCell align="right">
                {fmt2(r.old_balance)} → <b>{fmt2(r.new_balance)}</b>
              </TableCell>
              <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.reason}</TableCell>
              <TableCell>{r.status}</TableCell>
              <TableCell>
                <Typography variant="caption" sx={{ display: 'block' }}>{r.created_by_name || '—'}</Typography>
                {r.approved_by_name && <Typography variant="caption" color="text.secondary">→ {r.approved_by_name}</Typography>}
              </TableCell>
            </TableRow>
          ))}
          {data.rows.length === 0 && (
            <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
              No adjustments in this period.
            </TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

function CashierSummaryReport({ data, schoolName }) {
  const max = Math.max(1, ...data.rows.map(r => Number(r.total)));
  return (
    <Box>
      <PrintHeader schoolName={schoolName} title="Cashier Performance"
        subtitle={`${data.from} → ${data.to} · Grand Total GHS ${fmt2(data.grand)}`} />
      <Table size="small">
        <TableHead><TableRow>
          <TableCell><b>Cashier</b></TableCell>
          <TableCell align="right"><b>Receipts</b></TableCell>
          <TableCell align="right"><b>Days Active</b></TableCell>
          <TableCell align="right"><b>Total</b></TableCell>
          <TableCell align="right"><b>% of Grand</b></TableCell>
        </TableRow></TableHead>
        <TableBody>
          {data.rows.map((r) => (
            <TableRow key={r.cashier}>
              <TableCell sx={{ fontWeight: 600 }}>{r.cashier || '—'}</TableCell>
              <TableCell align="right">{r.receipts}</TableCell>
              <TableCell align="right">{r.days_active}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>GHS {fmt2(r.total)}</TableCell>
              <TableCell align="right">{data.grand ? ((Number(r.total) / data.grand) * 100).toFixed(1) : '0.0'}%</TableCell>
            </TableRow>
          ))}
          {data.rows.length === 0 && (
            <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
              No cashier activity.
            </TableCell></TableRow>
          )}
          {data.rows.length > 0 && (
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 800 }}>GRAND TOTAL</TableCell>
              <TableCell />
              <TableCell />
              <TableCell align="right" sx={{ fontWeight: 800 }}>GHS {fmt2(data.grand)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800 }}>100%</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

// ── Daily Collection Balances ───────────────────────────────────
// Snapshot report (no date range) — uses its own filter card with
// income type / class / search / only=owing|credit toggles.
function SnapshotBalancesFilters({ value, onChange, onApply, loading,
                                   incomeTypes = [], classes = [] }) {
  return (
    <Card sx={{ mb: 2 }} className="no-print">
      <CardContent sx={{ pb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="center">
          <TextField select size="small" label="Income type" sx={{ flex: 1, minWidth: 160 }}
            value={value.income_type}
            onChange={(e) => onChange({ ...value, income_type: e.target.value })}>
            <MenuItem value="">All income types</MenuItem>
            {incomeTypes.map((t) => (
              <MenuItem key={t.trans} value={t.type}>{t.type}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Class" sx={{ flex: 1, minWidth: 140 }}
            value={value.class_name}
            onChange={(e) => onChange({ ...value, class_name: e.target.value })}>
            <MenuItem value="">All classes</MenuItem>
            {classes.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Search name / ID" sx={{ flex: 1, minWidth: 180 }}
            value={value.q}
            onChange={(e) => onChange({ ...value, q: e.target.value })} />
          <TextField select size="small" label="Show" sx={{ flex: 1, minWidth: 140 }}
            value={value.only}
            onChange={(e) => onChange({ ...value, only: e.target.value })}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="owing">Owing only</MenuItem>
            <MenuItem value="credit">Credit only</MenuItem>
          </TextField>
          <Button variant="contained" size="medium" onClick={() => onApply()}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            sx={{ textTransform: 'none', fontWeight: 800, minWidth: 110 }}>
            Apply
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function DailyCollectionBalancesReport({ data, schoolName }) {
  const t = data?.totals || { arrears: 0, credit: 0, pending: 0, debtors: 0, creditors: 0, count: 0 };
  const f = data?.filters || {};
  const filterBits = [];
  if (f.income_type) filterBits.push(`Income: ${f.income_type}`);
  if (f.class_name)  filterBits.push(`Class: ${f.class_name}`);
  if (f.q)           filterBits.push(`Search: "${f.q}"`);
  if (f.only)        filterBits.push(f.only === 'owing' ? 'Owing only' : 'Credit only');
  const subtitle = filterBits.length
    ? filterBits.join(' · ')
    : 'All students, all income types';
  const rows = data?.rows || [];

  return (
    <Box>
      <PrintHeader schoolName={schoolName} title="Daily Collection Balances" subtitle={subtitle} />

      {/* Summary tiles */}
      <Box sx={{
        display: 'grid', gap: 1, mb: 2,
        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
      }}>
        {[
          { label: 'Total Arrears',  value: `GHS ${fmt2(t.arrears)}`, sub: `${t.debtors} debtor${t.debtors === 1 ? '' : 's'}`, color: '#b91c1c' },
          { label: 'Total Credit',   value: `GHS ${fmt2(t.credit)}`,  sub: `${t.creditors} ahead of bill`,                       color: '#15803d' },
          { label: 'Pending Approval', value: `GHS ${fmt2(t.pending)}`, sub: 'queued in collector approvals',                    color: '#b45309' },
          { label: 'Subscriptions',  value: t.count,                  sub: f.income_type || 'all income types',                  color: '#1e40af' },
        ].map((m) => (
          <Box key={m.label} sx={{
            border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.2,
          }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800,
              textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
              {m.label}
            </Typography>
            <Typography variant="h6" fontWeight={800} sx={{ color: m.color, lineHeight: 1.2,
              fontVariantNumeric: 'tabular-nums' }}>
              {m.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">{m.sub}</Typography>
          </Box>
        ))}
      </Box>

      <Table size="small">
        <TableHead><TableRow>
          <TableCell sx={{ width: 36 }}><b>#</b></TableCell>
          <TableCell><b>Student</b></TableCell>
          <TableCell><b>Index</b></TableCell>
          <TableCell><b>Class</b></TableCell>
          <TableCell><b>Income Type</b></TableCell>
          <TableCell align="right"><b>Daily</b></TableCell>
          <TableCell align="right"><b>Arrears</b></TableCell>
          <TableCell align="right"><b>Credit</b></TableCell>
          <TableCell align="right"><b>Pending</b></TableCell>
        </TableRow></TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={9} align="center"
              sx={{ py: 4, color: 'text.secondary' }}>
              No balances match the filters.
            </TableCell></TableRow>
          )}
          {rows.map((r, i) => (
            <TableRow key={r.id || `${r.student_id}-${r.income_type}`}>
              <TableCell>{i + 1}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{r.student_name || '-'}</TableCell>
              <TableCell>{r.student_id}</TableCell>
              <TableCell>{r.class_name || '-'}</TableCell>
              <TableCell>{r.income_type}</TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                GHS {fmt2(r.daily_amount)}
              </TableCell>
              <TableCell align="right" sx={{
                fontVariantNumeric: 'tabular-nums',
                color: Number(r.arrears) > 0 ? 'error.main' : 'text.secondary',
                fontWeight: Number(r.arrears) > 0 ? 800 : 500,
              }}>{fmt2(r.arrears)}</TableCell>
              <TableCell align="right" sx={{
                fontVariantNumeric: 'tabular-nums',
                color: Number(r.credit) > 0 ? 'success.main' : 'text.secondary',
                fontWeight: Number(r.credit) > 0 ? 800 : 500,
              }}>{fmt2(r.credit)}</TableCell>
              <TableCell align="right" sx={{
                fontVariantNumeric: 'tabular-nums',
                color: Number(r.pending_payment) > 0 ? 'warning.dark' : 'text.disabled',
              }}>{Number(r.pending_payment) > 0 ? `GHS ${fmt2(r.pending_payment)}` : '-'}</TableCell>
            </TableRow>
          ))}
          {rows.length > 0 && (
            <TableRow sx={{ '& td': { borderTop: '2px solid', fontWeight: 800 } }}>
              <TableCell colSpan={6} align="right">GRAND TOTAL</TableCell>
              <TableCell align="right" sx={{ color: 'error.main', fontVariantNumeric: 'tabular-nums' }}>
                {fmt2(t.arrears)}
              </TableCell>
              <TableCell align="right" sx={{ color: 'success.main', fontVariantNumeric: 'tabular-nums' }}>
                {fmt2(t.credit)}
              </TableCell>
              <TableCell align="right" sx={{ color: 'warning.dark', fontVariantNumeric: 'tabular-nums' }}>
                {fmt2(t.pending)}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

// ── Print header used inside each report's print area ────────────
function PrintHeader({ schoolName, title, subtitle }) {
  return (
    <Box sx={{ borderBottom: '2px solid #000', mb: 2, pb: 1.5, textAlign: 'center' }}>
      <Typography variant="h5" fontWeight={800}>{schoolName || 'School Management System'}</Typography>
      <Typography variant="h6" sx={{ mt: 0.5 }}>{title}</Typography>
      {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
      <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
        Generated: {new Date().toLocaleString('en-GH')}
      </Typography>
    </Box>
  );
}

// ── BROWSE: categorized cards of reports the user can see ────────
function ReportBrowser({ accessible, onPick }) {
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const filtered = q
    ? accessible.filter(r => r.label.toLowerCase().includes(q) || r.desc?.toLowerCase().includes(q))
    : accessible;

  const grouped = useMemo(() => {
    const map = {};
    for (const cat of REPORT_CATEGORIES) map[cat] = [];
    for (const r of filtered) (map[r.category] = map[r.category] || []).push(r);
    return map;
  }, [filtered]);

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <Box sx={{ p: 1.5 }}>
          <TextField fullWidth size="small" placeholder="Search reports…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        </Box>
      </Card>

      {accessible.length === 0 ? (
        <Card sx={{ p: 6, textAlign: 'center' }}>
          <EmptyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="subtitle1" fontWeight={700}>No reports assigned</Typography>
          <Typography variant="body2" color="text.secondary">
            Ask the super administrator to grant you access to one or more reports.
          </Typography>
        </Card>
      ) : (
        REPORT_CATEGORIES.filter(cat => grouped[cat]?.length > 0).map(cat => (
          <Card key={cat} sx={{ mb: 2 }}>
            <Box sx={{
              px: 2, py: 1.4, borderBottom: 1, borderColor: 'divider',
              bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
              display: 'flex', alignItems: 'center', gap: 1.2,
            }}>
              <CategoryIcon fontSize="small" color="primary" />
              <Typography variant="overline" fontWeight={800} color="primary" sx={{ letterSpacing: 1 }}>
                {cat}
              </Typography>
              <Chip size="small" label={grouped[cat].length} variant="outlined"
                sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
            </Box>
            <Box sx={{ p: 1.5, display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' } }}>
              {grouped[cat].map(r => (
                <Paper key={r.key} variant="outlined"
                  onClick={() => onPick(r)}
                  sx={{
                    p: 1.5, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.5,
                    transition: 'all 0.15s',
                    '&:hover': {
                      borderColor: 'primary.main', boxShadow: '0 2px 12px rgba(21,101,192,0.15)',
                      transform: 'translateY(-1px)',
                    },
                  }}>
                  <Avatar sx={{ width: 38, height: 38, bgcolor: (t) => alpha(t.palette.primary.main, 0.12), color: 'primary.main' }}>
                    {r.icon}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} noWrap>{r.label}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      overflow: 'hidden', fontSize: 11,
                    }}>
                      {r.desc}
                    </Typography>
                  </Box>
                  <ChevronIcon sx={{ color: 'text.disabled' }} />
                </Paper>
              ))}
            </Box>
          </Card>
        ))
      )}
    </>
  );
}

// ── Specific report views ────────────────────────────────────────

function AllStudentsReport({ data, schoolName }) {
  const cla = data?.filters?.cla || '';
  const subtitle = cla
    ? `Class: ${cla} - ${data.total} student(s)`
    : `${data.total} active student(s) - all classes`;
  return (
    <>
      <PrintHeader schoolName={schoolName} title="All Students Register"
        subtitle={subtitle} />
      <TableContainer>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>#</TableCell>
            <TableCell>Index No.</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Gender</TableCell>
            <TableCell>Class</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Guardian</TableCell>
            <TableCell>Mobile</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {data.rows.map((r, i) => (
              <TableRow key={r.trans_code}>
                <TableCell>{i + 1}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{r.index_no}</TableCell>
                <TableCell><strong>{r.firstname} {r.lastname}</strong></TableCell>
                <TableCell>{r.gender}</TableCell>
                <TableCell>{r.calss || '—'}</TableCell>
                <TableCell>{r.type || '—'}</TableCell>
                <TableCell>{r.p_name || '—'}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{r.mobi || r.p_t_m || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

function ClassSummaryReport({ data, schoolName }) {
  return (
    <>
      <PrintHeader schoolName={schoolName} title="Class Summary"
        subtitle={`${data.totals.total} students across ${data.rows.length} class(es)`} />
      <TableContainer>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Class</TableCell>
            <TableCell align="right">Male</TableCell>
            <TableCell align="right">Female</TableCell>
            <TableCell align="right">Total</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {data.rows.map(r => (
              <TableRow key={r.calss}>
                <TableCell><strong>{r.calss}</strong></TableCell>
                <TableCell align="right">{r.male}</TableCell>
                <TableCell align="right">{r.female}</TableCell>
                <TableCell align="right"><strong>{r.total}</strong></TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ '& td': { borderTop: 2, borderColor: 'divider', fontWeight: 800 } }}>
              <TableCell><strong>TOTAL</strong></TableCell>
              <TableCell align="right"><strong>{data.totals.male}</strong></TableCell>
              <TableCell align="right"><strong>{data.totals.female}</strong></TableCell>
              <TableCell align="right"><strong>{data.totals.total}</strong></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

function GenderReport({ data, schoolName }) {
  return (
    <>
      <PrintHeader schoolName={schoolName} title="Gender Distribution"
        subtitle={`Total active students: ${data.total}`} />
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {data.rows.map(r => {
          const pct = data.total ? ((Number(r.n) / data.total) * 100).toFixed(1) : '0.0';
          const color = r.gender === 'Male' ? '#1565c0' : r.gender === 'Female' ? '#c2185b' : '#6a1b9a';
          return (
            <Grid item xs={12} sm={4} key={r.gender}>
              <Card sx={{ p: 2.5, textAlign: 'center', border: `2px solid ${alpha(color, 0.2)}` }}>
                <Typography variant="overline" sx={{ color, fontWeight: 700 }}>{r.gender}</Typography>
                <Typography variant="h3" fontWeight={800} sx={{ color, my: 1 }}>{r.n}</Typography>
                <Typography variant="caption" color="text.secondary">{pct}% of total</Typography>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      <TableContainer>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Gender</TableCell>
            <TableCell align="right">Count</TableCell>
            <TableCell align="right">Percentage</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {data.rows.map(r => {
              const pct = data.total ? ((Number(r.n) / data.total) * 100).toFixed(1) : '0.0';
              return (
                <TableRow key={r.gender}>
                  <TableCell><strong>{r.gender}</strong></TableCell>
                  <TableCell align="right">{r.n}</TableCell>
                  <TableCell align="right">{pct}%</TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={{ '& td': { borderTop: 2, borderColor: 'divider' } }}>
              <TableCell><strong>TOTAL</strong></TableCell>
              <TableCell align="right"><strong>{data.total}</strong></TableCell>
              <TableCell align="right"><strong>100%</strong></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

function LeaversReport({ data, schoolName }) {
  return (
    <>
      <PrintHeader schoolName={schoolName} title="Leavers Register"
        subtitle={`${data.total} student(s) have left`} />
      <TableContainer>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>#</TableCell>
            <TableCell>Index No.</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Gender</TableCell>
            <TableCell>Last Class</TableCell>
            <TableCell>Date Left</TableCell>
            <TableCell>Reason</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {data.rows.map((r, i) => (
              <TableRow key={r.tran_code}>
                <TableCell>{i + 1}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{r.index_no}</TableCell>
                <TableCell><strong>{r.firstname} {r.lastname}</strong></TableCell>
                <TableCell>{r.gender}</TableCell>
                <TableCell>{r.calss || '—'}</TableCell>
                <TableCell>{r.left_at ? new Date(r.left_at).toLocaleDateString('en-GH') : '—'}</TableCell>
                <TableCell sx={{ maxWidth: 280 }}>{r.why || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

function IndividualReport({ data, schoolName }) {
  const s = data.student;
  const p = data.parents || {};
  return (
    <>
      <PrintHeader schoolName={schoolName} title="Student Profile Report" subtitle={`${s.firstname} ${s.lastname}`} />
      <Stack direction="row" spacing={3} sx={{ mb: 3 }}>
        {s.image && (
          <Box component="img" src={s.image} alt=""
            sx={{ width: 120, height: 120, borderRadius: 2, objectFit: 'cover', border: '1px solid #ddd' }} />
        )}
        <Box>
          <Typography variant="h5" fontWeight={800}>{s.firstname} {s.lastname}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {s.index_no} · {s.gender} · DOB {s.dob || '—'}
          </Typography>
          <Stack direction="row" spacing={1} mt={1}>
            <Chip size="small" label={s.calss === 'Alumna' ? 'Alumni' : (s.calss || '—')} color="primary" variant="outlined" />
            <Chip size="small" label={s.type || '—'} variant="outlined" />
            {s.house && <Chip size="small" label={`House: ${s.house}`} variant="outlined" />}
          </Stack>
        </Box>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" fontWeight={700} color="primary">Father</Typography>
            <Divider sx={{ my: 1 }} />
            <ProfileRow label="Name"        value={p.p_full_n} />
            <ProfileRow label="Mobile"      value={p.p_mo_num} />
            <ProfileRow label="Email"       value={p.p_e_add} />
            <ProfileRow label="Occupation"  value={p.p_occu} />
            <ProfileRow label="Employer"    value={p.p_emp_na} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" fontWeight={700} color="secondary">Mother</Typography>
            <Divider sx={{ my: 1 }} />
            <ProfileRow label="Name"        value={p.m_full_n} />
            <ProfileRow label="Mobile"      value={p.m_mo_num} />
            <ProfileRow label="Email"       value={p.m_e_add} />
            <ProfileRow label="Occupation"  value={p.m_occu} />
            <ProfileRow label="Employer"    value={p.m_emp_na} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" fontWeight={700} color="primary">Guardian</Typography>
            <Divider sx={{ my: 1 }} />
            <ProfileRow label="Name"          value={s.p_name} />
            <ProfileRow label="Relationship"  value={s.p_rele} />
            <ProfileRow label="Mobile"        value={s.mobi || s.p_t_m} />
            <ProfileRow label="Email"         value={s.p_email} />
            <ProfileRow label="Address"       value={s.p_res} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" fontWeight={700} color="primary">Health</Typography>
            <Divider sx={{ my: 1 }} />
            <ProfileRow label="Allergies"           value={s.alle} />
            <ProfileRow label="Health Conditions"   value={s.he_con} />
            <ProfileRow label="Emergency Contact"   value={s.e_na} />
            <ProfileRow label="Emergency Phone"     value={s.tele} />
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="overline" fontWeight={700} color="primary">Fee History</Typography>
              <Typography variant="body2" fontWeight={700}>
                Total: {Number(data.totalFees).toLocaleString('en-GH', { minimumFractionDigits: 2 })}
              </Typography>
            </Stack>
            <Divider sx={{ mb: 1 }} />
            {data.fees.length === 0 ? (
              <Typography variant="body2" color="text.disabled">No fee records on file.</Typography>
            ) : (
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Date</TableCell><TableCell>Term</TableCell><TableCell>Year</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Amount</TableCell><TableCell align="right">Arrears</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {data.fees.map(f => (
                    <TableRow key={f.trans_code}>
                      <TableCell>{f.dat}</TableCell>
                      <TableCell>{f.term?.replace('_', ' ')}</TableCell>
                      <TableCell>{f.acc_yer}</TableCell>
                      <TableCell>{f.item}</TableCell>
                      <TableCell align="right">{Number(f.amount || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">{Number(f.arr || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>
      </Grid>
    </>
  );
}

function ProfileRow({ label, value }) {
  return (
    <Stack direction="row" spacing={1} sx={{ py: 0.4 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 130, fontWeight: 700, textTransform: 'uppercase', fontSize: 10 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ flex: 1 }}>{value || '—'}</Typography>
    </Stack>
  );
}

// ── Main page ─────────────────────────────────────────────────────
export default function ReportsPage() {
  const { schoolName, user } = useAuth();
  const [accessible, setAccessible] = useState([]);
  const [picked, setPicked]         = useState(null);   // { key, label, ... }
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [studentChoice, setStudent] = useState(null);   // for individual report
  const [studentOpts, setOpts]      = useState([]);
  const [studentInput, setInput]    = useState('');
  const [finFilters, setFinFilters] = useState({ from: monthAgoStr(), to: todayStr(), status: '', cashier: '' });
  const [accFilters, setAccFilters] = useState({ ...thisMonthRange(), account: '' });
  const [ledFilters, setLedFilters] = useState({
    ...thisMonthRange(),
    // Expenditure-specific
    source: '', category: '', type: '', payee: '', account: '',
    // Income-specific
    scope: '', income_type: '', mode: '', cashier: '', class_name: '',
  });
  const [balFilters, setBalFilters] = useState({ cla: '', term: '', acc_yer: '', accounts: [], combine: false });
  const [balClassList,   setBalClassList]   = useState([]);
  const [balAccountList, setBalAccountList] = useState([]);

  // All-Students Register class filter — pre-loaded from /classes the
  // first time the report is opened, then re-used across visits.
  const [regAllCla, setRegAllCla]       = useState('');
  const [regAllClassList, setRegAllClassList] = useState([]);

  // Fee-Reminders → Send SMS dialog. Opened from the toolbar when the
  // Fee Reminders report is loaded and rows > 0.
  const [smsOpen, setSmsOpen] = useState(false);

  // Daily Collection Balances snapshot filters
  const [snapBalFilters, setSnapBalFilters] = useState({
    income_type: '', class_name: '', q: '', only: '',
  });
  const [snapIncomeTypes, setSnapIncomeTypes] = useState([]); // [{trans, type, cat}]
  const [snapClasses,     setSnapClasses]     = useState([]); // ['Class 1', ...]

  useEffect(() => {
    API.get('/reports').then(r => setAccessible(r.data)).catch(() => setAccessible([]));
  }, []);

  // Student picker for individual report
  useEffect(() => {
    if (picked?.key !== 'reg_individual') return;
    const t = setTimeout(() => {
      API.get('/reports/students/search', { params: { q: studentInput } })
        .then(r => setOpts(r.data)).catch(() => setOpts([]));
    }, 200);
    return () => clearTimeout(t);
  }, [studentInput, picked]);

  const loadReport = async (key, params = {}) => {
    setLoading(true); setError(''); setData(null);
    try {
      const res = await API.get(`/reports/data/${key}`, { params });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load report');
    } finally { setLoading(false); }
  };

  const pick = (r) => {
    setPicked(r); setData(null); setError('');
    setStudent(null); setOpts([]); setInput('');
    if (r.key === 'reg_individual') return;
    if (r.key === 'reg_all_students') {
      // Pre-load the class dropdown once. Initial fetch shows every
      // active student — the user narrows via the Class picker + Apply.
      if (regAllClassList.length === 0) {
        API.get('/classes')
          .then(rc => setRegAllClassList(rc.data || []))
          .catch(() => {});
      }
      setRegAllCla('');
      loadReport(r.key);
      return;
    }
    if (FINANCE_KEYS.has(r.key)) {
      // Default to TODAY when opening any finance report. The user can
      // widen via the preset chips (Today / Yesterday / This week / This
      // month / Last 30 days / This year) or the From / To pickers.
      const fresh = { from: todayStr(), to: todayStr(), status: '', cashier: '' };
      setFinFilters(fresh);
      loadReport(r.key, fresh);
      return;
    }
    if (LEDGER_KEYS.has(r.key)) {
      // Ledger reports default to "this month". The drill-down filters
      // get cleared on every open so we don't carry stale category /
      // payee filters into a different period.
      const fresh = {
        ...thisMonthRange(),
        source: '', category: '', type: '', payee: '', account: '',
        scope: '', income_type: '', mode: '', cashier: '', class_name: '',
      };
      setLedFilters(fresh);
      loadReport(r.key, ledParamsForReq(r.key, fresh));
      return;
    }
    if (ACCOUNTING_KEYS.has(r.key)) {
      // Accounting reports default to "this month" so something is
      // visible immediately. Bank reports also expose an Account picker.
      const fresh = { ...thisMonthRange(), account: '' };
      setAccFilters(fresh);
      loadReport(r.key, fresh);
      return;
    }
    if (r.key === 'fin_student_balances' || r.key === 'fin_fee_reminders') {
      // Student balances report — pre-fetch the class + fee account
      // dropdowns once, then render with no filters initially.
      Promise.all([
        API.get('/classes').catch(() => ({ data: [] })),
        API.get('/account-setup').catch(() => ({ data: [] })),
      ]).then(([clsRes, accRes]) => {
        setBalClassList(clsRes.data || []);
        setBalAccountList(((accRes.data || [])
          .filter(a => a.source === 'stu_add')
          .map(a => a.account)));
      });
      const fresh = { cla: '', term: '', acc_yer: '', accounts: [], min_owing: '', message: '', combine: false };
      setBalFilters(fresh);
      loadReport(r.key, balParamsForReq(fresh));
      return;
    }
    if (SNAPSHOT_BALANCE_KEYS.has(r.key)) {
      // Pre-fetch the income type + class dropdowns and load the full
      // snapshot with no filters first. The user narrows it from there.
      Promise.all([
        API.get('/collections/income-types').catch(() => ({ data: [] })),
        API.get('/classes').catch(() => ({ data: [] })),
      ]).then(([itRes, clsRes]) => {
        setSnapIncomeTypes(itRes.data || []);
        setSnapClasses((clsRes.data || []).map(c => c.class).filter(Boolean));
      });
      const fresh = { income_type: '', class_name: '', q: '', only: '' };
      setSnapBalFilters(fresh);
      loadReport(r.key, fresh);
      return;
    }
    loadReport(r.key);
  };

  // Convert UI ledger filters → request params. Each ledger only
  // honours its own set of drill-down keys (expenditure vs income),
  // so empty fields are stripped before the request goes out.
  const ledParamsForReq = (key, f) => {
    const out = { from: f.from, to: f.to };
    if (key === 'acc_expenditure_ledger') {
      if (f.source)   out.source   = f.source;
      if (f.category) out.category = f.category;
      if (f.type)     out.type     = f.type;
      if (f.payee)    out.payee    = f.payee;
      if (f.account)  out.account  = f.account;
    } else if (key === 'acc_income_ledger') {
      if (f.scope)       out.scope       = f.scope;
      if (f.income_type) out.income_type = f.income_type;
      if (f.mode)        out.mode        = f.mode;
      if (f.cashier)     out.cashier     = f.cashier;
      if (f.class_name)  out.class_name  = f.class_name;
    }
    return out;
  };

  // Convert UI balance filters → request params (joining accounts array)
  const balParamsForReq = (f) => {
    const out = {};
    if (f.cla)     out.cla     = f.cla;
    if (f.term)    out.term    = f.term;
    if (f.acc_yer) out.acc_yer = f.acc_yer;
    if (f.min_owing && Number(f.min_owing) > 0) out.min_owing = f.min_owing;
    if (Array.isArray(f.accounts) && f.accounts.length > 0)
      out.accounts = f.accounts.join(',');
    // Combine flag affects the reminder — backend keeps all accounts on
    // each row when ON so the consolidated total covers every account
    // (matching the Day Payment screen), not just owing ones.
    if (f.combine) out.combine = 1;
    return out;
  };

  const back = () => { setPicked(null); setData(null); setError(''); };

  const handlePrint = () => window.print();

  // Word export — snapshots the rendered print-area, wraps it in an HTML
  // shell with the global PRINT_CSS (so tables and headers keep their
  // styling), and downloads as a .doc that Word/LibreOffice will open
  // cleanly. Works for any report that has a .print-area.
  const handleExportWord = () => {
    if (!data || !picked) return;
    const area = document.querySelector('.print-area');
    if (!area) return;
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" />
      <title>${picked.label}</title>
      <style>
        body { font-family: 'Times New Roman', Georgia, serif; color: #111; padding: 18mm; }
        table { width: 100%; border-collapse: collapse; font-size: 11px;
                font-variant-numeric: tabular-nums; }
        th, td { border: 1px solid #999; padding: 4px 6px; text-align: left; }
        th { background: #eee; }
        ${PRINT_CSS || ''}
      </style></head><body>${area.innerHTML}</body></html>`;
    const blob = new Blob([html], { type: 'application/msword' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    const slug = (picked.label || 'report').toLowerCase().replace(/\s+/g, '-');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `${slug}-${stamp}.doc`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleExport = () => {
    if (!data || !picked) return;
    if (picked.key === 'reg_all_students') {
      downloadCsv('all-students.csv',
        ['Index', 'First Name', 'Last Name', 'Gender', 'Class', 'Status', 'Guardian', 'Mobile'],
        data.rows.map(r => [r.index_no, r.firstname, r.lastname, r.gender, r.calss, r.type, r.p_name, r.mobi || r.p_t_m]));
    } else if (picked.key === 'reg_class_summary') {
      const rows = data.rows.map(r => [r.calss, r.male, r.female, r.total]);
      rows.push(['TOTAL', data.totals.male, data.totals.female, data.totals.total]);
      downloadCsv('class-summary.csv', ['Class', 'Male', 'Female', 'Total'], rows);
    } else if (picked.key === 'reg_by_gender') {
      downloadCsv('gender-distribution.csv',
        ['Gender', 'Count', 'Percentage'],
        data.rows.map(r => [r.gender, r.n, ((Number(r.n) / data.total) * 100).toFixed(1) + '%']));
    } else if (picked.key === 'reg_leavers') {
      downloadCsv('leavers.csv',
        ['Index', 'First Name', 'Last Name', 'Gender', 'Class', 'Date Left', 'Reason'],
        data.rows.map(r => [r.index_no, r.firstname, r.lastname, r.gender, r.calss,
          r.left_at ? new Date(r.left_at).toLocaleDateString('en-GH') : '', r.why]));
    } else if (picked.key === 'fin_student_balances' || picked.key === 'fin_fee_reminders') {
      const accs = data.accounts || [];
      const headers = ['Index', 'Name', 'Class',
        ...accs.flatMap(a => [`${a} · Arrears`, `${a} · To Pay`, `${a} · Paid`, `${a} · Balance`]),
        'Total · Arrears', 'Total · To Pay', 'Total · Paid', 'Total · Balance'];
      const rows = (data.rows || []).map(r => {
        const totArrears = accs.reduce((s, a) => s + Number(r.accounts?.[a]?.arrears || 0), 0);
        const totBilled  = accs.reduce((s, a) => s + Number(r.accounts?.[a]?.billed  || 0), 0);
        const totPaid    = accs.reduce((s, a) => s + Number(r.accounts?.[a]?.paid    || 0), 0);
        const totBalance = accs.reduce((s, a) => s + Number(r.accounts?.[a]?.balance || 0), 0);
        return [
          r.index_no, r.name, r.cla,
          ...accs.flatMap(a => {
            const v = r.accounts?.[a] || { arrears: 0, billed: 0, paid: 0, balance: 0 };
            return [v.arrears, v.billed, v.paid, v.balance];
          }),
          Number(totArrears.toFixed(2)),
          Number(totBilled.toFixed(2)),
          Number(totPaid.toFixed(2)),
          Number(totBalance.toFixed(2)),
        ];
      });
      const f = data.filters || {};
      const tag = f.cla ? f.cla.replace(/\s+/g, '_') : 'all';
      const slug = picked.key === 'fin_fee_reminders' ? 'fee-reminders' : 'student-balances';
      downloadCsv(`${slug}-${tag}.csv`, headers, rows);
    } else if (picked.key === 'fin_general_report') {
      const rows = data.rows.map(r => [
        r.day, r.receipt_no, r.scope_label, r.income_type,
        r.payer_name, r.student_id, r.class_name, r.mode,
        r.received_by_name, r.amount,
      ]);
      rows.push(['', '', '', '', '', '', '', '', 'GRAND TOTAL', data.grandTotal]);
      const tag = data.cashier ? data.cashier.replace(/\s+/g, '_') : 'all';
      downloadCsv(`general-income-${data.from}_to_${data.to}-${tag}.csv`,
        ['Date', 'Receipt', 'Scope', 'Income Type', 'Payer', 'Student ID',
         'Class', 'Mode', 'Cashier', 'Amount'], rows);
    } else if (picked.key === 'fin_daily_income') {
      const rows = data.rows.map(r => [r.day, r.fee_total, r.other_total,
        Number(r.fee_total) + Number(r.other_total), r.receipts, r.cashiers]);
      rows.push(['TOTAL', data.totals.fee_total, data.totals.other_total,
        data.totals.fee_total + data.totals.other_total, data.totals.receipts, '']);
      downloadCsv(`daily-income-${data.from}_to_${data.to}.csv`,
        ['Date', 'Fee Total', 'Other Total', 'Day Total', 'Receipts', 'Cashiers'], rows);
    } else if (picked.key === 'fin_account_summary') {
      const rows = data.rows.map(r => [r.account, r.receipts, r.payers, r.total,
        data.grand ? ((Number(r.total) / data.grand) * 100).toFixed(1) + '%' : '']);
      rows.push(['GRAND TOTAL', '', '', data.grand, '100%']);
      downloadCsv(`income-by-account-${data.from}_to_${data.to}.csv`,
        ['Account', 'Receipts', 'Payers', 'Total', '% of Grand'], rows);
    } else if (picked.key === 'fin_class_other') {
      const rows = data.rows.map(r => [r.dat, r.rece_no, r.payment_type, r.payer_name,
        r.class_name, r.mode, r.received_by_name, r.amount]);
      rows.push(['', '', '', '', '', '', 'TOTAL', data.total]);
      downloadCsv(`class-other-${data.from}_to_${data.to}.csv`,
        ['Date', 'Receipt', 'Type', 'Payer', 'Class', 'Mode', 'Cashier', 'Amount'], rows);
    } else if (picked.key === 'fin_adjustments') {
      const rows = data.rows.map(r => [
        new Date(r.created_at).toLocaleString('en-GH'),
        r.student_id, r.student_name, r.acc_name,
        r.type === 'writeoff' ? 'Write-off' : 'Add Bill',
        r.amount, r.old_balance, r.new_balance, r.reason, r.status,
        r.created_by_name, r.approved_by_name,
      ]);
      downloadCsv(`adjustments-${data.from}_to_${data.to}.csv`,
        ['When', 'Student ID', 'Student', 'Account', 'Type', 'Amount',
         'Old Balance', 'New Balance', 'Reason', 'Status', 'Created By', 'Approved By'], rows);
    } else if (picked.key === 'fin_cashier_summary') {
      const rows = data.rows.map(r => [r.cashier, r.receipts, r.days_active, r.total,
        data.grand ? ((Number(r.total) / data.grand) * 100).toFixed(1) + '%' : '']);
      rows.push(['GRAND TOTAL', '', '', data.grand, '100%']);
      downloadCsv(`cashier-summary-${data.from}_to_${data.to}.csv`,
        ['Cashier', 'Receipts', 'Days Active', 'Total', '% of Grand'], rows);
    } else if (picked.key === 'fin_daily_collection_balances') {
      const t = data.totals || {};
      const dataRows = (data.rows || []).map(r => [
        r.student_id, r.student_name || '', r.class_name || '', r.income_type,
        Number(r.daily_amount) || 0,
        Number(r.arrears) || 0,
        Number(r.credit)  || 0,
        Number(r.pending_payment) || 0,
        r.last_pending_at || '',
      ]);
      dataRows.push([
        '', 'GRAND TOTAL', '', '', '',
        Number(t.arrears) || 0, Number(t.credit) || 0, Number(t.pending) || 0, '',
      ]);
      const stamp = new Date().toISOString().slice(0, 10);
      downloadCsv(`daily-collection-balances-${stamp}.csv`,
        ['Index', 'Student', 'Class', 'Income Type', 'Daily', 'Arrears', 'Credit',
         'Pending Payment', 'Last Pending At'], dataRows);
    } else if (picked.key === 'acc_trial_balance') {
      const rows = (data.rows || []).map(r => [
        r.code, r.account_name, ACCOUNT_TYPE_LABELS[r.account_type] || r.account_type,
        r.dr_total, r.cr_total, r.balance, r.balance_side,
      ]);
      rows.push(['', 'GRAND TOTAL', '', data.grand?.dr, data.grand?.cr,
        data.grand?.difference, '']);
      downloadCsv(`trial-balance-${data.from}_to_${data.to}.csv`,
        ['Code', 'Account', 'Type', 'Debit', 'Credit', 'Balance', 'Side'], rows);
    } else if (picked.key === 'acc_bank_reconciliation') {
      const rows = (data.rows || []).map(r => [
        r.account, r.date, r.description, r.cheque_no || '',
        r.deposit, r.payment, r.balance,
      ]);
      const tag = data.account ? data.account.replace(/\s+/g, '_') : 'all';
      downloadCsv(`bank-reconciliation-${data.from}_to_${data.to}-${tag}.csv`,
        ['Account', 'Date', 'Description', 'Cheque', 'Deposit', 'Payment', 'Balance'], rows);
    } else if (picked.key === 'acc_bank_analysis') {
      const rows = (data.accounts || []).map(a => [
        a.account, a.opening, a.deposits, a.payments, a.net, a.closing,
        a.dep_count, a.pay_count,
      ]);
      const tag = data.account ? data.account.replace(/\s+/g, '_') : 'all';
      downloadCsv(`bank-analysis-${data.from}_to_${data.to}-${tag}.csv`,
        ['Account', 'Opening', 'Deposits', 'Payments', 'Net', 'Closing',
         'Dep Count', 'Pay Count'], rows);
    } else if (picked.key === 'acc_cashbook_statement') {
      const rows = (data.rows || []).map(r => [
        r.account, r.date, r.description, r.voucher_no || '', r.cheque_no || '',
        r.in_amt, r.out_amt, r.balance,
      ]);
      const tag = data.account ? data.account.replace(/\s+/g, '_') : 'all';
      downloadCsv(`cashbook-statement-${data.from}_to_${data.to}-${tag}.csv`,
        ['Account', 'Date', 'Description', 'Voucher', 'Cheque',
         'In', 'Out', 'Balance'], rows);
    } else if (picked.key === 'acc_payments_analysis') {
      const rows = (data.rows || []).map(r => [
        r.date, r.voucher_no, r.account, r.cheque_no || '',
        r.category, r.type, r.payee, r.narration, r.amount,
      ]);
      rows.push(['', '', '', '', '', '', '', 'GRAND TOTAL', data.grand]);
      const tag = data.account ? data.account.replace(/\s+/g, '_') : 'all';
      downloadCsv(`payments-analysis-${data.from}_to_${data.to}-${tag}.csv`,
        ['Date', 'Voucher', 'Account', 'Cheque', 'Category', 'Type',
         'Payee', 'Narration', 'Amount'], rows);
    } else if (picked.key === 'acc_expenditure_ledger') {
      const f = data.filters || {};
      const filterBits = [
        f.source && `source=${f.source}`,
        f.account && `bank=${f.account}`,
        f.category && `cat=${f.category}`,
        f.type && `type=${f.type}`,
        f.payee && `payee=${f.payee}`,
      ].filter(Boolean).join('_').replace(/\s+/g, '-') || 'all';
      const rows = (data.rows || []).map(r => [
        r.date ? new Date(r.date).toISOString().slice(0, 10) : '',
        r.source, r.voucher_no || '', r.account, r.category, r.type,
        r.payee, r.narration || '', r.amount,
      ]);
      rows.push(['', '', '', '', '', '', '', 'GRAND TOTAL', data.grand]);
      downloadCsv(`expenditure-ledger-${data.from}_to_${data.to}-${filterBits}.csv`,
        ['Date', 'Source', 'Voucher', 'Account', 'Category', 'Type',
         'Payee', 'Narration', 'Amount'], rows);
    } else if (picked.key === 'acc_income_ledger') {
      const f = data.filters || {};
      const filterBits = [
        f.scope && `scope=${f.scope}`,
        f.income_type && `inc=${f.income_type}`,
        f.mode && `mode=${f.mode}`,
        f.cashier && `cashier=${f.cashier}`,
        f.class_name && `class=${f.class_name}`,
      ].filter(Boolean).join('_').replace(/\s+/g, '-') || 'all';
      const rows = (data.rows || []).map(r => [
        r.day ? new Date(r.day).toISOString().slice(0, 10) : '',
        r.receipt_no || '', r.scope_label, r.income_type || '',
        r.payer_name || '', r.student_id || '', r.class_name || '',
        r.mode || '', r.received_by_name || '', r.amount,
      ]);
      rows.push(['', '', '', '', '', '', '', '', 'GRAND TOTAL', data.grand]);
      downloadCsv(`income-ledger-${data.from}_to_${data.to}-${filterBits}.csv`,
        ['Date', 'Receipt', 'Scope', 'Income Type', 'Payer', 'Student ID',
         'Class', 'Mode', 'Cashier', 'Amount'], rows);
    } else if (picked.key === 'acc_income_vs_expense') {
      const rows = [
        ['== INCOME =='],
        ...(data.income || []).map(r => [r.label, r.n, r.total]),
        ['Total Income', '', data.totals?.income],
        [],
        ['== EXPENDITURE =='],
        ...(data.expenses || []).map(r => [r.label, r.n, r.total]),
        ['Total Expenditure', '', data.totals?.expense],
        [],
        ['NET RESULT', '', data.totals?.net],
      ];
      downloadCsv(`income-vs-expenditure-${data.from}_to_${data.to}.csv`,
        ['Category', '#', 'Total'], rows);
    } else if (picked.key === 'acc_petty_statement') {
      const rows = (data.rows || []).map(r => [
        r.date, r.description, r.voucher_no || '', r.category, r.type,
        r.request_holder, r.in_amt, r.out_amt, r.balance,
      ]);
      downloadCsv(`petty-statement-${data.from}_to_${data.to}.csv`,
        ['Date', 'Description', 'Voucher', 'Category', 'Type',
         'Requester', 'In', 'Out', 'Balance'], rows);
    } else if (picked.key === 'acc_petty_analysis') {
      const rows = [
        ['== BY CATEGORY =='],
        ...(data.by_category || []).map(r => [r.label, r.n, r.total]),
        [],
        ['== BY TYPE =='],
        ...(data.by_type || []).map(r => [r.label, r.n, r.total]),
        [],
        ['== BY REQUESTER =='],
        ...(data.by_requester || []).map(r => [r.label, r.n, r.total]),
        [],
        ['== BY MONTH =='],
        ...(data.by_month || []).map(r => [r.label, r.n, r.total]),
      ];
      downloadCsv(`petty-analysis-${data.from}_to_${data.to}.csv`,
        ['Label', '#', 'Total'], rows);
    } else if (picked.key === 'acc_receipts_payments_classic') {
      const rows = [
        ['== BROUGHT FORWARD =='],
        ['Cash b/fwd', '', data.bf?.cash + (data.bf?.petty || 0)],
        ['Bank b/fwd', '', data.bf?.bank],
        ['Total Cash & Bank b/fwd', '', '', data.bf?.total],
        [],
        ['== RECEIPTS =='],
        ...(data.receipts?.by_type || []).map(r => [`  ${r.type}`, r.total]),
        ['TOTAL RECEIPTS', data.receipts?.total],
        ['Receipts Grand Total', '', '', data.receipts_grand],
        [],
        ['== LESS PAYMENT =='],
        ...(data.payments?.by_category || []).flatMap(c => [
          [`Category: ${c.category}`],
          ...(c.narrations || []).map(n => [`  ${n.narration}`, n.total]),
          [`  Subtotal — ${c.category}`, '', c.total],
        ]),
        ['TOTAL PAYMENT', '', '', data.payments?.total],
        [],
        ['== CLOSING CASH BAL =='],
        ['  Cashbook', data.closing?.cashbook],
        ['  Petty Cash', data.closing?.petty],
        ['  Total Cash-In-Hand', '', data.closing?.cash_in_hand],
        ...(data.closing?.banks || []).map(b => [`  ${b.account}`, b.balance]),
        ['  Total Bank', '', data.closing?.bank_total],
        ['Payments Grand Total', '', '', data.payments_grand],
      ];
      downloadCsv(`receipts-payments-classic-${data.from}_to_${data.to}.csv`,
        ['Particulars', 'Line', 'Subtotal', 'Total'], rows);
    } else if (picked.key === 'acc_cash_analysis') {
      const t = data.totals || {};
      const rows = [
        ['Cash Bal. B/F', data.cash_bf, '', '', '', '', '', ''],
        ['Cash Receipt / Payment', data.cash_receipt, '', '', '', data.cash_payment, '', ''],
        ...(data.banks || []).map(b => [
          `Bank Bal. B/f - ${b.account}`, '', b.bank_bf, b.deposits, b.bank_payment, '', '', '',
        ]),
        ['Totals', t.cash_total_in, t.bank_bf, t.deposits, t.bank_payment, t.cash_payment, t.closing_cash, t.closing_bank],
      ];
      downloadCsv(`cash-analysis-${(data.mon_yer || '').replace(/\//g, '-')}.csv`,
        ['Details', 'Cash', 'Bank b/f', 'Deposits', 'Bank Payment',
         'Cash Payment', 'Closing Bal. Cash', 'Closing Bal. Bank'], rows);
    } else if (picked.key === 'acc_cashbook_classic') {
      const t = data.totals || {};
      const rows = [
        ['== RECEIPTS =='],
        ['Date', 'Description', 'Cash', 'Bank'],
        ...(data.receipts || []).map(r => [r.date, r.description, r.cash || '', r.bank || '']),
        ['', 'Total', t.cash_total, t.bank_total],
        [],
        ['== PAYMENT =='],
        ['Date', 'Description', 'Cheque No', 'Type', 'Amount'],
        ...(data.payments || []).map(p => [p.date, p.description, p.cheque_no, p.exp_type, p.amount || '']),
        ['', 'Total', '', '', t.payment_total],
        [],
        ['== CASH SUMMARY =='],
        ['+ Balance B/F',         data.cash_summary?.bf],
        ['+ Total Receipts',      data.cash_summary?.receipts],
        ['- Total Bank Deposit',  data.cash_summary?.bank_deposit],
        ['- Total Cash Payment',  data.cash_summary?.cash_payment],
        ['= Balance C/fwd',       data.cash_summary?.cfwd],
        [],
        ['== BANK SUMMARY =='],
        ['+ Balance B/F',     data.bank_summary?.bf],
        ['+ Total Deposit',   data.bank_summary?.deposit],
        ['- Cheque Payment',  data.bank_summary?.cheque_payment],
        ['= Balance C/fwd',   data.bank_summary?.cfwd],
      ];
      downloadCsv(`cashbook-${(data.mon_yer || '').replace(/\//g, '-')}.csv`,
        ['', '', '', '', ''], rows);
    } else if (picked.key === 'acc_receipts_payments') {
      const t = data.totals || {};
      const rows = [
        ['== RECEIPTS =='],
        ['To Balance b/d', '', '', ''],
        ...(data.opening?.accounts || []).map(a => [
          `  ${a.account}`, '',
          a.is_cash ? a.balance : '',
          !a.is_cash ? a.balance : '',
        ]),
        ['  Subtotal — Opening', '', data.opening?.cash, data.opening?.bank],
        [],
        ...(data.receipts || []).flatMap(r => [
          [`To ${r.type}`, r.discount_total || '', r.cash_total || '', r.bank_total || '']
        ]),
        ...(data.cash_to_bank_transfer > 0
          ? [['To Cash Deposited (from cashbox)', '', '', data.cash_to_bank_transfer]]
          : []),
        ['TOTAL — Receipts', '', t.receipts_cash, t.receipts_bank],
        [],
        ['== PAYMENTS =='],
        ...(data.payments_by_category || []).flatMap(c => [
          [`By ${c.category}`, '', '', ''],
          ...(c.types || []).map(tt => [
            `  ${tt.type}`, tt.discount_total || '', tt.cash_total || '', tt.bank_total || '',
          ]),
          [`  Subtotal — ${c.category}`, '', c.cash_total, c.bank_total],
        ]),
        ...(data.cash_to_bank_transfer > 0
          ? [['By Cash Deposited (to bank)', '', data.cash_to_bank_transfer, '']]
          : []),
        ['By Balance c/d', '', '', ''],
        ...(data.closing?.accounts || []).map(a => [
          `  ${a.account}`, '',
          a.is_cash ? a.balance : '',
          !a.is_cash ? a.balance : '',
        ]),
        ['  Subtotal — Closing', '', data.closing?.cash, data.closing?.bank],
        ['TOTAL — Payments', '', t.payments_cash, t.payments_bank],
      ];
      downloadCsv(`receipts-payments-${data.from}_to_${data.to}.csv`,
        ['Particulars', 'Discount', 'Cash', 'Bank'], rows);
    }
  };

  const renderReport = () => {
    if (!picked) return null;

    if (picked.key === 'reg_individual') {
      return (
        <Card sx={{ mb: 2 }} className="no-print">
          <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Autocomplete sx={{ minWidth: 380, flex: 1 }} size="small"
              options={studentOpts}
              value={studentChoice}
              onChange={(_, v) => {
                setStudent(v);
                if (v) loadReport('reg_individual', { index_no: v.index_no });
              }}
              onInputChange={(_, v) => setInput(v)}
              getOptionLabel={(o) => o ? `${o.firstname} ${o.lastname} · ${o.index_no}` : ''}
              isOptionEqualToValue={(a, b) => a.index_no === b.index_no}
              renderInput={(params) => (
                <TextField {...params} label="Search student by name or index" autoFocus />
              )}
              renderOption={(props, o) => (
                <li {...props} key={o.index_no}>
                  <Stack>
                    <Typography variant="body2" fontWeight={600}>{o.firstname} {o.lastname}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {o.index_no} {o.calss && `· ${o.calss}`}
                    </Typography>
                  </Stack>
                </li>
              )}
            />
          </Box>
        </Card>
      );
    }
    if (FINANCE_KEYS.has(picked.key)) {
      return (
        <FinanceFilters keyName={picked.key} value={finFilters} onChange={setFinFilters}
          onApply={(next) => loadReport(picked.key, next || finFilters)}
          loading={loading}
          cashiers={data?.cashiers || []} />
      );
    }
    if (LEDGER_KEYS.has(picked.key)) {
      return (
        <LedgerFilters keyName={picked.key} value={ledFilters} onChange={setLedFilters}
          onApply={(next) => loadReport(picked.key, ledParamsForReq(picked.key, next || ledFilters))}
          loading={loading}
          distinct={data?.distinct || {}} />
      );
    }
    if (ACCOUNTING_KEYS.has(picked.key)) {
      return (
        <AccountingFilters keyName={picked.key} value={accFilters} onChange={setAccFilters}
          onApply={(next) => loadReport(picked.key, next || accFilters)}
          loading={loading}
          banks={data?.banks || []} />
      );
    }
    if (picked.key === 'fin_student_balances' || picked.key === 'fin_fee_reminders') {
      return (
        <BalancesFilters keyName={picked.key}
          value={balFilters} onChange={setBalFilters}
          onApply={(next) => loadReport(picked.key, balParamsForReq(next || balFilters))}
          loading={loading}
          classes={balClassList}
          accounts={balAccountList} />
      );
    }
    if (SNAPSHOT_BALANCE_KEYS.has(picked.key)) {
      return (
        <SnapshotBalancesFilters
          value={snapBalFilters} onChange={setSnapBalFilters}
          onApply={(next) => loadReport(picked.key, next || snapBalFilters)}
          loading={loading}
          incomeTypes={snapIncomeTypes}
          classes={snapClasses} />
      );
    }
    return null;
  };

  return (
    <Box sx={{ maxWidth: 1320, mx: 'auto' }}>
      <style>{PRINT_CSS}</style>

      <Box>
        <Box className="no-print">
          <ModuleHeader icon={<ReportsIcon sx={{ fontSize: 28 }} />}
            title={picked ? picked.label : 'Reports'}
            subtitle={picked ? picked.desc : 'View, print and export reports you have been granted access to'}
            actionLabel={picked ? 'Back to Reports' : null}
            actionIcon={<BackIcon />}
            onAction={picked ? back : undefined} />
        </Box>

        {!picked && <ReportBrowser accessible={accessible} onPick={pick} />}

        {picked && (
          <>
            {renderReport()}

            {!data && !loading && !error && picked.key === 'reg_individual' && (
              <Card sx={{ p: 6, textAlign: 'center' }} className="no-print">
                <SearchIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.disabled">Pick a student above to load their report</Typography>
              </Card>
            )}

            {/* All-Students Register — class filter strip. Lives outside
                the loading/data guard so it's visible while the report is
                fetching too. */}
            {picked && picked.key === 'reg_all_students' && (
              <Card sx={{ mb: 2 }} className="no-print">
                <CardContent sx={{ pb: '16px !important' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.2 }}>
                    <CategoryIcon color="primary" fontSize="small" />
                    <Typography variant="overline" color="primary" fontWeight={700}>Filters</Typography>
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}
                    alignItems={{ sm: 'center' }} flexWrap="wrap" rowGap={1.5}>
                    <TextField select size="small" label="Class" value={regAllCla}
                      onChange={(e) => setRegAllCla(e.target.value)}
                      sx={{ minWidth: 220 }}>
                      <MenuItem value="">All classes</MenuItem>
                      {regAllClassList.map(c => (
                        <MenuItem key={c.trans_code || c.class} value={c.class}>{c.class}</MenuItem>
                      ))}
                    </TextField>
                    <Button variant="contained" onClick={() => loadReport('reg_all_students', regAllCla ? { cla: regAllCla } : {})}
                      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                      sx={{ textTransform: 'none', fontWeight: 800, minWidth: 110 }}>
                      Apply
                    </Button>
                    {regAllCla && (
                      <Button onClick={() => { setRegAllCla(''); loadReport('reg_all_students'); }}
                        sx={{ textTransform: 'none', fontWeight: 700 }}>
                        Clear
                      </Button>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {loading && <Card sx={{ p: 8, textAlign: 'center' }} className="no-print"><CircularProgress /></Card>}
            {error && <Alert severity="error" sx={{ mb: 2 }} className="no-print" onClose={() => setError('')}>{error}</Alert>}

            {data && (
              <>
                <Card sx={{ mb: 2, p: 1.2 }} className="no-print">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Tooltip title="Export as CSV">
                      <span>
                        <Button startIcon={<DownloadIcon />} onClick={handleExport}
                          disabled={picked.key === 'reg_individual'}>
                          Export CSV
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title="Export as Word document">
                      <span>
                        <Button startIcon={<DownloadIcon />} onClick={handleExportWord}
                          disabled={picked.key === 'reg_individual'}>
                          Export Word
                        </Button>
                      </span>
                    </Tooltip>
                    {picked.key === 'fin_fee_reminders' && (data?.rows?.length > 0) && (
                      <Tooltip title="Send SMS reminders to parents / guardians">
                        <Button startIcon={<SmsIcon />} onClick={() => setSmsOpen(true)}
                          sx={{ fontWeight: 700 }}>
                          Send SMS
                        </Button>
                      </Tooltip>
                    )}
                    <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>
                      Print
                    </Button>
                  </Stack>
                </Card>

                {picked.key === 'fin_fee_reminders' && (
                  <FeeReminderSmsDialog
                    open={smsOpen}
                    onClose={() => setSmsOpen(false)}
                    rows={data?.rows || []}
                    schoolName={schoolName}
                    defaultMessage={balFilters.message}
                    combine={balFilters.combine}
                  />
                )}

                <Card className="print-area" sx={{ p: { xs: 2, md: 3 } }}>
                  {picked.key === 'reg_all_students'   && <AllStudentsReport   data={data} schoolName={schoolName} />}
                  {picked.key === 'reg_class_summary'  && <ClassSummaryReport  data={data} schoolName={schoolName} />}
                  {picked.key === 'reg_by_gender'      && <GenderReport        data={data} schoolName={schoolName} />}
                  {picked.key === 'reg_leavers'        && <LeaversReport       data={data} schoolName={schoolName} />}
                  {picked.key === 'reg_individual'     && <IndividualReport    data={data} schoolName={schoolName} />}
                  {picked.key === 'fin_student_balances' && <StudentBalancesReport data={data} schoolName={schoolName} />}
                  {picked.key === 'fin_fee_reminders'    && <FeeRemindersReport    data={data} schoolName={schoolName} customMessage={balFilters.message} combine={balFilters.combine} />}
                  {picked.key === 'fin_general_report'  && <GeneralReport       data={data} schoolName={schoolName} />}
                  {picked.key === 'fin_daily_income'    && <DailyIncomeReport   data={data} schoolName={schoolName} />}
                  {picked.key === 'fin_account_summary' && <AccountSummaryReport data={data} schoolName={schoolName} />}
                  {picked.key === 'fin_class_other'     && <ClassOtherReport    data={data} schoolName={schoolName} />}
                  {picked.key === 'fin_adjustments'     && <AdjustmentsReport   data={data} schoolName={schoolName} />}
                  {picked.key === 'fin_cashier_summary' && <CashierSummaryReport data={data} schoolName={schoolName} />}
                  {picked.key === 'fin_daily_collection_balances' && <DailyCollectionBalancesReport data={data} schoolName={schoolName} />}
                  {picked.key === 'acc_trial_balance'        && <TrialBalanceReport       data={data} schoolName={schoolName} />}
                  {picked.key === 'acc_bank_reconciliation'  && <BankReconciliationReport data={data} schoolName={schoolName} />}
                  {picked.key === 'acc_bank_analysis'        && <BankAnalysisReport       data={data} schoolName={schoolName} />}
                  {picked.key === 'acc_cashbook_statement'   && <CashbookStatementReport  data={data} schoolName={schoolName} />}
                  {picked.key === 'acc_payments_analysis'    && <PaymentsAnalysisReport   data={data} schoolName={schoolName} />}
                  {picked.key === 'acc_income_vs_expense'    && <IncomeVsExpenseReport    data={data} schoolName={schoolName} />}
                  {picked.key === 'acc_expenditure_ledger'   && <ExpenditureLedgerReport  data={data} schoolName={schoolName} />}
                  {picked.key === 'acc_income_ledger'        && <IncomeLedgerReport       data={data} schoolName={schoolName} />}
                  {picked.key === 'acc_petty_statement'      && <PettyStatementReport     data={data} schoolName={schoolName} />}
                  {picked.key === 'acc_petty_analysis'       && <PettyAnalysisReport      data={data} schoolName={schoolName} />}
                  {picked.key === 'acc_receipts_payments'    && <ReceiptsAndPaymentsReport data={data} schoolName={schoolName} />}
                  {picked.key === 'acc_cashbook_classic'     && <ClassicCashbookReport     data={data} schoolName={schoolName} />}
                  {picked.key === 'acc_cash_analysis'        && <CashAnalysisReport        data={data} schoolName={schoolName} />}
                  {picked.key === 'acc_receipts_payments_classic' && <ReceiptsPaymentsClassicReport data={data} schoolName={schoolName} />}
                  {picked.key === 'hr_employee_register' && <HrEmployeeRegisterReport data={data} schoolName={schoolName} />}
                  {picked.key === 'hr_payroll_summary'   && <HrPayrollSummaryReport   data={data} schoolName={schoolName} />}
                  {picked.key === 'hr_payslip_history'   && <HrPayslipHistoryReport   data={data} schoolName={schoolName} />}
                  {picked.key === 'hr_ssnit_remit'       && <HrMonthSchedule data={data} schoolName={schoolName} variant="ssnit"    title="SSNIT Remittance Schedule" />}
                  {picked.key === 'hr_paye_remit'        && <HrMonthSchedule data={data} schoolName={schoolName} variant="paye"     title="PAYE Remittance Schedule" />}
                  {picked.key === 'hr_payroll_register'  && <HrMonthSchedule data={data} schoolName={schoolName} variant="register" title="Payroll Register" />}
                  {picked.key === 'hr_payslips_browse'   && <HrPayslipsBrowse data={data} schoolName={schoolName} />}
                </Card>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────────
// HR Reports — three printable renderers wired through /reports/data
// ──────────────────────────────────────────────────────────────────
const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmtGHS = (n) => Number(n || 0).toLocaleString('en-GH',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function HrEmployeeRegisterReport({ data, schoolName }) {
  const [filters, setFilters] = useState(data?.filters || { department: '', employment_type: '', status: 'active' });
  const [rows, setRows]       = useState(data?.rows   || []);
  const [totals, setTotals]   = useState(data?.totals || { headcount: 0, basic: 0, allowances: 0, gross: 0 });
  const [loading, setLoading] = useState(false);

  const depts = useMemo(() => {
    const s = new Set();
    rows.forEach(r => r.department && s.add(r.department));
    return [...s].sort();
  }, [rows]);

  const refetch = async (next) => {
    setLoading(true);
    try {
      const r = await API.get('/reports/data/hr_employee_register', { params: next });
      setRows(r.data.rows || []);
      setTotals(r.data.totals || { headcount: 0, basic: 0, allowances: 0, gross: 0 });
      setFilters(r.data.filters || next);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  return (
    <>
      <PrintHeader schoolName={schoolName} title="HR Employee Register"
        subtitle={`${totals.headcount} employee(s)${filters.department ? ` · ${filters.department}` : ''}${filters.employment_type ? ` · ${filters.employment_type}` : ''}`} />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} className="no-print"
        sx={{ mb: 1.8 }}>
        <TextField select size="small" label="Department" value={filters.department}
          onChange={(e) => refetch({ ...filters, department: e.target.value })}
          sx={{ minWidth: 180 }}>
          <MenuItem value=""><em>All</em></MenuItem>
          {depts.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Type" value={filters.employment_type}
          onChange={(e) => refetch({ ...filters, employment_type: e.target.value })}
          sx={{ minWidth: 160 }}>
          <MenuItem value=""><em>All</em></MenuItem>
          {['Full-time', 'Part-time', 'Contract', 'Locum', 'Intern'].map(t =>
            <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Status" value={filters.status}
          onChange={(e) => refetch({ ...filters, status: e.target.value })}
          sx={{ minWidth: 150 }}>
          <MenuItem value="active">Active only</MenuItem>
          <MenuItem value="inactive">Inactive only</MenuItem>
          <MenuItem value="all">All</MenuItem>
        </TextField>
        {loading && <CircularProgress size={22} sx={{ alignSelf: 'center' }} />}
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead><TableRow sx={{ '& th': { fontWeight: 800, fontSize: 10.5, textTransform: 'uppercase', bgcolor: '#f7f9fc' } }}>
            <TableCell sx={{ width: 36 }}>#</TableCell>
            <TableCell>Staff ID</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Department</TableCell>
            <TableCell>Position</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Hire date</TableCell>
            <TableCell align="right">Basic</TableCell>
            <TableCell align="right">Allowances</TableCell>
            <TableCell align="right">Gross</TableCell>
            <TableCell>Bank · Account</TableCell>
            <TableCell>SSNIT</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={12} align="center"
                sx={{ py: 4, color: 'text.disabled', fontStyle: 'italic' }}>
                No employees match these filters.
              </TableCell></TableRow>
            )}
            {rows.map((r, i) => {
              const gross = Number(r.basic_salary || 0) + Number(r.allowances || 0);
              return (
                <TableRow key={r.id} hover sx={!r.is_active ? { '& td': { color: 'text.disabled' } } : null}>
                  <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                  <TableCell sx={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>{r.staff_id || '—'}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{r.full_name}</TableCell>
                  <TableCell>{r.department || '—'}</TableCell>
                  <TableCell>{r.position || '—'}</TableCell>
                  <TableCell>{r.employment_type || '—'}</TableCell>
                  <TableCell>{r.hire_date ? String(r.hire_date).slice(0, 10) : '—'}</TableCell>
                  <TableCell align="right">{fmtGHS(r.basic_salary)}</TableCell>
                  <TableCell align="right">{fmtGHS(r.allowances)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtGHS(gross)}</TableCell>
                  <TableCell>{r.bank_name ? `${r.bank_name} · ${r.bank_account_no || '—'}` : '—'}</TableCell>
                  <TableCell>{r.ssnit_no || '—'}</TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={{ '& td': { borderTop: 2, borderColor: 'divider', fontWeight: 800, bgcolor: '#f7f9fc' } }}>
              <TableCell colSpan={7} align="right">TOTALS ({totals.headcount})</TableCell>
              <TableCell align="right">{fmtGHS(totals.basic)}</TableCell>
              <TableCell align="right">{fmtGHS(totals.allowances)}</TableCell>
              <TableCell align="right">{fmtGHS(totals.gross)}</TableCell>
              <TableCell colSpan={2} />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

function HrPayrollSummaryReport({ data, schoolName }) {
  const T = data?.totals || { headcount: 0, basic: 0, allowances: 0, gross: 0 };
  return (
    <>
      <PrintHeader schoolName={schoolName} title="Payroll Summary"
        subtitle={`${T.headcount} active employee(s) · monthly gross GHS ${fmtGHS(T.gross)}`} />

      <Typography fontWeight={800} sx={{ mt: 1, mb: 1 }}>By Department</Typography>
      <TableContainer sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead><TableRow sx={{ '& th': { fontWeight: 800, fontSize: 10.5, textTransform: 'uppercase', bgcolor: '#f7f9fc' } }}>
            <TableCell>Department</TableCell>
            <TableCell align="right">Headcount</TableCell>
            <TableCell align="right">Basic</TableCell>
            <TableCell align="right">Allowances</TableCell>
            <TableCell align="right">Gross</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {(data?.by_department || []).map(r => (
              <TableRow key={r.department} hover>
                <TableCell sx={{ fontWeight: 700 }}>{r.department}</TableCell>
                <TableCell align="right">{r.headcount}</TableCell>
                <TableCell align="right">{fmtGHS(r.basic)}</TableCell>
                <TableCell align="right">{fmtGHS(r.allowances)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtGHS(r.gross)}</TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ '& td': { borderTop: 2, borderColor: 'divider', fontWeight: 800, bgcolor: '#f7f9fc' } }}>
              <TableCell>TOTAL</TableCell>
              <TableCell align="right">{T.headcount}</TableCell>
              <TableCell align="right">{fmtGHS(T.basic)}</TableCell>
              <TableCell align="right">{fmtGHS(T.allowances)}</TableCell>
              <TableCell align="right">{fmtGHS(T.gross)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Typography fontWeight={800} sx={{ mt: 1, mb: 1 }}>By Employment Type</Typography>
      <TableContainer>
        <Table size="small">
          <TableHead><TableRow sx={{ '& th': { fontWeight: 800, fontSize: 10.5, textTransform: 'uppercase', bgcolor: '#f7f9fc' } }}>
            <TableCell>Type</TableCell>
            <TableCell align="right">Headcount</TableCell>
            <TableCell align="right">Basic</TableCell>
            <TableCell align="right">Allowances</TableCell>
            <TableCell align="right">Gross</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {(data?.by_type || []).map(r => (
              <TableRow key={r.employment_type} hover>
                <TableCell sx={{ fontWeight: 700 }}>{r.employment_type}</TableCell>
                <TableCell align="right">{r.headcount}</TableCell>
                <TableCell align="right">{fmtGHS(r.basic)}</TableCell>
                <TableCell align="right">{fmtGHS(r.allowances)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtGHS(r.gross)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

function HrPayslipHistoryReport({ data, schoolName }) {
  const [employees, setEmployees] = useState([]);
  const [picked, setPicked]       = useState(null);
  const [d, setD]                 = useState(data || { employee: null, payslips: [], totals: { gross: 0, deductions: 0, net: 0 } });
  const [loading, setLoading]     = useState(false);

  useEffect(() => {
    API.get('/reports/data/hr_payslip_history', { params: { mode: 'employees' } })
      .then(r => setEmployees(r.data.employees || []))
      .catch(() => setEmployees([]));
  }, []);

  const load = async (emp) => {
    setPicked(emp);
    if (!emp) {
      setD({ employee: null, payslips: [], totals: { gross: 0, deductions: 0, net: 0 } });
      return;
    }
    setLoading(true);
    try {
      const r = await API.get('/reports/data/hr_payslip_history', { params: { employee_id: emp.id } });
      setD(r.data);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  return (
    <>
      <PrintHeader schoolName={schoolName} title="Payslip History"
        subtitle={d?.employee
          ? `${d.employee.full_name} · ${d.employee.department || '—'} · ${d.payslips.length} payslip(s)`
          : 'Pick an employee'} />

      <Box className="no-print" sx={{ mb: 2 }}>
        <Autocomplete options={employees} value={picked}
          getOptionLabel={(e) => e ? `${e.full_name}${e.department ? ` · ${e.department}` : ''}` : ''}
          isOptionEqualToValue={(a, b) => a?.id === b?.id}
          onChange={(_, v) => load(v)} size="small"
          renderInput={(p) => <TextField {...p} fullWidth size="small" label="Employee *"
            helperText="Pick an employee to load their payslip trail" />} />
      </Box>

      {loading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>}

      {!loading && d?.employee && (
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow sx={{ '& th': { fontWeight: 800, fontSize: 10.5, textTransform: 'uppercase', bgcolor: '#f7f9fc' } }}>
              <TableCell>Period</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Basic</TableCell>
              <TableCell align="right">Allowances</TableCell>
              <TableCell align="right">Gross</TableCell>
              <TableCell align="right">SSNIT</TableCell>
              <TableCell align="right">PAYE</TableCell>
              <TableCell align="right">Deductions</TableCell>
              <TableCell align="right">Net</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {d.payslips.length === 0 && (
                <TableRow><TableCell colSpan={9} align="center"
                  sx={{ py: 4, color: 'text.disabled', fontStyle: 'italic' }}>
                  No payslips yet for this employee.
                </TableCell></TableRow>
              )}
              {d.payslips.map(p => (
                <TableRow key={p.id} hover>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {MONTH_ABBR[(p.period_month || 1) - 1]} {p.period_year}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={p.run_status}
                      color={p.run_status === 'paid' ? 'success' : p.run_status === 'approved' ? 'info' : 'default'}
                      sx={{ fontWeight: 700, fontSize: 10.5, height: 20 }} />
                  </TableCell>
                  <TableCell align="right">{fmtGHS(p.basic)}</TableCell>
                  <TableCell align="right">{fmtGHS(p.allowances)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtGHS(p.gross)}</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}>{fmtGHS(p.ssnit_employee)}</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}>{fmtGHS(p.paye)}</TableCell>
                  <TableCell align="right">{fmtGHS(p.deduction_total)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, color: 'success.main' }}>{fmtGHS(p.net)}</TableCell>
                </TableRow>
              ))}
              {d.payslips.length > 0 && (
                <TableRow sx={{ '& td': { borderTop: 2, borderColor: 'divider', fontWeight: 800, bgcolor: '#f7f9fc' } }}>
                  <TableCell colSpan={4} align="right">TOTALS ({d.payslips.length} payslip(s))</TableCell>
                  <TableCell align="right">{fmtGHS(d.totals.gross)}</TableCell>
                  <TableCell colSpan={2} />
                  <TableCell align="right">{fmtGHS(d.totals.deductions)}</TableCell>
                  <TableCell align="right" sx={{ color: 'success.main' }}>{fmtGHS(d.totals.net)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}

// ──────────────────────────────────────────────────────────────────
// HrMonthSchedule — shared renderer for the 3 monthly HR schedules
// (SSNIT remittance · PAYE remittance · Payroll register). The
// `variant` prop swaps the column set.
// ──────────────────────────────────────────────────────────────────
function HrMonthSchedule({ data, schoolName, variant, title }) {
  const today = new Date();
  const [year, setYear]   = useState(data?.filters?.year  || today.getFullYear());
  const [month, setMonth] = useState(data?.filters?.month || (today.getMonth() + 1));
  const [d, setD]         = useState(data || { run: null, payslips: [], totals: null });
  const [loading, setLoading] = useState(false);

  const refetch = async (y, m) => {
    setLoading(true);
    try {
      const r = await API.get(`/reports/data/${dispatchKey(variant)}`,
        { params: { year: y, month: m } });
      setD(r.data);
    } finally { setLoading(false); }
  };

  const onMonth = (m) => { setMonth(m); refetch(year, m); };
  const onYear  = (y) => { setYear(y);  refetch(y, month); };

  const subtitle = d.run
    ? `${MONTH_ABBR[(d.filters.month||1)-1]} ${d.filters.year} · ${d.totals?.headcount || 0} employee(s) · status ${d.run.status}`
    : `${MONTH_ABBR[(month||1)-1]} ${year} — no payroll run found for this period`;

  return (
    <>
      <PrintHeader schoolName={schoolName} title={title} subtitle={subtitle} />

      {/* Period picker — hidden in print */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} className="no-print"
        sx={{ mb: 1.8 }}>
        <TextField select size="small" label="Year" value={year}
          onChange={(e) => onYear(Number(e.target.value))} sx={{ minWidth: 130 }}>
          {[1,0,-1,-2].map(off => {
            const y = today.getFullYear() + off;
            return <MenuItem key={y} value={y}>{y}</MenuItem>;
          })}
        </TextField>
        <TextField select size="small" label="Month" value={month}
          onChange={(e) => onMonth(Number(e.target.value))} sx={{ minWidth: 170 }}>
          {['January','February','March','April','May','June',
            'July','August','September','October','November','December'].map((m, i) =>
            <MenuItem key={i+1} value={i+1}>{m}</MenuItem>
          )}
        </TextField>
        {loading && <CircularProgress size={22} sx={{ alignSelf: 'center' }} />}
      </Stack>

      {!d.run && (
        <Alert severity="info" variant="outlined">
          No payroll run exists for <b>{MONTH_ABBR[(month||1)-1]} {year}</b>.
          Create one under <b>HR → Payroll → New payroll run</b> first.
        </Alert>
      )}

      {d.run && variant === 'ssnit' && <HrSsnitTable d={d} />}
      {d.run && variant === 'paye'  && <HrPayeTable  d={d} />}
      {d.run && variant === 'register' && <HrRegisterTable d={d} />}
    </>
  );
}

function dispatchKey(variant) {
  if (variant === 'ssnit')    return 'hr_ssnit_remit';
  if (variant === 'paye')     return 'hr_paye_remit';
  return 'hr_payroll_register';
}

function HrSsnitTable({ d }) {
  const T = d.totals;
  // Only contributors — rows where SSNIT was actually deducted.
  const rows = d.payslips.filter(p => Number(p.ssnit_employee) > 0);
  const sumEmp     = rows.reduce((a, p) => a + Number(p.ssnit_employee || 0), 0);
  const sumEr      = rows.reduce((a, p) => a + Number(p.ssnit_employer || 0), 0);
  const sumBasic   = rows.reduce((a, p) => a + Number(p.basic || 0), 0);
  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead><TableRow sx={{ '& th': { fontWeight: 800, fontSize: 10.5, textTransform: 'uppercase', bgcolor: '#f7f9fc' } }}>
            <TableCell sx={{ width: 36 }}>#</TableCell>
            <TableCell>Employee</TableCell>
            <TableCell>SSNIT number</TableCell>
            <TableCell>Department</TableCell>
            <TableCell align="right">Basic (pensionable)</TableCell>
            <TableCell align="right">5.5% Employee</TableCell>
            <TableCell align="right">13% Employer</TableCell>
            <TableCell align="right">Total</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center"
                sx={{ py: 4, color: 'text.disabled', fontStyle: 'italic' }}>
                No SSNIT contributors on this run.
              </TableCell></TableRow>
            )}
            {rows.map((p, i) => (
              <TableRow key={p.id} hover>
                <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{p.employee_name}</TableCell>
                <TableCell sx={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>{p.ssnit_number || '—'}</TableCell>
                <TableCell>{p.department || '—'}</TableCell>
                <TableCell align="right">{fmtGHS(p.basic)}</TableCell>
                <TableCell align="right">{fmtGHS(p.ssnit_employee)}</TableCell>
                <TableCell align="right">{fmtGHS(p.ssnit_employer)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {fmtGHS(Number(p.ssnit_employee || 0) + Number(p.ssnit_employer || 0))}
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ '& td': { borderTop: 2, borderColor: 'divider', fontWeight: 800, bgcolor: '#f7f9fc' } }}>
              <TableCell colSpan={4} align="right">TOTALS ({rows.length})</TableCell>
              <TableCell align="right">{fmtGHS(sumBasic)}</TableCell>
              <TableCell align="right">{fmtGHS(sumEmp)}</TableCell>
              <TableCell align="right">{fmtGHS(sumEr)}</TableCell>
              <TableCell align="right" sx={{ color: 'success.dark' }}>{fmtGHS(sumEmp + sumEr)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ mt: 3, p: 2, borderRadius: 1.5,
        border: '2px solid', borderColor: 'success.main',
        bgcolor: 'rgba(46,125,50,0.05)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontWeight: 900, fontSize: 14, color: 'success.dark' }}>
          TOTAL PAYABLE TO SSNIT
        </Typography>
        <Typography sx={{ fontWeight: 900, fontSize: 20, color: 'success.dark' }}>
          {fmtGHS(sumEmp + sumEr)}
        </Typography>
      </Box>
    </>
  );
}

function HrPayeTable({ d }) {
  const rows = d.payslips.filter(p => Number(p.paye) > 0);
  const sumTaxable = rows.reduce((a, p) =>
    a + Number(p.basic || 0) + Number(p.allowances || 0) - Number(p.ssnit_employee || 0), 0);
  const sumPaye = rows.reduce((a, p) => a + Number(p.paye || 0), 0);
  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead><TableRow sx={{ '& th': { fontWeight: 800, fontSize: 10.5, textTransform: 'uppercase', bgcolor: '#f7f9fc' } }}>
            <TableCell sx={{ width: 36 }}>#</TableCell>
            <TableCell>Employee</TableCell>
            <TableCell>TIN / SSNIT number</TableCell>
            <TableCell>Department</TableCell>
            <TableCell align="right">Gross</TableCell>
            <TableCell align="right">SSNIT</TableCell>
            <TableCell align="right">Taxable income</TableCell>
            <TableCell align="right">PAYE</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center"
                sx={{ py: 4, color: 'text.disabled', fontStyle: 'italic' }}>
                No PAYE due on this run (all below tax-free band).
              </TableCell></TableRow>
            )}
            {rows.map((p, i) => {
              const taxable = Number(p.basic || 0) + Number(p.allowances || 0) - Number(p.ssnit_employee || 0);
              return (
                <TableRow key={p.id} hover>
                  <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{p.employee_name}</TableCell>
                  <TableCell sx={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>
                    {p.tin_number || p.ssnit_number || '—'}
                  </TableCell>
                  <TableCell>{p.department || '—'}</TableCell>
                  <TableCell align="right">{fmtGHS(p.gross)}</TableCell>
                  <TableCell align="right">{fmtGHS(p.ssnit_employee)}</TableCell>
                  <TableCell align="right">{fmtGHS(taxable)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main' }}>
                    {fmtGHS(p.paye)}
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={{ '& td': { borderTop: 2, borderColor: 'divider', fontWeight: 800, bgcolor: '#f7f9fc' } }}>
              <TableCell colSpan={6} align="right">TOTALS ({rows.length})</TableCell>
              <TableCell align="right">{fmtGHS(sumTaxable)}</TableCell>
              <TableCell align="right" sx={{ color: 'error.dark' }}>{fmtGHS(sumPaye)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ mt: 3, p: 2, borderRadius: 1.5,
        border: '2px solid', borderColor: 'error.main',
        bgcolor: 'rgba(211,47,47,0.05)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontWeight: 900, fontSize: 14, color: 'error.dark' }}>
          TOTAL PAYABLE TO GRA
        </Typography>
        <Typography sx={{ fontWeight: 900, fontSize: 20, color: 'error.dark' }}>
          {fmtGHS(sumPaye)}
        </Typography>
      </Box>
    </>
  );
}

function HrRegisterTable({ d }) {
  const T = d.totals;
  return (
    <TableContainer>
      <Table size="small">
        <TableHead><TableRow sx={{ '& th': { fontWeight: 800, fontSize: 10.5, textTransform: 'uppercase', bgcolor: '#f7f9fc' } }}>
          <TableCell sx={{ width: 36 }}>#</TableCell>
          <TableCell>Employee</TableCell>
          <TableCell>Dept · Type</TableCell>
          <TableCell align="right">Basic</TableCell>
          <TableCell align="right">Allowances</TableCell>
          <TableCell align="right">Gross</TableCell>
          <TableCell align="right">SSNIT</TableCell>
          <TableCell align="right">PAYE</TableCell>
          <TableCell align="right">Other</TableCell>
          <TableCell align="right">Net</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {d.payslips.map((p, i) => (
            <TableRow key={p.id} hover>
              <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{p.employee_name}</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>
                {p.department || '—'}{p.employment_type ? ` · ${p.employment_type}` : ''}
              </TableCell>
              <TableCell align="right">{fmtGHS(p.basic)}</TableCell>
              <TableCell align="right">{fmtGHS(p.allowances)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtGHS(p.gross)}</TableCell>
              <TableCell align="right" sx={{ color: Number(p.ssnit_employee) > 0 ? 'error.main' : 'text.disabled' }}>
                {fmtGHS(p.ssnit_employee)}
              </TableCell>
              <TableCell align="right" sx={{ color: Number(p.paye) > 0 ? 'error.main' : 'text.disabled' }}>
                {fmtGHS(p.paye)}
              </TableCell>
              <TableCell align="right">{fmtGHS(p.other_deductions)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, color: 'success.dark' }}>{fmtGHS(p.net)}</TableCell>
            </TableRow>
          ))}
          <TableRow sx={{ '& td': { borderTop: 2, borderColor: 'divider', fontWeight: 800, bgcolor: '#f7f9fc' } }}>
            <TableCell colSpan={3} align="right">TOTALS ({T.headcount})</TableCell>
            <TableCell align="right">{fmtGHS(T.basic)}</TableCell>
            <TableCell align="right">{fmtGHS(T.allowances)}</TableCell>
            <TableCell align="right">{fmtGHS(T.gross)}</TableCell>
            <TableCell align="right">{fmtGHS(T.ssnit_employee)}</TableCell>
            <TableCell align="right">{fmtGHS(T.paye)}</TableCell>
            <TableCell align="right">{fmtGHS(T.other)}</TableCell>
            <TableCell align="right" sx={{ color: 'success.dark' }}>{fmtGHS(T.net)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ──────────────────────────────────────────────────────────────────
// HrPayslipsBrowse — Reports panel browser
// Pick a month → grid of every payslip on that run → click any one
// to print just that payslip, or "Print all selected" to print a
// batch. Uses the same `openPayslipPrint` template as the Payroll
// page so the output is identical (full Ghana-standard A4 with
// itemised allowances, Tier 1 + Tier 2, PAYE bracket walk, etc.)
// ──────────────────────────────────────────────────────────────────
function HrPayslipsBrowse({ data, schoolName }) {
  const today = new Date();
  const [year, setYear]   = useState(data?.filters?.year  || today.getFullYear());
  const [month, setMonth] = useState(data?.filters?.month || (today.getMonth() + 1));
  const [d, setD]         = useState(data || { run: null, payslips: [] });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [school, setSchool] = useState({});

  // Fetch school info once for the print template.
  useEffect(() => {
    API.get('/setup/info').then(r => setSchool(r.data?.info || {})).catch(() => {});
  }, []);

  const refetch = async (y, m) => {
    setLoading(true);
    setSelected(new Set());
    try {
      const r = await API.get('/reports/data/hr_payslips_browse',
        { params: { year: y, month: m } });
      setD(r.data);
    } finally { setLoading(false); }
  };

  const toggle = (id) => setSelected(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const selectAll = () => setSelected(new Set((d.payslips || []).map(p => p.id)));
  const clearAll  = () => setSelected(new Set());

  const printOne   = (slip) => openPayslipPrint([slip], d.run, school);
  const printBatch = (slips) => {
    if (slips.length) openPayslipPrint(slips, d.run, school);
  };

  const subtitle = d.run
    ? `${MONTH_ABBR[(d.filters.month||1)-1]} ${d.filters.year} · ${d.payslips.length} payslip(s) · status ${d.run.status}`
    : `${MONTH_ABBR[(month||1)-1]} ${year} — no payroll run found`;

  return (
    <>
      <PrintHeader schoolName={schoolName} title="Payslips" subtitle={subtitle} />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} className="no-print"
        sx={{ mb: 1.8 }} alignItems={{ sm: 'center' }}>
        <TextField select size="small" label="Year" value={year}
          onChange={(e) => { setYear(Number(e.target.value)); refetch(Number(e.target.value), month); }}
          sx={{ minWidth: 130 }}>
          {[1,0,-1,-2].map(off => {
            const y = today.getFullYear() + off;
            return <MenuItem key={y} value={y}>{y}</MenuItem>;
          })}
        </TextField>
        <TextField select size="small" label="Month" value={month}
          onChange={(e) => { setMonth(Number(e.target.value)); refetch(year, Number(e.target.value)); }}
          sx={{ minWidth: 170 }}>
          {['January','February','March','April','May','June',
            'July','August','September','October','November','December'].map((m, i) =>
            <MenuItem key={i+1} value={i+1}>{m}</MenuItem>
          )}
        </TextField>
        {loading && <CircularProgress size={22} />}
        <Box sx={{ flex: 1 }} />
        {(d.payslips || []).length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1, fontWeight: 700 }}>
              {selected.size} selected
            </Typography>
            <Button size="small" onClick={selectAll}    sx={{ textTransform: 'none' }}>Select all</Button>
            <Button size="small" onClick={clearAll}     sx={{ textTransform: 'none' }}>Clear</Button>
            <Button size="small" variant="outlined" onClick={() => printBatch((d.payslips || []).filter(p => selected.has(p.id)))}
              disabled={selected.size === 0}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              Print selected
            </Button>
            <Button size="small" variant="contained" onClick={() => printBatch(d.payslips || [])}
              sx={{ textTransform: 'none', fontWeight: 800 }}>
              Print all ({d.payslips.length})
            </Button>
          </>
        )}
      </Stack>

      {!d.run && (
        <Alert severity="info" variant="outlined">
          No payroll run exists for <b>{MONTH_ABBR[(month||1)-1]} {year}</b>.
          Create one under <b>HR → Payroll → New payroll run</b> first.
        </Alert>
      )}

      {d.run && (
        <Grid container spacing={1.2}>
          {(d.payslips || []).map(p => {
            const picked = selected.has(p.id);
            return (
              <Grid item xs={12} sm={6} md={4} key={p.id}>
                <Card variant="outlined" sx={{
                  borderRadius: 2,
                  borderLeft: 4,
                  borderColor: picked ? 'primary.main' : 'success.main',
                  transition: 'box-shadow 0.15s, border-color 0.15s',
                  '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
                  cursor: 'pointer',
                }} onClick={() => printOne(p)}>
                  <CardContent sx={{ p: 1.8, '&:last-child': { pb: 1.8 } }}>
                    <Stack direction="row" alignItems="flex-start" spacing={1}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2 }} noWrap>
                          {p.employee_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap
                          sx={{ display: 'block', mt: 0.2 }}>
                          {p.department || '—'} · {p.position || '—'}
                        </Typography>
                      </Box>
                      <input type="checkbox" checked={picked}
                        onClick={(e) => { e.stopPropagation(); toggle(p.id); }}
                        onChange={() => {}}
                        style={{ width: 16, height: 16, cursor: 'pointer' }} />
                    </Stack>

                    <Box sx={{ mt: 1.2, p: 1, borderRadius: 1.5,
                      bgcolor: alpha('#2e7d32', 0.08), textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: 0.5,
                        color: 'success.dark', fontSize: 9.5 }}>
                        Net pay
                      </Typography>
                      <Typography sx={{ fontWeight: 800, fontSize: 18, color: 'success.dark', lineHeight: 1.2 }}>
                        GHS {fmtGHS(p.net)}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
                      <Chip size="small" label={`Gross GHS ${fmtGHS(p.gross)}`}
                        variant="outlined" sx={{ fontWeight: 700, fontSize: 10, height: 18 }} />
                      {Number(p.ssnit_employee) > 0 && (
                        <Chip size="small" color="warning" label={`SSNIT GHS ${fmtGHS(p.ssnit_employee)}`}
                          sx={{ fontWeight: 700, fontSize: 10, height: 18 }} />
                      )}
                      {Number(p.paye) > 0 && (
                        <Chip size="small" color="error" label={`PAYE GHS ${fmtGHS(p.paye)}`}
                          sx={{ fontWeight: 700, fontSize: 10, height: 18 }} />
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </>
  );
}
